import React, {
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import * as d3 from 'd3';
import { useDataPoints } from '../contexts/DataPointContext';
import { GraphContext } from '../contexts/GraphContext';
import { AuthContext } from '../contexts/AuthContext';
import { SIL_TRACKER_API_KEY, SIL_TRACKER_USERNAME } from '../constants/silTracking';
import GatewayTripCalculator from './GatewayTripCalculator';

const STORAGE_PERCENT_FIELDS = [
  'PercentFull',
  'percentFull',
  'FillPercent',
  'FillPercentage',
  'Utilization',
  'UsagePercent'
];

const SHIP_PERCENT_FIELDS = [
  'CargoPercentFull',
  'StoragePercentFull',
  'CargoUsage',
  'CargoUtilization',
  'LoadPercent',
  'CapacityPercent'
];

const STORAGE_WEIGHT_CAPACITY_FIELDS = [
  'WeightCapacity',
];

const STORAGE_VOLUME_CAPACITY_FIELDS = [
  'VolumeCapacity',
];

const STORAGE_WEIGHT_LOAD_FIELDS = [
  'WeightLoad',
];

const STORAGE_VOLUME_LOAD_FIELDS = [
  'VolumeLoad',
];

const SHIP_CAPACITY_PROFILES = [
  {
    key: 'weight3000_volume1000',
    weight: 3000,
    volume: 1000,
    color: '#f91616ff',
    label: 'Wt 3000 / Vol 1000'
  },
  {
    key: 'weight1000_volume3000',
    weight: 1000,
    volume: 3000,
    color: '#00eeffff',
    label: 'Wt 1000 / Vol 3000'
  },
  {
    key: 'capacity100',
    weight: 100,
    volume: 100,
    color: '#367cffff',
    label: '100 / 100'
  },
  {
    key: 'capacity500',
    weight: 500,
    volume: 500,
    color: '#019514ff',
    label: '500 / 500'
  },
  {
    key: 'capacity1000',
    weight: 1000,
    volume: 1000,
    color: '#fffb00ff',
    label: '1000 / 1000'
  },
  {
    key: 'capacity2000',
    weight: 2000,
    volume: 2000,
    color: '#ea8c08ff',
    label: '2000 / 2000'
  },
  {
    key: 'capacity5000',
    weight: 5000,
    volume: 5000,
    color: '#d35cffff',
    label: '5000 / 5000'
  }
];

const normalizeLookupKey = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;
  return str.toLowerCase();
};

const toNumericValue = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/,/g, '').trim();
    if (!normalized) {
      return null;
    }

    const numericMatch = normalized.match(/-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/);
    if (!numericMatch) {
      return null;
    }

    const parsed = Number(numericMatch[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  return null;
};

const normalizeDeliveryShipmentType = (value) => {
  if (value == null) {
    return null;
  }

  const text = String(value).trim();
  if (!text) {
    return null;
  }

  const collapsed = text.toUpperCase().replace(/[^A-Z]/g, '');
  if (collapsed === 'DELIVERYSHIPMENT' || collapsed === 'DELIVERSHIPMENT') {
    return 'DELIVERY_SHIPMENT';
  }

  return null;
};

const isDeliveryShipmentType = (value) => normalizeDeliveryShipmentType(value) != null;

const pickNumericValue = (source, fields) => {
  if (!source || typeof source !== 'object') return null;
  for (const field of fields) {
    if (field in source) {
      const numeric = toNumericValue(source[field]);
      if (numeric != null) {
        return numeric;
      }
    }
  }
  return null;
};

const storageRecordScoreCache = new WeakMap();

const evaluateStorageRecord = (record) => {
  if (!record || typeof record !== 'object') {
    return { score: -Infinity, timestamp: -Infinity };
  }

  if (storageRecordScoreCache.has(record)) {
    return storageRecordScoreCache.get(record);
  }

  const rawType = typeof record.Type === 'string' ? record.Type.toLowerCase() : '';
  const normalizedType = rawType.replace(/[^a-z]/g, '');

  let score = 0;

  if (normalizedType.includes('shipstore')) {
    score += 300;
  } else if (normalizedType.includes('ship') && normalizedType.includes('store')) {
    score += 240;
  } else if (normalizedType.includes('ship')) {
    score += 180;
  } else if (normalizedType.includes('ftlfuel')) {
    score += 90;
  } else if (normalizedType.includes('stlfuel')) {
    score += 80;
  } else if (normalizedType.includes('store')) {
    score += 60;
  }

  if (record.FixedStore === false) {
    score += 25;
  }

  if (pickNumericValue(record, STORAGE_WEIGHT_CAPACITY_FIELDS) != null) {
    score += 40;
  }

  if (pickNumericValue(record, STORAGE_VOLUME_CAPACITY_FIELDS) != null) {
    score += 40;
  }

  if (pickNumericValue(record, STORAGE_WEIGHT_LOAD_FIELDS) != null) {
    score += 20;
  }

  if (pickNumericValue(record, STORAGE_VOLUME_LOAD_FIELDS) != null) {
    score += 20;
  }

  if (record.Name) {
    score += 5;
  }

  const timestampRaw = record.Timestamp
    || record.timestamp
    || record.LastUpdated
    || record.lastUpdated
    || record.UpdatedAt
    || record.updatedAt
    || null;
  const timestampValue = typeof timestampRaw === 'string' || typeof timestampRaw === 'number'
    ? Date.parse(timestampRaw)
    : Number.NaN;

  const evaluation = {
    score,
    timestamp: Number.isFinite(timestampValue) ? timestampValue : -Infinity
  };

  storageRecordScoreCache.set(record, evaluation);
  return evaluation;
};

const pickPreferredStorageRecord = (existing, candidate) => {
  if (!candidate) {
    return existing || null;
  }

  if (!existing) {
    return candidate;
  }

  if (existing === candidate) {
    return existing;
  }

  const existingEval = evaluateStorageRecord(existing);
  const candidateEval = evaluateStorageRecord(candidate);

  if (candidateEval.score > existingEval.score) {
    return candidate;
  }

  if (candidateEval.score < existingEval.score) {
    return existing;
  }

  if (candidateEval.timestamp > existingEval.timestamp) {
    return candidate;
  }

  if (candidateEval.timestamp < existingEval.timestamp) {
    return existing;
  }

  return existing;
};

const DataPointOverlay = ({ mapRef }) => {
  const {
    meteorDensityData,
    luminosityData,
    systemNames,
    systemIdByNaturalId,
    isOverlayVisible,
    isLoading,
    error,
    maxValues,
    showShipLabels,
    toggleShipLabels,
    isGatewayLayerVisible,
    gatewayData,
    showGatewayBubbles,
    tripRoute,
    tripStartSystem,
    tripEndSystem,
    tripCalculatorOpen,
    setTripCalculatorOpen
  } = useDataPoints();
  const {
    graph,
    ships,
    flights,
    planetData,
    universeData,
    storageData,
    contracts,
    stationData,
    groupShips,
    groupFlights,
    groupStorageData,
    groupContracts,
    setShips,
    setFlights,
    setStorageData,
    setContracts,
    setGroupShips,
    setGroupFlights,
    setGroupStorageData,
    setGroupContracts,
    refetchUserData
  } = useContext(GraphContext);
  const {
    authToken,
    userName,
    isAuthenticated,
    loginWithApiKey,
    logout
  } = useContext(AuthContext);
  const [selectedShipId, setSelectedShipId] = useState('__all__');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [apiUsername, setApiUsername] = useState('');
  const [pendingApiUsername, setPendingApiUsername] = useState('');
  const [isSilTracking, setIsSilTracking] = useState(false);
  const [silToggleLoading, setSilToggleLoading] = useState(false);
  const [silToggleError, setSilToggleError] = useState(null);
  const [isShipFilterCollapsed, setIsShipFilterCollapsed] = useState(false);
  const [isTripCalculatorCollapsed, setIsTripCalculatorCollapsed] = useState(true);
  const [showSilTooltip, setShowSilTooltip] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState(null);
  const [groupUsernames, setGroupUsernames] = useState([]);
  const [selectedGroupUsers, setSelectedGroupUsers] = useState(['__all__']);
  const [isGroupUserFilterCollapsed, setIsGroupUserFilterCollapsed] = useState(true);

  // Combine user and group data for rendering
  const combinedShips = useMemo(() => {
    const userShips = ships || [];
    const filteredGroupShips = selectedGroupUsers.includes('__all__')
      ? (groupShips || [])
      : (groupShips || []).filter(ship => selectedGroupUsers.includes(ship._user));
    // When group users exist and are selected, exclude user's own data; otherwise show user data
    return (groupUsernames.length > 0 && selectedGroupUsers.length > 0) ? filteredGroupShips : [...userShips, ...filteredGroupShips];
  }, [ships, groupShips, selectedGroupUsers, groupUsernames]);
  const combinedFlights = useMemo(() => {
    const userFlights = flights || [];
    const filteredGroupFlights = selectedGroupUsers.includes('__all__')
      ? (groupFlights || [])
      : (groupFlights || []).filter(flight => selectedGroupUsers.includes(flight._user));
    // When group users exist and are selected, exclude user's own data; otherwise show user data
    return (groupUsernames.length > 0 && selectedGroupUsers.length > 0) ? filteredGroupFlights : [...userFlights, ...filteredGroupFlights];
  }, [flights, groupFlights, selectedGroupUsers, groupUsernames]);
  const combinedStorageData = useMemo(() => {
    const userStorage = storageData || [];
    const filteredGroupStorage = selectedGroupUsers.includes('__all__')
      ? (groupStorageData || [])
      : (groupStorageData || []).filter(storage => selectedGroupUsers.includes(storage._user));
    // When group users exist and are selected, exclude user's own data; otherwise show user data
    return (groupUsernames.length > 0 && selectedGroupUsers.length > 0) ? filteredGroupStorage : [...userStorage, ...filteredGroupStorage];
  }, [storageData, groupStorageData, selectedGroupUsers, groupUsernames]);
  const combinedContracts = useMemo(() => {
    const userContracts = contracts || [];
    const filteredGroupContracts = selectedGroupUsers.includes('__all__')
      ? (groupContracts || [])
      : (groupContracts || []).filter(contract => selectedGroupUsers.includes(contract._user));
    // When group users exist and are selected, exclude user's own data; otherwise show user data
    return (groupUsernames.length > 0 && selectedGroupUsers.length > 0) ? filteredGroupContracts : [...userContracts, ...filteredGroupContracts];
  }, [contracts, groupContracts, selectedGroupUsers, groupUsernames]);
  const labelsEnabled = Boolean(showShipLabels);
  const previousAuthRef = useRef(null);

  const loadColorScale = useMemo(() => (
    d3.scaleLinear()
      .domain([0, 0.5, 0.85, 1, 1.1])
      .range(['#10b981', '#84cc16', '#facc15', '#f97316', '#ef4444'])
      .clamp(true)
  ), []);

  const storageIndex = useMemo(() => {
    const index = new Map();
    (combinedStorageData || []).forEach((record) => {
      if (!record || typeof record !== 'object') {
        return;
      }

      const candidateKeys = [
        record.StorageId,
        record.StorageID,
        record.StorageNaturalId,
        record.StorageNaturalID,
        record.StorageName,
        record.StorageLabel,
        record.AddressableId,
        record.AddressableID,
        record.AddressId,
        record.AddressID,
        record.Id,
        record.ID,
        record.Name,
        record.NaturalId,
        record.NaturalID,
        record.LocationId,
        record.OwnerId
      ];

      candidateKeys.forEach((candidate) => {
        const normalized = normalizeLookupKey(candidate);
        if (normalized) {
          const existing = index.get(normalized);
          const preferred = pickPreferredStorageRecord(existing, record);
          if (preferred && (!existing || preferred !== existing)) {
            index.set(normalized, preferred);
          }
        }
      });
    });
    return index;
  }, [combinedStorageData]);

  const shipmentContractsByItemId = useMemo(() => {
    const map = new Map();

    const normalizeTimestamp = (value) => {
      if (value == null) return null;
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    (combinedContracts || []).forEach((contract) => {
      if (!contract || typeof contract !== 'object') {
        return;
      }

      const rawContractType = contract.Type || contract.type || null;

      const contractId = contract.ContractId || contract.contractId || null;
      const contractLocalId = contract.ContractLocalId
        || contract.ContractNumber
        || contract.ContractCode
        || contract.contractLocalId
        || contract.contractNumber
        || contract.contractCode
        || null;
      const contractStatus = contract.Status || contract.status || null;
      const partnerName = contract.PartnerName || contract.partnerName || null;
      const partnerCode = contract.PartnerCompanyCode || contract.partnerCompanyCode || null;
      const dueDateEpochMs = toNumericValue(contract.DueDateEpochMs ?? contract.dueDateEpochMs);
      const contractTimestamp = normalizeTimestamp(contract.Timestamp ?? contract.timestamp);

      const conditions = Array.isArray(contract.Conditions)
        ? contract.Conditions
        : (Array.isArray(contract.conditions) ? contract.conditions : []);

      const normalizedContractType = normalizeDeliveryShipmentType(rawContractType);

      const hasDeliveryShipmentCondition = conditions.some((condition) => {
        if (!condition || typeof condition !== 'object') {
          return false;
        }
        return isDeliveryShipmentType(condition.Type || condition.type);
      });

      if (!normalizedContractType && !hasDeliveryShipmentCondition) {
        return;
      }

      conditions.forEach((condition) => {
        if (!condition || typeof condition !== 'object') {
          return;
        }

        const rawConditionType = condition.Type || condition.type || null;
        const normalizedConditionType = normalizeDeliveryShipmentType(rawConditionType);
        if (!normalizedConditionType) {
          return;
        }

        const rawShipmentItemId = condition.ShipmentItemId
          || condition.ShipmentItemID
          || condition.MaterialId
          || condition.MaterialID
          || null;
        const normalizedShipmentItemId = normalizeLookupKey(rawShipmentItemId);
        if (!normalizedShipmentItemId) {
          return;
        }

        const conditionEntry = {
          contractId,
          contractLocalId,
          contractStatus,
          partnerName,
          partnerCode,
          contractType: normalizedContractType ?? (typeof rawContractType === 'string' ? rawContractType : null),
          contractTypeNormalized: normalizedContractType,
          dueDateEpochMs: dueDateEpochMs != null ? dueDateEpochMs : null,
          timestampEpochMs: contractTimestamp,
          conditionId: condition.ConditionId || condition.conditionId || null,
          conditionType: normalizedConditionType,
          conditionTypeRaw: typeof rawConditionType === 'string' ? rawConditionType : null,
          conditionStatus: condition.Status || condition.status || null,
          conditionIndex: toNumericValue(condition.ConditionIndex ?? condition.conditionIndex),
          destination: condition.Destination || condition.destination || null,
          party: condition.Party || condition.party || null,
          weight: toNumericValue(condition.Weight ?? condition.weight),
          volume: toNumericValue(condition.Volume ?? condition.volume),
          deadlineEpochMs: toNumericValue(condition.DeadlineEpochMs ?? condition.deadlineEpochMs),
          dependencies: Array.isArray(condition.Dependencies)
            ? condition.Dependencies
            : (Array.isArray(condition.dependencies) ? condition.dependencies : []),
          condition
        };

        const existingEntries = map.get(normalizedShipmentItemId);
        if (existingEntries) {
          existingEntries.push(conditionEntry);
        } else {
          map.set(normalizedShipmentItemId, [conditionEntry]);
        }
      });
    });

    map.forEach((entries) => {
      entries.sort((a, b) => {
        const indexA = Number.isFinite(a.conditionIndex) ? a.conditionIndex : Number.POSITIVE_INFINITY;
        const indexB = Number.isFinite(b.conditionIndex) ? b.conditionIndex : Number.POSITIVE_INFINITY;
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        const typeA = (a.conditionType || '').toString();
        const typeB = (b.conditionType || '').toString();
        return typeA.localeCompare(typeB);
      });

    });

    return map;
  }, [combinedContracts]);

  const shipLoadInfo = useMemo(() => {
    const map = new Map();

    const normalizePercent = (value) => {
      if (value == null) return null;
      const numeric = toNumericValue(value);
      if (numeric == null) return null;
      if (numeric > 1.5) {
        return numeric / 100;
      }
      return numeric;
    };

    const considerShip = (ship) => {
      if (!ship || typeof ship !== 'object') {
        return;
      }

      const shipIdCandidates = [
        ship.ShipId,
        ship.Id,
        ship.Ship,
        ship.Registration,
        ship.Name,
        ship.ShipName,
        ship.DisplayName,
        ship.AddressableId,
        ship.AddressId,
        ship.StorageAddressableId
      ];

      const storageCandidates = [
        ship.StorageId,
        ship.StorageID,
        ship.StorageNaturalId,
        ship.StorageNaturalID,
        ship.StorageName,
        ship.CurrentStorageId,
        ship.CurrentStorageID,
        ship.CurrentStorage?.StorageId,
        ship.CurrentStorage?.StorageID,
        ship.CurrentStorage?.Id,
        ship.CurrentStorage?.Name,
        ship.Storage?.StorageId,
        ship.Storage?.StorageID,
        ship.Storage?.Id,
        ship.Storage?.Name,
        ship.Storage?.StorageNaturalId,
        ship.Storage?.StorageNaturalID,
        ship.Storage?.NaturalId,
        ship.Storage?.AddressableId,
        ship.Storage?.AddressableID,
        ship.CurrentStorage?.StorageId,
        ship.CurrentStorage?.StorageID,
        ship.CurrentStorage?.Id,
        ship.CurrentStorage?.Name,
        ship.CurrentStorage?.StorageNaturalId,
        ship.CurrentStorage?.StorageNaturalID,
        ship.CurrentStorage?.NaturalId,
        ship.CurrentStorage?.AddressableId,
        ship.CurrentStorage?.AddressableID
      ];

      const allLookupCandidates = [...shipIdCandidates, ...storageCandidates];

      let storageRecord = null;
      let storageRecordEval = null;

      for (const candidate of allLookupCandidates) {
        const normalized = normalizeLookupKey(candidate);
        if (!normalized) {
          continue;
        }
        const candidateRecord = storageIndex.get(normalized);
        if (!candidateRecord) {
          continue;
        }

        const evaluation = evaluateStorageRecord(candidateRecord);
        if (!storageRecordEval
          || evaluation.score > storageRecordEval.score
          || (evaluation.score === storageRecordEval.score && evaluation.timestamp > storageRecordEval.timestamp)) {
          storageRecord = candidateRecord;
          storageRecordEval = evaluation;
        }
      }

      const shipStorageSource = ship.Storage && typeof ship.Storage === 'object' ? ship.Storage : null;

      const shipments = [];
      const seenShipmentKeys = new Set();

      const collectShipmentsFromRecord = (record) => {
        if (!record || typeof record !== 'object') {
          return;
        }

        const items = Array.isArray(record.StorageItems)
          ? record.StorageItems
          : (Array.isArray(record.Items) ? record.Items : []);

        items.forEach((item, index) => {
          if (!item || typeof item !== 'object') {
            return;
          }

          const candidateIds = [
            item.ShipmentItemId,
            item.ShipmentItemID,
            item.MaterialId,
            item.MaterialID,
            item.ItemId,
            item.ItemID,
            item.Id,
            item.ID
          ];

          let matchedKey = null;
          let matchedContracts = null;

          for (const candidateId of candidateIds) {
            const normalizedCandidate = normalizeLookupKey(candidateId);
            if (!normalizedCandidate) {
              continue;
            }
            if (shipmentContractsByItemId.has(normalizedCandidate)) {
              matchedKey = normalizedCandidate;
              matchedContracts = shipmentContractsByItemId.get(normalizedCandidate).map((entry) => ({
                ...entry
              }));
              break;
            }
          }

          const dedupeKey = matchedKey
            || normalizeLookupKey(item.MaterialId)
            || normalizeLookupKey(item.ShipmentItemId)
            || `${normalizeLookupKey(record.StorageId || record.Id || record.Name || 'record')}::${index}`;

          if (dedupeKey && seenShipmentKeys.has(dedupeKey)) {
            return;
          }

          const itemTypeRaw = typeof item.Type === 'string' ? item.Type.toLowerCase() : '';
          const isLikelyShipment = itemTypeRaw.includes('shipment');

          if (!matchedContracts && !isLikelyShipment) {
            return;
          }

          if (dedupeKey) {
            seenShipmentKeys.add(dedupeKey);
          }

          shipments.push({
            storageItem: item,
            shipmentItemKey: matchedKey,
            contractMatches: matchedContracts || [],
            source: {
              storageId: record.StorageId || record.Id || null,
              name: record.Name || record.StorageName || null,
              type: record.Type || null
            }
          });
        });
      };

      const volumeCapacitySources = [storageRecord, shipStorageSource, ship];
      const weightCapacitySources = [storageRecord, shipStorageSource, ship];
      const volumeLoadSources = [storageRecord, shipStorageSource, ship];
      const weightLoadSources = [storageRecord, shipStorageSource, ship];

      const pickFromSources = (sources, fields) => {
        for (const source of sources) {
          const value = pickNumericValue(source, fields);
          if (value != null) {
            return value;
          }
        }
        return null;
      };

      const volumeCapacity = pickFromSources(volumeCapacitySources, STORAGE_VOLUME_CAPACITY_FIELDS)
        ?? pickFromSources([ship], STORAGE_VOLUME_CAPACITY_FIELDS);
      const weightCapacity = pickFromSources(weightCapacitySources, STORAGE_WEIGHT_CAPACITY_FIELDS)
        ?? pickFromSources([ship], STORAGE_WEIGHT_CAPACITY_FIELDS);

      const volumeLoad = pickFromSources(volumeLoadSources, STORAGE_VOLUME_LOAD_FIELDS)
        ?? pickFromSources([ship], STORAGE_VOLUME_LOAD_FIELDS);
      const weightLoad = pickFromSources(weightLoadSources, STORAGE_WEIGHT_LOAD_FIELDS)
        ?? pickFromSources([ship], STORAGE_WEIGHT_LOAD_FIELDS);

      const percentSources = [storageRecord, shipStorageSource, ship];
      const generalPercent = pickFromSources(percentSources, STORAGE_PERCENT_FIELDS)
        ?? pickFromSources([ship], SHIP_PERCENT_FIELDS);

      const volumeRatio = (() => {
        if (volumeCapacity != null && Number.isFinite(volumeCapacity) && volumeCapacity > 0 && volumeLoad != null) {
          return Math.max(0, volumeLoad / volumeCapacity);
        }
        return null;
      })();

      const weightRatio = (() => {
        if (weightCapacity != null && Number.isFinite(weightCapacity) && weightCapacity > 0 && weightLoad != null) {
          return Math.max(0, weightLoad / weightCapacity);
        }
        return null;
      })();

      const percentRatio = normalizePercent(generalPercent);

      const ratioCandidates = [volumeRatio, weightRatio, percentRatio].filter((value) => value != null && Number.isFinite(value));
      const ratio = ratioCandidates.length > 0 ? Math.max(...ratioCandidates) : null;

      const normalizedVolumeCapacity = Number.isFinite(volumeCapacity) ? volumeCapacity : null;
      const normalizedWeightCapacity = Number.isFinite(weightCapacity) ? weightCapacity : null;
      const normalizedVolumeLoad = Number.isFinite(volumeLoad) ? volumeLoad : null;
      const normalizedWeightLoad = Number.isFinite(weightLoad) ? weightLoad : null;

      collectShipmentsFromRecord(storageRecord);
      collectShipmentsFromRecord(shipStorageSource);

      if (!storageRecord
        && normalizedVolumeCapacity == null
        && normalizedWeightCapacity == null
        && normalizedVolumeLoad == null
        && normalizedWeightLoad == null
        && ratio == null
        && shipments.length === 0) {
        return;
      }

      const derivedVolumeLoad = normalizedVolumeLoad != null
        ? normalizedVolumeLoad
        : (normalizedVolumeCapacity != null && volumeRatio != null
          ? volumeRatio * normalizedVolumeCapacity
          : null);

      const derivedWeightLoad = normalizedWeightLoad != null
        ? normalizedWeightLoad
        : (normalizedWeightCapacity != null && weightRatio != null
          ? weightRatio * normalizedWeightCapacity
          : null);

      const info = {
        storageRecord,
        volumeCapacity: normalizedVolumeCapacity,
        weightCapacity: normalizedWeightCapacity,
        volumeLoad: derivedVolumeLoad,
        weightLoad: derivedWeightLoad,
        volumeRatio: volumeRatio != null ? Math.max(0, volumeRatio) : null,
        weightRatio: weightRatio != null ? Math.max(0, weightRatio) : null,
        ratio: ratio != null ? Math.max(0, ratio) : null,
        shipments
      };

      allLookupCandidates.forEach((candidate) => {
        const normalized = normalizeLookupKey(candidate);
        if (normalized && !map.has(normalized)) {
          map.set(normalized, info);
        }
      });
    };

    (combinedShips || []).forEach(considerShip);

    return map;
  }, [combinedShips, storageIndex, shipmentContractsByItemId]);

  const normalizedPartnerFilter = useMemo(() => normalizeLookupKey(partnerFilter) || '', [partnerFilter]);
  const partnerFilterActive = normalizedPartnerFilter.length > 0;

  // Use custom API username if provided, otherwise use authenticated username
  const effectiveApiUsername = useMemo(() => {
    return apiUsername.trim() || userName || '';
  }, [apiUsername, userName]);

  useEffect(() => {
    setIsSilTracking(authToken === SIL_TRACKER_API_KEY);
    if (!authToken || authToken === SIL_TRACKER_API_KEY || !userName) {
      return;
    }

    previousAuthRef.current = {
      token: authToken,
      userName
    };
  }, [authToken, userName]);

  // Sync pending username with applied username
  useEffect(() => {
    setPendingApiUsername(apiUsername);
  }, [apiUsername]);
  useEffect(() => {
    if (!authToken || !effectiveApiUsername || effectiveApiUsername === userName || isSilTracking) {
      return;
    }

    const headers = {
      Authorization: authToken,
      Accept: 'application/json'
    };

    const fetchCustomUserData = async () => {
      try {
        const [shipsResp, flightsResp, storageResp, contractsResp] = await Promise.all([
          fetch(`https://rest.fnar.net/ship/ships/${encodeURIComponent(effectiveApiUsername)}`, { headers }),
          fetch(`https://rest.fnar.net/ship/flights/${encodeURIComponent(effectiveApiUsername)}`, { headers }),
          fetch(`https://rest.fnar.net/storage/${encodeURIComponent(effectiveApiUsername)}`, { headers }),
          fetch(`https://rest.fnar.net/contract/allcontracts/${encodeURIComponent(effectiveApiUsername)}?count=500`, { headers })
        ]);

        if (shipsResp.ok) {
          const shipsData = await shipsResp.json();
          setShips(Array.isArray(shipsData) ? shipsData : []);
        }

        if (flightsResp.ok) {
          const flightsData = await flightsResp.json();
          setFlights(Array.isArray(flightsData) ? flightsData : []);
        }

        if (storageResp.ok) {
          const storageData = await storageResp.json();
          setStorageData(Array.isArray(storageData) ? storageData : []);
        }

        if (contractsResp.ok) {
          const contractsData = await contractsResp.json();
          setContracts(Array.isArray(contractsData) ? contractsData : []);
        }
      } catch (error) {
        console.error('Error fetching custom user data:', error);
      }
    };

    fetchCustomUserData();
  }, [effectiveApiUsername, authToken, userName, isSilTracking, setShips, setFlights, setStorageData, setContracts]);

  const matchesPartnerFilter = useCallback((entry) => {
    if (!partnerFilterActive) {
      return true;
    }

    if (!entry || typeof entry !== 'object') {
      return false;
    }

    const candidateValues = [
      entry.partnerCode,
      entry.PartnerCompanyCode,
      entry.condition?.PartnerCompanyCode,
      entry.condition?.partnerCompanyCode
    ];

    return candidateValues.some((value) => {
      const normalized = normalizeLookupKey(value);
      return normalized ? normalized.includes(normalizedPartnerFilter) : false;
    });
  }, [partnerFilterActive, normalizedPartnerFilter]);

  const filterShipmentsByPartner = useCallback((shipments) => {
    const list = Array.isArray(shipments) ? shipments : [];
    if (!partnerFilterActive) {
      return list;
    }
    return list.filter((shipment) => (
      Array.isArray(shipment?.contractMatches)
        ? shipment.contractMatches.some(matchesPartnerFilter)
        : false
    ));
  }, [partnerFilterActive, matchesPartnerFilter]);

  const partnerFilteredShipments = useMemo(() => {
    if (!partnerFilterActive) {
      return null;
    }

    const result = new Map();
    shipLoadInfo.forEach((info, key) => {
      const filtered = filterShipmentsByPartner(info?.shipments);
      if (filtered.length > 0) {
        result.set(key, filtered);
      }
    });
    return result;
  }, [shipLoadInfo, filterShipmentsByPartner, partnerFilterActive]);

  // Compute system-level shipment counts and details
  const systemShipmentCounts = useMemo(() => {
    const counts = new Map();
    const shipmentDetails = new Map(); // Map<systemId, Array<shipment info>>

    // Helper to find system ID from planet natural ID
    const findSystemIdFromPlanet = (planetNaturalId) => {
      if (!planetNaturalId || !planetData || typeof planetData !== 'object') return null;

      for (const [sysId, planets] of Object.entries(planetData)) {
        for (const planet of planets || []) {
          if (planet?.PlanetNaturalId === planetNaturalId) {
            return sysId;
          }
        }
      }
      return null;
    };

    // Helper to find system ID from station using stationData
    // Returns SystemId which matches the format used in the map
    // Note: storage.AddressableId often matches station.WarehouseId
    const findSystemIdFromStation = (storageId, addressableId) => {
      if (!stationData || !Array.isArray(stationData)) return null;

      // Try to match by comparing storage fields with station WarehouseId
      for (const station of stationData) {
        if (!station || typeof station !== 'object') continue;

        // Storage AddressableId often matches station WarehouseId
        if (addressableId && station.WarehouseId === addressableId) {
          return station.SystemId;
        }

        // Also try matching StorageId to WarehouseId
        if (storageId && station.WarehouseId === storageId) {
          return station.SystemId;
        }

        // Try matching AddressableId to AddressableId (if station has it)
        if (addressableId && station.AddressableId === addressableId) {
          return station.SystemId;
        }
      }

      return null;
    };

    // Helper to find system ID from warehouse/station natural ID using universeData
    const findSystemIdFromWarehouse = (naturalId) => {
      if (!naturalId || !universeData || typeof universeData !== 'object') return null;

      const normalizedId = String(naturalId).toUpperCase().trim();

      for (const [systemId, entries] of Object.entries(universeData)) {
        if (!Array.isArray(entries)) continue;

        for (const entry of entries) {
          if (!entry || typeof entry !== 'object') continue;

          const entryNaturalId = entry.NaturalId || entry.naturalId;
          if (entryNaturalId && String(entryNaturalId).toUpperCase().trim() === normalizedId) {
            return systemId;
          }
        }
      }
      return null;
    };

    // Helper to extract system from AddressableId (format: planet.system or station.system)
    const extractSystemFromAddressableId = (addressableId) => {
      if (!addressableId || typeof addressableId !== 'string') return null;

      const parts = addressableId.split('.');
      if (parts.length >= 2) {
        // Last part is typically the system
        return parts[parts.length - 1];
      }
      return null;
    };

    // Count shipments from storage (planetary storage + warehouses)
    // Badge shows where shipments currently ARE, not where they're going
    (combinedStorageData || []).forEach((storage) => {
      if (!storage || typeof storage !== 'object') return;

      // Find the system where this storage is located
      let systemId = null;

      // Try 1: Station data lookup (for WAREHOUSE_STORE - most accurate)
      // Note: storage.AddressableId often matches station.WarehouseId
      if (storage.Type === 'WAREHOUSE_STORE' || storage.Type === 'STATION_STORE') {
        const storageId = storage.StorageId;
        const addressableId = storage.AddressableId;
        systemId = findSystemIdFromStation(storageId, addressableId);
      }

      // Try 2: Planet natural ID (for planetary storage)
      if (!systemId) {
        const planetNaturalId = storage.PlanetNaturalId;
        if (planetNaturalId) {
          systemId = findSystemIdFromPlanet(planetNaturalId);
        }
      }

      // Try 3: Warehouse/Station natural ID using universeData
      if (!systemId) {
        const warehouseNaturalId = storage.StorageNaturalId || storage.NaturalId;
        if (warehouseNaturalId) {
          systemId = findSystemIdFromWarehouse(warehouseNaturalId);
        }
      }

      // Try 4: AddressableId parsing (extract system from format like "ABC.Antares")
      if (!systemId && storage.AddressableId) {
        systemId = extractSystemFromAddressableId(storage.AddressableId);
      }

      // Try 5: LocationName field
      if (!systemId && storage.LocationName) {
        const locationParts = storage.LocationName.split('.');
        if (locationParts.length >= 2) {
          systemId = locationParts[locationParts.length - 1];
        }
      }

      // Try 6: SystemId field directly
      if (!systemId && storage.SystemId) {
        systemId = storage.SystemId;
      }

      if (!systemId) return;

      const items = Array.isArray(storage.StorageItems)
        ? storage.StorageItems
        : (Array.isArray(storage.Items) ? storage.Items : []);

      items.forEach((item) => {
        if (!item || typeof item !== 'object') return;

        const itemTypeRaw = typeof item.Type === 'string' ? item.Type.toLowerCase() : '';
        const isShipment = itemTypeRaw.includes('shipment');

        // Also check if it's linked to a shipment contract
        const candidateIds = [
          item.ShipmentItemId,
          item.ShipmentItemID,
          item.MaterialId,
          item.MaterialID
        ];

        let hasShipmentContract = false;
        for (const candidateId of candidateIds) {
          const normalized = normalizeLookupKey(candidateId);
          if (normalized && shipmentContractsByItemId && shipmentContractsByItemId.has(normalized)) {
            hasShipmentContract = true;
            break;
          }
        }

        // Count if it's a shipment type or has a shipment contract
        if (isShipment || hasShipmentContract) {
          const currentCount = counts.get(systemId) || 0;
          counts.set(systemId, currentCount + 1);

          // Store shipment details for tooltip
          if (!shipmentDetails.has(systemId)) {
            shipmentDetails.set(systemId, []);
          }

          // Find the associated contract(s) using material ID
          let contractInfo = null;
          for (const candidateId of candidateIds) {
            const normalized = normalizeLookupKey(candidateId);
            if (normalized && shipmentContractsByItemId && shipmentContractsByItemId.has(normalized)) {
              const contractEntries = shipmentContractsByItemId.get(normalized);
              // Get the first contract entry (they're already sorted)
              const firstEntry = Array.isArray(contractEntries) ? contractEntries[0] : contractEntries;
              if (firstEntry) {
                contractInfo = {
                  localId: firstEntry.contractLocalId,
                  id: firstEntry.contractId,
                  partnerName: firstEntry.partnerName,
                  partnerCode: firstEntry.partnerCode,
                  destination: firstEntry.destination || firstEntry.party,
                  deadlineEpochMs: firstEntry.deadlineEpochMs,
                  weight: firstEntry.weight,
                  volume: firstEntry.volume
                };
              }
              break;
            }
          }

          shipmentDetails.get(systemId).push({
            amount: item.MaterialAmount || item.Amount || 1,
            weight: item.TotalWeight || 0,
            volume: item.TotalVolume || 0,
            contract: contractInfo,
            storageType: storage.Type,
            locationName: storage.LocationName || storage.PlanetName,
            owner: storage._user
          });
        }
      });
    });

    return { counts, shipmentDetails };
  }, [combinedStorageData, planetData, universeData, stationData, shipmentContractsByItemId]);

  const showNoPartnerMatches = partnerFilterActive
    && (!partnerFilteredShipments || partnerFilteredShipments.size === 0);

  const handleSilTrackingToggle = useCallback(async () => {
    if (!loginWithApiKey) {
      return;
    }

    setSilToggleLoading(true);
    setSilToggleError(null);

    try {
      if (authToken === SIL_TRACKER_API_KEY) {
        const fallback = previousAuthRef.current;

        if (
          fallback
          && fallback.token
          && fallback.userName
          && fallback.token !== SIL_TRACKER_API_KEY
        ) {
          await loginWithApiKey({ apiKey: fallback.token, rememberMe: false });
        } else {
          logout?.();
        }

        previousAuthRef.current = null;
      } else {
        if (authToken && authToken !== SIL_TRACKER_API_KEY) {
          previousAuthRef.current = {
            token: authToken,
            userName
          };
        }

        if (!SIL_TRACKER_API_KEY) {
          throw new Error('SIL tracker credentials are not configured.');
        }

        await loginWithApiKey({ apiKey: SIL_TRACKER_API_KEY, rememberMe: false });
      }
    } catch (error) {
      setSilToggleError(error instanceof Error ? error.message : 'Failed to toggle SIL shipment tracking');
    } finally {
      setSilToggleLoading(false);
    }
  }, [authToken, loginWithApiKey, logout, userName]);

  const handleGroupFetch = useCallback(async () => {
    setGroupLoading(true);
    setGroupError(null);
    if (!groupId || !isAuthenticated) return;
    try {
      const headerValue = typeof authToken === 'string' ? authToken.trim() : '';
      const headers = headerValue ? { Authorization: headerValue, Accept: 'application/json' } : { Accept: 'application/json' };
      const resp = await fetch(`https://rest.fnar.net/auth/group/${groupId}`, { headers });
      if (resp.status === 204) {
        setGroupError('Group with specified id not found');
        setGroupLoading(false);
        return;
      }
      if (!resp.ok) {
        setGroupError(`Failed to fetch group info: ${resp.status}`);
        setGroupLoading(false);
        return;
      }
      const payload = await resp.json();
      const usernames = [
        ...(payload.GroupAdmins?.map(a => a.GroupAdminUserName) || []),
        ...(payload.GroupUsers?.map(u => u.GroupUserName) || [])
      ];
      // Remove duplicates from usernames
      const uniqueUsernames = [...new Set(usernames)];

      // Track successful fetches per user
      const successfulUsers = new Set();

      // Fetch data for each user
      const allGroupShips = [];
      const allGroupFlights = [];
      const allGroupStorage = [];
      const allGroupContracts = [];

      for (const username of uniqueUsernames) {
        let allFetchesSuccessful = true;

        try {
          // Example endpoints, replace with actual endpoints as needed
          const shipResp = await fetch(`https://rest.fnar.net/ship/ships/${username}`, { headers });
          const flightResp = await fetch(`https://rest.fnar.net/ship/flights/${username}`, { headers });
          const contractResp = await fetch(`https://rest.fnar.net/contract/allcontracts/${username}?count=500`, { headers });
          const storageResp = await fetch(`https://rest.fnar.net/storage/${username}`, { headers });

          const shipData = shipResp.ok ? await shipResp.json() : null;
          const flightData = flightResp.ok ? await flightResp.json() : null;
          const storageData = storageResp.ok ? await storageResp.json() : null;
          const contractData = contractResp.ok ? await contractResp.json() : null;

          // Check if all fetches were successful
          if (!shipResp.ok || !flightResp.ok || !contractResp.ok || !storageResp.ok) {
            allFetchesSuccessful = false;
          }

          if (allFetchesSuccessful) {
            successfulUsers.add(username);

            // Collect all data for rendering
            if (shipData && Array.isArray(shipData)) {
              shipData.forEach(ship => {
                allGroupShips.push({ ...ship, _user: username });
              });
            }
            if (flightData && Array.isArray(flightData)) {
              flightData.forEach(flight => {
                allGroupFlights.push({ ...flight, _user: username });
              });
            }
            if (storageData && Array.isArray(storageData)) {
              storageData.forEach(storage => {
                allGroupStorage.push({ ...storage, _user: username });
              });
            }
            if (contractData && Array.isArray(contractData)) {
              contractData.forEach(contract => {
                allGroupContracts.push({ ...contract, _user: username });
              });
            }

            // Log each successfully pulled user's data
            if (shipData || flightData || storageData || contractData) {
              // eslint-disable-next-line no-console
              console.log(`[Group Fetch] Pulled data for user: ${username}`, {
                ships: shipData,
                flights: flightData,
                storage: storageData,
                contracts: contractData
              });
            }
          }
        } catch (err) {
          // If any error occurs, mark as unsuccessful
          allFetchesSuccessful = false;
        }
      }

      // Only include users where all fetches were successful
      const successfulUsernames = uniqueUsernames.filter(username => successfulUsers.has(username));
      setGroupUsernames(successfulUsernames);

      // Update GraphContext with combined group data
      setGroupShips(allGroupShips);
      setGroupFlights(allGroupFlights);
      setGroupStorageData(allGroupStorage);
      setGroupContracts(allGroupContracts);
    } catch (err) {
      setGroupError('Failed to fetch group info');
    }
    setGroupLoading(false);
  }, [groupId, isAuthenticated, authToken, setGroupShips, setGroupFlights, setGroupStorageData, setGroupContracts]);

  const handleGroupClear = useCallback(() => {
    setGroupId('');
    setGroupUsernames([]);
    setSelectedGroupUsers(['__all__']);
    setGroupError(null);
    setGroupShips([]);
    setGroupFlights([]);
    setGroupStorageData([]);
    setGroupContracts([]);
  }, [setGroupShips, setGroupFlights, setGroupStorageData, setGroupContracts]);

  const handleGroupUserSelectionChange = useCallback((username, checked) => {
    if (username === '__all__') {
      if (checked) {
        setSelectedGroupUsers(['__all__']);
      } else {
        setSelectedGroupUsers([]);
      }
    } else {
      setSelectedGroupUsers(prev => {
        let newSelection;
        if (checked) {
          // Remove __all__ if it was selected and add the specific user
          newSelection = prev.filter(user => user !== '__all__');
          if (!newSelection.includes(username)) {
            newSelection.push(username);
          }
        } else {
          // Remove the specific user
          newSelection = prev.filter(user => user !== username);
        }
        return newSelection;
      });
    }
  }, []);

  useEffect(() => {
    if (!partnerFilterActive) {
      return;
    }

    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const uniqueCodes = new Set();
    let totalContracts = 0;
    let shipmentsMissingCodes = 0;

    shipLoadInfo.forEach((info, shipKey) => {
      const shipments = Array.isArray(info?.shipments) ? info.shipments : [];
      shipments.forEach((shipment, shipmentIndex) => {
        const matches = Array.isArray(shipment?.contractMatches) ? shipment.contractMatches : [];
        if (matches.length === 0) {
          return;
        }

        let shipmentHasCode = false;
        matches.forEach((match, matchIndex) => {
          totalContracts += 1;
          const partnerCode = match?.PartnerCompanyCode
            ?? match?.partnerCompanyCode
            ?? match?.condition?.PartnerCompanyCode
            ?? match?.condition?.partnerCompanyCode
            ?? match?.partnerCode
            ?? null;

          if (partnerCode) {
            uniqueCodes.add(String(partnerCode));
            shipmentHasCode = true;
          } else if (process.env.NODE_ENV === 'development') {
            console.debug('[DataPointOverlay][PartnerFilterDebug] Missing PartnerCompanyCode', {
              shipKey,
              shipmentIndex,
              contractIndex: matchIndex,
              contractId: match?.contractId ?? null,
              contractLocalId: match?.contractLocalId ?? null,
              contractType: match?.contractType ?? null,
              availablePartnerCodeField: match?.partnerCode ?? null
            });
          }
        });

        if (!shipmentHasCode) {
          shipmentsMissingCodes += 1;
        }
      });
    });

    console.debug('[DataPointOverlay][PartnerFilterDebug] Contract summary', {
      filter: normalizedPartnerFilter,
      totalContracts,
      uniquePartnerCodes: Array.from(uniqueCodes).sort(),
      shipmentsMissingCodes
    });
  }, [partnerFilterActive, normalizedPartnerFilter, shipLoadInfo]);

  const getShipLoadInfoById = useCallback((candidate) => {
    const normalized = normalizeLookupKey(candidate);
    if (!normalized) return null;
    return shipLoadInfo.get(normalized) || null;
  }, [shipLoadInfo]);

  const formatCapacityValue = useCallback((value) => {
    const numeric = toNumericValue(value);
    if (numeric == null) return 'Unknown';
    const abs = Math.abs(numeric);
    if (abs >= 1_000_000_000) return `${(numeric / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `${(numeric / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${(numeric / 1_000).toFixed(1)}k`;
    if (abs >= 1) return numeric % 1 === 0 ? numeric.toFixed(0) : numeric.toFixed(1);
    if (abs === 0) return '0';
    return numeric.toFixed(2);
  }, []);

  const buildLoadSummary = useCallback((info) => {
    if (!info) return null;

    const ratioCandidates = [info.ratio, info.volumeRatio, info.weightRatio]
      .map((value) => (Number.isFinite(value) ? Math.max(0, value) : null))
      .filter((value) => value != null);

    if (ratioCandidates.length > 0) {
      const ratioValue = Math.max(...ratioCandidates);
      const clamped = Math.min(ratioValue, 1);
      const overSuffix = ratioValue > 1 ? ' (Over)' : '';
      return `Util ${(clamped * 100).toFixed(1)}%${overSuffix}`;
    }

    return null;
  }, []);

  const buildShipmentTiles = useCallback((info) => {
    if (!info?.shipments?.length) {
      return [];
    }

    const weightKeySet = new Set([
      'weight',
      'totalweight',
      'materialweight',
      'shipmentweight',
      'expectedweight',
      'payloadweight',
      'mass'
    ]);

    const volumeKeySet = new Set([
      'volume',
      'totalvolume',
      'materialvolume',
      'shipmentvolume',
      'expectedvolume',
      'payloadvolume',
      'space'
    ]);

    const pickFromObject = (source, keySet) => {
      if (!source || typeof source !== 'object' || Array.isArray(source)) {
        return null;
      }

      for (const [key, value] of Object.entries(source)) {
        const normalizedKey = key.trim().toLowerCase();
        if (!keySet.has(normalizedKey)) {
          continue;
        }

        const numeric = toNumericValue(value);
        if (numeric != null && Math.abs(numeric) > 0) {
          return numeric;
        }
      }

      return null;
    };

    const deepSearchForMetric = (root, keySet) => {
      if (!root || typeof root !== 'object') {
        return null;
      }

      const visited = new Set();
      const stack = [root];

      while (stack.length > 0) {
        const current = stack.pop();

        if (!current || typeof current !== 'object') {
          continue;
        }

        if (visited.has(current)) {
          continue;
        }

        visited.add(current);

        const direct = pickFromObject(current, keySet);
        if (direct != null) {
          return direct;
        }

        if (Array.isArray(current)) {
          for (let i = 0; i < current.length; i += 1) {
            stack.push(current[i]);
          }
        } else {
          Object.values(current).forEach((child) => {
            if (child && typeof child === 'object') {
              stack.push(child);
            }
          });
        }
      }

      return null;
    };

    const resolveMetric = (matchEntry, keySet, storageFallbackCandidates) => {
      const entry = (matchEntry && typeof matchEntry === 'object') ? matchEntry : null;

      const fromMatch = pickFromObject(entry, keySet);
      if (fromMatch != null) {
        return fromMatch;
      }

      const fromCondition = pickFromObject(entry?.condition, keySet);
      if (fromCondition != null) {
        return fromCondition;
      }

      const deepCondition = deepSearchForMetric(entry?.condition, keySet);
      if (deepCondition != null) {
        return deepCondition;
      }

      const deepMatch = deepSearchForMetric(entry, keySet);
      if (deepMatch != null) {
        return deepMatch;
      }

      for (const candidate of storageFallbackCandidates) {
        const numericCandidate = toNumericValue(candidate);
        if (numericCandidate != null && Math.abs(numericCandidate) > 0) {
          return numericCandidate;
        }
      }

      return null;
    };

    return info.shipments.reduce((acc, shipment, index) => {
      if (!shipment || typeof shipment !== 'object') {
        return acc;
      }

      const matches = Array.isArray(shipment.contractMatches) ? shipment.contractMatches : [];
      const deliveryMatches = matches.filter((match) => {
        const typeCandidate = match?.conditionType
          ?? match?.conditionTypeRaw
          ?? match?.condition?.Type
          ?? match?.condition?.type;
        return isDeliveryShipmentType(typeCandidate);
      });

      if (deliveryMatches.length === 0) {
        return acc;
      }

      const preferredMatch = deliveryMatches[0];
      const contractLocalId = preferredMatch?.contractLocalId || preferredMatch?.contractId || shipment.shipmentItemKey;
      const contractLabel = contractLocalId != null ? String(contractLocalId) : `Shipment ${index + 1}`;

      const destination = preferredMatch?.destination
        || preferredMatch?.party
        || shipment.source?.name
        || 'Unknown destination';

      const weightValue = resolveMetric(preferredMatch, weightKeySet, [
        shipment.storageItem?.TotalWeight,
        shipment.storageItem?.MaterialWeight,
        shipment.storageItem?.Weight
      ]);

      const volumeValue = resolveMetric(preferredMatch, volumeKeySet, [
        shipment.storageItem?.TotalVolume,
        shipment.storageItem?.MaterialVolume,
        shipment.storageItem?.Volume
      ]);

      const weightText = weightValue != null ? formatCapacityValue(weightValue) : '—';
      const volumeText = volumeValue != null ? formatCapacityValue(volumeValue) : '—';

      // Calculate deadline text
      let deadlineText = '';
      const deadlineEpochMs = preferredMatch?.deadlineEpochMs;
      if (deadlineEpochMs) {
        const deadlineDate = new Date(deadlineEpochMs);
        const now = new Date();
        const totalHours = Math.round((deadlineDate - now) / (1000 * 60 * 60));
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;

        if (days > 0) {
          deadlineText = ` (${days}d ${hours}h)`;
        } else {
          deadlineText = ` (${hours}h)`;
        }
      }

      acc.push({
        id: shipment.shipmentItemKey || `${index}`,
        contractId: contractLabel,
        destination: String(destination),
        weightText,
        volumeText,
        lines: [
          `Contract ${contractLabel}${deadlineText}`,
          `Wt ${weightText} · Vol ${volumeText}`,
          `Dest ${String(destination)}`
        ]
      });

      return acc;
    }, []);
  }, [formatCapacityValue]);

  const getLoadColorForRatio = useCallback((ratio) => {
    if (ratio == null || !Number.isFinite(ratio)) {
      return null;
    }
    const clamped = Math.max(0, Math.min(ratio, 1.1));
    return loadColorScale(clamped);
  }, [loadColorScale]);

  const buildLoadBarDescriptors = useCallback((info) => {
    if (!info) return [];

    const descriptors = [];

    const coerceNumber = (value) => {
      if (value == null) return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const buildSummary = (kindLabel, ratioValue, loadValue, capacityValue) => {
      const percentText = ratioValue != null
        ? `${(Math.min(ratioValue, 1) * 100).toFixed(1)}%${ratioValue > 1 ? ' (Over)' : ''}`
        : null;
      const loadText = loadValue != null ? formatCapacityValue(loadValue) : null;
      const capacityText = capacityValue != null ? formatCapacityValue(capacityValue) : null;

      const parts = [];
      parts.push(percentText ? `${kindLabel} ${percentText}` : kindLabel);
      if (loadText && capacityText) {
        parts.push(`${loadText} / ${capacityText}`);
      } else if (capacityText) {
        parts.push(`Cap ${capacityText}`);
      } else if (loadText) {
        parts.push(`Load ${loadText}`);
      }

      return parts.filter(Boolean).join(' · ');
    };

    const addDescriptor = (key, kindLabel, ratioValue, loadValue, capacityValue) => {
      const numericLoad = coerceNumber(loadValue);
      const numericCapacity = coerceNumber(capacityValue);
      const normalizedRatio = (() => {
        if (ratioValue != null && Number.isFinite(ratioValue)) {
          return Math.max(0, ratioValue);
        }
        if (numericCapacity != null && numericCapacity > 0 && numericLoad != null) {
          return Math.max(0, numericLoad / numericCapacity);
        }
        return null;
      })();

      if (normalizedRatio == null && numericLoad == null && numericCapacity == null) {
        return;
      }

      descriptors.push({
        key,
        kindLabel,
        ratio: normalizedRatio,
        load: numericLoad,
        capacity: numericCapacity,
        summary: buildSummary(kindLabel, normalizedRatio, numericLoad, numericCapacity)
      });
    };

    addDescriptor('volume', 'Vol', info.volumeRatio, info.volumeLoad, info.volumeCapacity);
    addDescriptor('weight', 'Wt', info.weightRatio, info.weightLoad, info.weightCapacity);

    if (descriptors.length === 0 && info.ratio != null && Number.isFinite(info.ratio)) {
      const normalized = Math.max(0, info.ratio);
      descriptors.push({
        key: 'utilization',
        kindLabel: 'Util',
        ratio: normalized,
        load: null,
        capacity: null,
        summary: buildSummary('Util', normalized, null, null)
      });
    }

    return descriptors;
  }, [formatCapacityValue]);

  const planetLookups = useMemo(() => {
    const byId = new Map();
    const byNaturalId = new Map();

    if (planetData && typeof planetData === 'object') {
      Object.values(planetData).forEach((planetsInSystem) => {
        (planetsInSystem || []).forEach((planet) => {
          if (planet?.PlanetId) {
            byId.set(String(planet.PlanetId).toLowerCase(), planet);
          }
          if (planet?.PlanetNaturalId) {
            byNaturalId.set(String(planet.PlanetNaturalId).toUpperCase(), planet);
          }
        });
      });
    }

    return { byId, byNaturalId };
  }, [planetData]);

  const systemLookups = useMemo(() => {
    const byName = new Map();
    const byNaturalId = new Map();

    Object.entries(systemNames || {}).forEach(([systemId, name]) => {
      if (!systemId || typeof name !== 'string') {
        return;
      }
      byName.set(name.trim().toLowerCase(), systemId);
    });

    if (universeData && typeof universeData === 'object') {
      Object.entries(universeData).forEach(([systemId, entries]) => {
        if (!systemId || !Array.isArray(entries)) {
          return;
        }
        entries.forEach((entry) => {
          if (!entry || typeof entry !== 'object') {
            return;
          }
          const entryName = typeof entry.Name === 'string' ? entry.Name.trim() : null;
          const entryNaturalId = typeof entry.NaturalId === 'string' ? entry.NaturalId.trim() : null;

          if (entryName) {
            byName.set(entryName.toLowerCase(), systemId);
          }

          if (entryNaturalId) {
            byNaturalId.set(entryNaturalId.toUpperCase(), systemId);
          }
        });
      });
    }

    return { byName, byNaturalId };
  }, [systemNames, universeData]);

  const shipOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    (combinedShips || []).forEach((ship) => {
      const id = ship?.ShipId || ship?.Id || ship?.Ship || ship?.Registration || ship?.Name;
      if (!id) return;
      const idStr = String(id);
      if (seen.has(idStr)) return;
      seen.add(idStr);
      const labelBase = ship?.Name || ship?.ShipName || ship?.Registration || ship?.ShipId || ship?.Id || 'Unknown Ship';
      opts.push({ id: idStr, label: labelBase });
    });
    opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return [{ id: '__all__', label: 'All Ships' }, ...opts];
  }, [combinedShips]);

  const handleShipChange = useCallback((event) => {
    setSelectedShipId(event.target.value);
  }, [setSelectedShipId]);

  const handlePartnerFilterChange = useCallback((event) => {
    setPartnerFilter(event.target.value);
  }, []);

  const handleApiUsernameChange = useCallback((event) => {
    setPendingApiUsername(event.target.value);
  }, []);

  const handleApplyApiUsername = useCallback(() => {
    setApiUsername(pendingApiUsername.trim());
  }, [pendingApiUsername]);

  useEffect(() => {
    if (selectedShipId === '__all__') return;
    const stillExists = (combinedShips || []).some((ship) => {
      const id = ship?.ShipId || ship?.Id || ship?.Ship || ship?.Registration || ship?.Name;
      return id && String(id) === selectedShipId;
    });
    if (!stillExists) {
      setSelectedShipId('__all__');
    }
  }, [combinedShips, selectedShipId]);

  const renderOverlay = useCallback(() => {
    if (!mapRef?.current?.g) return;

    const { g } = mapRef.current;

    const getLayer = (className) => {
      let layer = g.select(`g.${className}`);
      if (layer.empty()) {
        layer = g.append('g').attr('class', className);
      }
      return layer;
    };

    const flightLayer = getLayer('flight-layer');
    const gatewayLayer = getLayer('gateway-layer');
    const tripRouteLayer = getLayer('trip-route-layer');
    const gatewayBubblesLayer = getLayer('gateway-bubbles-layer');
    const overlayLayer = getLayer('overlay-layer');
    const tripMarkersLayer = getLayer('trip-markers-layer'); // S/E markers above overlay
    const shipLayer = getLayer('ship-layer');
    const tooltipLayer = getLayer('tooltip-layer');

    flightLayer.selectAll('*').remove();
    gatewayLayer.selectAll('*').remove();
    tripRouteLayer.selectAll('*').remove();
    gatewayBubblesLayer.selectAll('*').remove();
    shipLayer.selectAll('*').remove();
    overlayLayer.selectAll('*').remove();
    tripMarkersLayer.selectAll('*').remove();
    tooltipLayer.selectAll('*').remove();

    // Layer ordering: flights < gateway-links < trip-route < gateway-bubbles < overlay < trip-markers < ships < tooltips
    flightLayer.raise();
    gatewayLayer.raise();       // Gateway links
    tripRouteLayer.raise();     // Trip route above gateway links
    gatewayBubblesLayer.raise(); // Gateway bubbles above trip route
    overlayLayer.raise();       // CX markers, destination markers
    tripMarkersLayer.raise();   // S/E markers above CX
    shipLayer.raise();
    tooltipLayer.raise();

    const { byId: planetsById, byNaturalId: planetsByNaturalId } = planetLookups;

    const transform = d3.zoomTransform(g.node());
    const zoomLevel = transform?.k || 1;

    // Gateway visualization
    if (isGatewayLayerVisible && gatewayData && gatewayData.length > 0 && graph?.systems) {
      
      // Color mapping based on volume upgrades and operational state
      const getGatewayColor = (volumeUpgrades, isOperational = true) => {
        // Non-operational gateways are grey
        if (!isOperational) return '#808080ff';
        
        switch (volumeUpgrades) {
          case 0: return '#f91616ff'; // weight3000_volume1000 color
          case 1: return '#ea8c08ff'; // capacity2000 color
          case 2: return '#00eeffff'; // weight1000_volume3000 color
          case 3: return '#d35cffff'; // capacity5000 color
          default: return '#d35cffff'; // 3+ uses capacity5000 color
        }
      };

      // Build gateway lookup by GatewayId
      const gatewayById = new Map();
      gatewayData.forEach(gw => {
        gatewayById.set(gw.GatewayId, gw);
      });

      // Find linked gateway pairs
      const processedLinks = new Set();
      
      gatewayData.forEach(gateway => {
        if (!gateway.OutgoingLink) return;
        if (gateway.LinkStatus !== 'ESTABLISHED') return;

        const linkKey = [gateway.GatewayId, gateway.OutgoingLink].sort().join('-');
        if (processedLinks.has(linkKey)) return;
        processedLinks.add(linkKey);

        const targetGateway = gatewayById.get(gateway.OutgoingLink);
        if (!targetGateway) return;

        // Get system positions for both gateways
        // Gateway LocationNaturalId is like "ZV-307c", need to extract system part (before letter suffix if planet)
        const extractSystemNaturalId = (locationNaturalId) => {
          if (!locationNaturalId) return null;
          // If it ends with a lowercase letter (planet suffix), remove it
          const match = locationNaturalId.match(/^([A-Z]{2}-\d{3})/i);
          return match ? match[1].toUpperCase() : locationNaturalId.toUpperCase();
        };

        const sourceSystemNatId = extractSystemNaturalId(gateway.LocationNaturalId);
        const targetSystemNatId = extractSystemNaturalId(targetGateway.LocationNaturalId);
        
        if (!sourceSystemNatId || !targetSystemNatId) return;

        const sourceSystemId = systemIdByNaturalId[sourceSystemNatId] || systemIdByNaturalId[sourceSystemNatId.toLowerCase()];
        const targetSystemId = systemIdByNaturalId[targetSystemNatId] || systemIdByNaturalId[targetSystemNatId.toLowerCase()];

        if (!sourceSystemId || !targetSystemId) return;

        const sourceSystem = graph.systems[sourceSystemId];
        const targetSystem = graph.systems[targetSystemId];

        if (!sourceSystem || !targetSystem) return;

        // Determine if link is bidirectional or unidirectional
        const sourceOperational = gateway.OperationalState === 'OPERATIONAL';
        const targetOperational = targetGateway.OperationalState === 'OPERATIONAL';
        const isBidirectional = sourceOperational && targetOperational;

        const sourceVolumeUpgrades = gateway.VolumeUpgrades || 0;
        const targetVolumeUpgrades = targetGateway.VolumeUpgrades || 0;
        const hasVolumeDifference = sourceVolumeUpgrades !== targetVolumeUpgrades;

        const startX = sourceSystem.x;
        const startY = sourceSystem.y;
        const endX = targetSystem.x;
        const endY = targetSystem.y;

        const strokeWidth = Math.max(4.8 / zoomLevel, 2.4); // Reduced by 20%
        const dashArray = `${24 / zoomLevel} ${16 / zoomLevel}`; // Longer dashes and gaps
        
        // Calculate perpendicular offset for dual lines
        const dx = endX - startX;
        const dy = endY - startY;
        const len = Math.sqrt(dx * dx + dy * dy);
        const perpX = -dy / len;
        const perpY = dx / len;
        const offset = hasVolumeDifference && isBidirectional ? Math.max(3 / zoomLevel, 1.5) : 0;

        // Helper to draw a single gateway line with arrows along its length
        const drawGatewayLine = (x1, y1, x2, y2, color, showArrow, arrowDirection, gwData, targetGwData) => {
          const path = gatewayLayer.append('path')
            .attr('class', 'gateway-link')
            .attr('d', `M ${x1} ${y1} L ${x2} ${y2}`)
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', strokeWidth)
            .attr('stroke-dasharray', dashArray)
            .attr('stroke-opacity', 0.85)
            .attr('data-gateway-link', linkKey)
            .style('cursor', 'pointer');

          if (showArrow) {
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const arrowAngle = arrowDirection === 'forward' ? angle : angle + Math.PI;
            const lineLen = Math.sqrt((x2-x1)*(x2-x1) + (y2-y1)*(y2-y1));
            
            // Arrow parameters - larger triangles, bigger than link width
            const arrowLength = Math.max(10 / zoomLevel, 6);
            const arrowWidth = Math.max(12 / zoomLevel, 7);
            const arrowSpacing = Math.max(35 / zoomLevel, 20);
            const numArrows = Math.max(3, Math.floor(lineLen / arrowSpacing));
            
            // Draw arrows along the line
            for (let i = 1; i < numArrows; i++) {
              const t = i / numArrows;
              const ax = x1 + (x2 - x1) * t;
              const ay = y1 + (y2 - y1) * t;
              
              // Large triangle arrow pointing in direction
              const tipX = ax + arrowLength * 0.5 * Math.cos(arrowAngle);
              const tipY = ay + arrowLength * 0.5 * Math.sin(arrowAngle);
              const baseLeftX = ax - arrowLength * 0.5 * Math.cos(arrowAngle) + arrowWidth * 0.5 * Math.cos(arrowAngle + Math.PI/2);
              const baseLeftY = ay - arrowLength * 0.5 * Math.sin(arrowAngle) + arrowWidth * 0.5 * Math.sin(arrowAngle + Math.PI/2);
              const baseRightX = ax - arrowLength * 0.5 * Math.cos(arrowAngle) - arrowWidth * 0.5 * Math.cos(arrowAngle + Math.PI/2);
              const baseRightY = ay - arrowLength * 0.5 * Math.sin(arrowAngle) - arrowWidth * 0.5 * Math.sin(arrowAngle + Math.PI/2);
              
              gatewayLayer.append('polygon')
                .attr('class', 'gateway-arrow')
                .attr('points', `${tipX},${tipY} ${baseLeftX},${baseLeftY} ${baseRightX},${baseRightY}`)
                .attr('fill', color)
                .attr('opacity', 1)
                .attr('data-gateway-link', linkKey);
            }
          }

          return path;
        };

        // Draw lines based on volume upgrade difference or operational state difference
        let linkPaths = [];
        
        // Check if we need two lines (different volume OR different operational state)
        const hasOperationalDifference = sourceOperational !== targetOperational;
        const needsTwoLines = (hasVolumeDifference || hasOperationalDifference);
        
        if (needsTwoLines) {
          // Two parallel lines with different colors showing capacity/status in each direction
          // Line 1: Source → Target direction (uses source's volume upgrades and operational state)
          const sourceColor = getGatewayColor(sourceVolumeUpgrades, sourceOperational);
          const line1StartX = startX + perpX * offset;
          const line1StartY = startY + perpY * offset;
          const line1EndX = endX + perpX * offset;
          const line1EndY = endY + perpY * offset;
          // Recalculate offset if it was 0 (same volume but different operational)
          const effectiveOffset = offset || Math.max(3 / zoomLevel, 1.5);
          const effLine1StartX = startX + perpX * effectiveOffset;
          const effLine1StartY = startY + perpY * effectiveOffset;
          const effLine1EndX = endX + perpX * effectiveOffset;
          const effLine1EndY = endY + perpY * effectiveOffset;
          linkPaths.push(drawGatewayLine(effLine1StartX, effLine1StartY, effLine1EndX, effLine1EndY, sourceColor, true, 'forward', gateway, targetGateway));

          // Line 2: Target → Source direction (uses target's volume upgrades and operational state)
          const targetColor = getGatewayColor(targetVolumeUpgrades, targetOperational);
          const effLine2StartX = startX - perpX * effectiveOffset;
          const effLine2StartY = startY - perpY * effectiveOffset;
          const effLine2EndX = endX - perpX * effectiveOffset;
          const effLine2EndY = endY - perpY * effectiveOffset;
          // Arrow direction is backward (from target back to source)
          linkPaths.push(drawGatewayLine(effLine2StartX, effLine2StartY, effLine2EndX, effLine2EndY, targetColor, true, 'backward', targetGateway, gateway));
        } else {
          // Single line - both directions have same volume and both operational (or both non-operational)
          const effectiveVolumeUpgrades = Math.min(sourceVolumeUpgrades, targetVolumeUpgrades);
          const effectiveOperational = sourceOperational && targetOperational;
          const linkColor = getGatewayColor(effectiveVolumeUpgrades, effectiveOperational);
          
          // No arrows for bidirectional same-capacity links
          linkPaths.push(drawGatewayLine(startX, startY, endX, endY, linkColor, false, 'forward', gateway, targetGateway));
        }

        // Variables to track bubble groups (may be null if bubbles disabled)
        let sourceBubbleGroup = null;
        let targetBubbleGroup = null;

        // Calculate capacity/fuel data (always needed for tooltips)
        const sourceCurrentJumps = gateway.Jumps || 0;
        const targetCurrentJumps = targetGateway.Jumps || 0;
        const sourceMaxJumps = gateway.JumpsPerDay || 250;
        const targetMaxJumps = targetGateway.JumpsPerDay || 250;
        const sourceRemainingJumps = Math.max(0, sourceMaxJumps - sourceCurrentJumps);
        const targetRemainingJumps = Math.max(0, targetMaxJumps - targetCurrentJumps);
        
        // Color based on remaining capacity (inverse of usage)
        const getCapacityColor = (remaining, max) => {
          const ratio = max > 0 ? remaining / max : 0;
          if (ratio >= 0.7) return '#22c55e'; // Green - plenty remaining
          if (ratio >= 0.4) return '#eab308'; // Yellow
          if (ratio >= 0.2) return '#f97316'; // Orange
          return '#ef4444'; // Red - nearly full
        };
        
        // Set bubble color to red if the opposite gateway does not have enough fuel for 1 jump (30 fuel per jump)
        const sourceFuelPerJump = gateway.FuelPerJump || 30;
        const targetFuelPerJump = targetGateway.FuelPerJump || 30;
        const sourceHasFuel = gateway.AvailableFuelUnits && gateway.AvailableFuelUnits >= sourceFuelPerJump;
        const targetHasFuel = targetGateway.AvailableFuelUnits && targetGateway.AvailableFuelUnits >= targetFuelPerJump;
        const sourceBubbleColor = targetHasFuel ? getCapacityColor(targetRemainingJumps, targetMaxJumps) : '#ef4444';
        const targetBubbleColor = sourceHasFuel ? getCapacityColor(sourceRemainingJumps, sourceMaxJumps) : '#ef4444';

        // Calculate fuel stats
        const sourceFuel = gateway.AvailableFuelUnits || 0;
        const sourceMaxFuel = gateway.MaxFuelUnits || 25000;
        const targetFuel = targetGateway.AvailableFuelUnits || 0;
        const targetMaxFuel = targetGateway.MaxFuelUnits || 25000;
        const sourceFuelRatio = sourceMaxFuel > 0 ? sourceFuel / sourceMaxFuel : 0;
        const targetFuelRatio = targetMaxFuel > 0 ? targetFuel / targetMaxFuel : 0;
        
        // Fuel color based on ratio
        const getFuelColor = (ratio) => {
          if (ratio >= 0.5) return '#fbbf24'; // Gold/Yellow - good fuel
          if (ratio >= 0.25) return '#f97316'; // Orange
          if (ratio >= 0.1) return '#ef4444'; // Red
          return '#991b1b'; // Dark red - critical
        };
        const sourceFuelColor = getFuelColor(sourceFuelRatio);
        const targetFuelColor = getFuelColor(targetFuelRatio);

        // Add capacity bubbles for each direction (only if showGatewayBubbles is true)
        if (showGatewayBubbles) {
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        
        // Calculate direction for positioning bubbles
        const linkDx = endX - startX;
        const linkDy = endY - startY;
        const linkLen = Math.sqrt(linkDx * linkDx + linkDy * linkDy);
        const linkDirX = linkDx / linkLen;
        const linkDirY = linkDy / linkLen;
        const linkPerpX = -linkDy / linkLen;
        const linkPerpY = linkDx / linkLen;
        
        const bubbleRadius = Math.max(12 / zoomLevel, 7);
        const bubbleFontSize = Math.max(9 / zoomLevel, 5);
        
        // Position bubbles along the link - offset from center toward each gateway
        const bubbleOffset = Math.max(20 / zoomLevel, 12);
        const sourceBubbleX = midX - linkDirX * bubbleOffset;
        const sourceBubbleY = midY - linkDirY * bubbleOffset;
        const targetBubbleX = midX + linkDirX * bubbleOffset;
        const targetBubbleY = midY + linkDirY * bubbleOffset;
        
        // Circular fuel ring parameters
        const fuelRingRadius = bubbleRadius + Math.max(4 / zoomLevel, 2.5);
        const fuelRingWidth = Math.max(3 / zoomLevel, 2);
        
        // Helper to create arc path for circular progress
        const createArcPath = (cx, cy, radius, startAngle, endAngle) => {
          const start = {
            x: cx + radius * Math.cos(startAngle),
            y: cy + radius * Math.sin(startAngle)
          };
          const end = {
            x: cx + radius * Math.cos(endAngle),
            y: cy + radius * Math.sin(endAngle)
          };
          const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
          return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
        };
        
        // Draw source bubble with circular fuel ring
        sourceBubbleGroup = gatewayBubblesLayer.append('g')
          .attr('class', 'gateway-capacity-bubble')
          .attr('data-gateway-link', linkKey)
          .style('cursor', 'pointer');
        
        // Source fuel ring background (full circle)
        sourceBubbleGroup.append('circle')
          .attr('cx', sourceBubbleX)
          .attr('cy', sourceBubbleY)
          .attr('r', fuelRingRadius)
          .attr('fill', 'none')
          .attr('stroke', 'rgba(0,0,0,0.5)')
          .attr('stroke-width', fuelRingWidth)
          .attr('opacity', 0.8);
        
        // Source fuel ring fill (arc based on fuel ratio)
        // NOTE: Bars were swapped - show the target's fuel bar on the source bubble
        if (targetFuelRatio > 0) {
          const startAngle = -Math.PI / 2; // Start at top
          const endAngle = startAngle + (2 * Math.PI * Math.min(targetFuelRatio, 0.999));
          sourceBubbleGroup.append('path')
            .attr('d', createArcPath(sourceBubbleX, sourceBubbleY, fuelRingRadius, startAngle, endAngle))
            .attr('fill', 'none')
            .attr('stroke', targetFuelColor)
            .attr('stroke-width', fuelRingWidth)
            .attr('stroke-linecap', 'round')
            .attr('opacity', 0.95);
        }
        
        // Source bubble circle (inner)
        sourceBubbleGroup.append('circle')
          .attr('cx', sourceBubbleX)
          .attr('cy', sourceBubbleY)
          .attr('r', bubbleRadius)
          .attr('fill', sourceBubbleColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', Math.max(1.5 / zoomLevel, 0.8))
          .attr('opacity', 0.95);
        
        // Directional triangle pointing toward source (the destination for this bubble)
        const sourceTriangleSize = Math.max(8 / zoomLevel, 5);
        const sourceTriangleOffset = bubbleRadius + sourceTriangleSize * 0.7;
        // Point toward source gateway (negative direction along link)
        const sourceTriangleTipX = sourceBubbleX - linkDirX * sourceTriangleOffset;
        const sourceTriangleTipY = sourceBubbleY - linkDirY * sourceTriangleOffset;
        const sourceTriangleBaseX = sourceBubbleX - linkDirX * (sourceTriangleOffset - sourceTriangleSize);
        const sourceTriangleBaseY = sourceBubbleY - linkDirY * (sourceTriangleOffset - sourceTriangleSize);
        const sourceTrianglePerpOffset = sourceTriangleSize * 0.6;
        
        sourceBubbleGroup.append('polygon')
          .attr('points', [
            `${sourceTriangleTipX},${sourceTriangleTipY}`,
            `${sourceTriangleBaseX + linkPerpX * sourceTrianglePerpOffset},${sourceTriangleBaseY + linkPerpY * sourceTrianglePerpOffset}`,
            `${sourceTriangleBaseX - linkPerpX * sourceTrianglePerpOffset},${sourceTriangleBaseY - linkPerpY * sourceTrianglePerpOffset}`
          ].join(' '))
          .attr('fill', sourceBubbleColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', Math.max(1 / zoomLevel, 0.5))
          .attr('opacity', 0.95);
        
        sourceBubbleGroup.append('text')
          .attr('x', sourceBubbleX)
          .attr('y', sourceBubbleY)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('fill', '#ffffff')
          .attr('font-size', `${bubbleFontSize}px`)
          .attr('font-weight', 700)
          .attr('stroke', '#000000')
          .attr('stroke-width', Math.max(0.5 / zoomLevel, 0.3))
          .attr('paint-order', 'stroke')
          .text(targetRemainingJumps > 999 ? '999+' : targetRemainingJumps);
        
        // Draw target direction bubble (closer to target gateway)
        targetBubbleGroup = gatewayBubblesLayer.append('g')
          .attr('class', 'gateway-capacity-bubble')
          .attr('data-gateway-link', linkKey)
          .style('cursor', 'pointer');
        
        // Target fuel ring background (full circle)
        targetBubbleGroup.append('circle')
          .attr('cx', targetBubbleX)
          .attr('cy', targetBubbleY)
          .attr('r', fuelRingRadius)
          .attr('fill', 'none')
          .attr('stroke', 'rgba(0,0,0,0.5)')
          .attr('stroke-width', fuelRingWidth)
          .attr('opacity', 0.8);
        
        // Target fuel ring fill (arc based on fuel ratio)
        // NOTE: Bars were swapped - show the source's fuel bar on the target bubble
        if (sourceFuelRatio > 0) {
          const startAngle = -Math.PI / 2; // Start at top
          const endAngle = startAngle + (2 * Math.PI * Math.min(sourceFuelRatio, 0.999));
          targetBubbleGroup.append('path')
            .attr('d', createArcPath(targetBubbleX, targetBubbleY, fuelRingRadius, startAngle, endAngle))
            .attr('fill', 'none')
            .attr('stroke', sourceFuelColor)
            .attr('stroke-width', fuelRingWidth)
            .attr('stroke-linecap', 'round')
            .attr('opacity', 0.95);
        }
        
        // Target bubble circle (inner)
        targetBubbleGroup.append('circle')
          .attr('cx', targetBubbleX)
          .attr('cy', targetBubbleY)
          .attr('r', bubbleRadius)
          .attr('fill', targetBubbleColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', Math.max(1.5 / zoomLevel, 0.8))
          .attr('opacity', 0.95);
        
        // Directional triangle pointing toward target (the destination for this bubble)
        const targetTriangleSize = Math.max(8 / zoomLevel, 5);
        const targetTriangleOffset = bubbleRadius + targetTriangleSize * 0.7;
        // Point toward target gateway (positive direction along link)
        const targetTriangleTipX = targetBubbleX + linkDirX * targetTriangleOffset;
        const targetTriangleTipY = targetBubbleY + linkDirY * targetTriangleOffset;
        const targetTriangleBaseX = targetBubbleX + linkDirX * (targetTriangleOffset - targetTriangleSize);
        const targetTriangleBaseY = targetBubbleY + linkDirY * (targetTriangleOffset - targetTriangleSize);
        const targetTrianglePerpOffset = targetTriangleSize * 0.6;
        
        targetBubbleGroup.append('polygon')
          .attr('points', [
            `${targetTriangleTipX},${targetTriangleTipY}`,
            `${targetTriangleBaseX + linkPerpX * targetTrianglePerpOffset},${targetTriangleBaseY + linkPerpY * targetTrianglePerpOffset}`,
            `${targetTriangleBaseX - linkPerpX * targetTrianglePerpOffset},${targetTriangleBaseY - linkPerpY * targetTrianglePerpOffset}`
          ].join(' '))
          .attr('fill', targetBubbleColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', Math.max(1 / zoomLevel, 0.5))
          .attr('opacity', 0.95);
        
        targetBubbleGroup.append('text')
          .attr('x', targetBubbleX)
          .attr('y', targetBubbleY)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('fill', '#ffffff')
          .attr('font-size', `${bubbleFontSize}px`)
          .attr('font-weight', 700)
          .attr('stroke', '#000000')
          .attr('stroke-width', Math.max(0.5 / zoomLevel, 0.3))
          .attr('paint-order', 'stroke')
          .text(sourceRemainingJumps > 999 ? '999+' : sourceRemainingJumps);
        } // End of showGatewayBubbles conditional

        // Look up planet names for gateway locations
        const sourcePlanet = planetsByNaturalId.get(gateway.LocationNaturalId?.toUpperCase());
        const targetPlanet = planetsByNaturalId.get(targetGateway.LocationNaturalId?.toUpperCase());
        const sourcePlanetName = sourcePlanet?.PlanetName || sourcePlanet?.Name || gateway.LocationNaturalId;
        const targetPlanetName = targetPlanet?.PlanetName || targetPlanet?.Name || targetGateway.LocationNaturalId;

        // Add hover tooltip to all link paths and bubbles
        const allHoverables = [...linkPaths];
        // Add bubble groups to hoverables (if they exist)
        if (sourceBubbleGroup) allHoverables.push(sourceBubbleGroup);
        if (targetBubbleGroup) allHoverables.push(targetBubbleGroup);
        
        allHoverables.forEach(element => {
          element
            .on('mouseover', function(event) {
              // Highlight all paths for this link
              gatewayLayer.selectAll(`[data-gateway-link="${linkKey}"]`)
                .filter('path')
                .attr('stroke-opacity', 1)
                .attr('stroke-width', strokeWidth * 1.5);

            // Calculate fuel-based potential jumps for tooltip
            const sourceJumpsFromFuel = gateway.FuelPerJump && gateway.AvailableFuelUnits 
              ? Math.floor(gateway.AvailableFuelUnits / gateway.FuelPerJump) 
              : 0;
            const targetJumpsFromFuel = targetGateway.FuelPerJump && targetGateway.AvailableFuelUnits 
              ? Math.floor(targetGateway.AvailableFuelUnits / targetGateway.FuelPerJump) 
              : 0;
            
            // Calculate reliability from historical upkeep phases and fuel availability
            const calculateReliability = (gw) => {
              const phases = gw.UpkeepPhases || [];
              
              // Only consider completed phases (not the current one which may be in progress)
              const completedPhases = phases.filter(p => {
                const endDate = new Date(p.End);
                return endDate < new Date();
              });
              
              // Calculate upkeep reliability
              let upkeepReliability = null;
              let fullUpkeepPhases = 0;
              let totalPhases = 0;
              
              if (completedPhases.length > 0) {
                const totalServiceLevel = completedPhases.reduce((sum, p) => sum + (p.ServiceLevel || 0), 0);
                upkeepReliability = (totalServiceLevel / completedPhases.length) * 100;
                fullUpkeepPhases = completedPhases.filter(p => p.ServiceLevel >= 0.999).length;
                totalPhases = completedPhases.length;
              }
              
              // Calculate fuel reliability from AverageFuelAvailibility and failed jumps
              const fuelAvailability = gw.AverageFuelAvailibility;
              const fuelReliability = fuelAvailability !== undefined && fuelAvailability !== null 
                ? fuelAvailability * 100 
                : null;
              
              // Get failed jumps data
              const jumpsIn24h = gw.Jumps || 0;
              const avgFailedFuel = gw.PhaseAverageJumpsFailedDueInsufficientFuel || 0;
              const avgFailedInoperative = gw.PhaseAverageJumpsFailedDueInoperative || 0;
              const avgFailedCapacity = gw.PhaseAverageJumpsFailedDueNoCapacity || 0;
              const totalAvgFailed = avgFailedFuel + avgFailedInoperative + avgFailedCapacity;
              
              // Calculate overall reliability combining upkeep and fuel
              let overallReliability = null;
              if (upkeepReliability !== null && fuelReliability !== null) {
                // Weight: 60% upkeep, 40% fuel availability
                overallReliability = (upkeepReliability * 0.6) + (fuelReliability * 0.4);
              } else if (upkeepReliability !== null) {
                overallReliability = upkeepReliability;
              } else if (fuelReliability !== null) {
                overallReliability = fuelReliability;
              }
              
              return {
                upkeepReliability,
                fuelReliability,
                overallReliability,
                fullUpkeepPhases,
                totalPhases,
                avgFailedFuel,
                avgFailedInoperative,
                avgFailedCapacity,
                totalAvgFailed,
                jumpsIn24h,
              };
            };
            
            const sourceReliability = calculateReliability(gateway);
            const targetReliability = calculateReliability(targetGateway);
            
            // Get reliability color based on percentage
            const getReliabilityColor = (reliability) => {
              if (reliability === null) return '#64748b';
              if (reliability >= 95) return '#22c55e'; // Green
              if (reliability >= 80) return '#84cc16'; // Lime
              if (reliability >= 60) return '#fbbf24'; // Yellow
              if (reliability >= 40) return '#f97316'; // Orange
              return '#ef4444'; // Red
            };
            
            const sourceReliabilityColor = getReliabilityColor(sourceReliability?.overallReliability);
            const targetReliabilityColor = getReliabilityColor(targetReliability?.overallReliability);
            
            // Format reliability display
            const formatReliability = (rel, color) => {
              if (!rel || rel.overallReliability === null) {
                return `<div style="color: #64748b; font-size: 12px; margin-top: 5px;">📊 Reliability: No history</div>`;
              }
              
              let html = `<div style="color: ${color}; font-size: 12px; margin-top: 5px;">📊 Reliability: ${rel.overallReliability.toFixed(1)}%</div>`;
              
              // Show breakdown
              const details = [];
              if (rel.upkeepReliability !== null) {
                details.push(`Upkeep: ${rel.upkeepReliability.toFixed(0)}% (${rel.fullUpkeepPhases}/${rel.totalPhases})`);
              }
              if (rel.fuelReliability !== null) {
                details.push(`Fuel: ${rel.fuelReliability.toFixed(0)}%`);
              }
              if (details.length > 0) {
                html += `<div style="color: #64748b; font-size: 10px;">${details.join(' · ')}</div>`;
              }
              
              // Show failed jumps if any
              if (rel.totalAvgFailed > 0) {
                const failDetails = [];
                if (rel.avgFailedFuel > 0) failDetails.push(`⛽${rel.avgFailedFuel.toFixed(1)}`);
                if (rel.avgFailedInoperative > 0) failDetails.push(`🔧${rel.avgFailedInoperative.toFixed(1)}`);
                if (rel.avgFailedCapacity > 0) failDetails.push(`📦${rel.avgFailedCapacity.toFixed(1)}`);
                html += `<div style="color: #f87171; font-size: 10px;">Avg fails/phase: ${failDetails.join(' ')}</div>`;
              }
              
              return html;
            };
            
            const tooltipHtml = [
              '<div style="background: rgba(15, 23, 42, 0.95); padding: 20px 24px; border-radius: 8px; border: 1px solid #334155; box-shadow: 0 6px 16px rgba(0,0,0,0.5); max-width: 480px; font-family: system-ui, -apple-system, sans-serif;">',
              `<div style="color: #f7a600; font-weight: 700; font-size: 16px; margin-bottom: 13px; letter-spacing: 0.7px;">GATEWAY LINK</div>`,
              // Directional capacity summary - swapped order (target on left, source on right)
              `<div style="display: flex; gap: 10px; margin-bottom: 13px;">`,
              `<div style="flex: 1; background: ${targetBubbleColor}22; border: 1px solid ${targetBubbleColor}; border-radius: 6px; padding: 8px 11px; text-align: center;">`,
              `<div style="color: #f7a600; font-size: 10px; margin-bottom: 3px;">« TO ${targetPlanetName}</div>`,
              `<div style="color: ${targetBubbleColor}; font-size: 20px; font-weight: 700;">${sourceRemainingJumps}</div>`,
              `<div style="color: #94a3b8; font-size: 9px;">${sourceCurrentJumps} / ${sourceMaxJumps} used</div>`,
              `<div style="color: ${sourceFuelColor}; font-size: 9px; margin-top: 3px;">⛽ ${Math.round(sourceFuelRatio * 100)}%</div>`,
              '</div>',
              `<div style="flex: 1; background: ${sourceBubbleColor}22; border: 1px solid ${sourceBubbleColor}; border-radius: 6px; padding: 8px 11px; text-align: center;">`,
              `<div style="color: #f7a600; font-size: 10px; margin-bottom: 3px;">TO ${sourcePlanetName} »</div>`,
              `<div style="color: ${sourceBubbleColor}; font-size: 20px; font-weight: 700;">${targetRemainingJumps}</div>`,
              `<div style="color: #94a3b8; font-size: 9px;">${targetCurrentJumps} / ${targetMaxJumps} used</div>`,
              `<div style="color: ${targetFuelColor}; font-size: 9px; margin-top: 3px;">⛽ ${Math.round(targetFuelRatio * 100)}%</div>`,
              '</div>',
              '</div>',
              '<div style="display: flex; gap: 20px;">',
              // Source gateway (left side)
              '<div style="flex: 1; text-align: left;">',
              `<div style="color: #ffffff; font-weight: 600; font-size: 14px; margin-bottom: 8px;">${gateway.Name || gateway.NaturalId}</div>`,
              `<div style="color: ${sourceOperational ? '#4ade80' : '#f87171'}; font-size: 12px; margin-top: 5px;">${sourceOperational ? '● Operational' : '○ ' + gateway.OperationalState}</div>`,
              `<div style="color: #bfdbfe; font-size: 12px; margin-top: 8px;">Volume: ${gateway.MaxShipVolume?.toLocaleString()} m³</div>`,
              `<div style="color: #c4b5fd; font-size: 12px;">Upgrades: ${gateway.VolumeUpgrades || 0}</div>`,
              `<div style="color: #fb923c; font-size: 12px; margin-top: 5px;">💰 ${gateway.UsageAmount?.toLocaleString() || 0} ${gateway.UsageCurrency || 'AIC'}/jump</div>`,
              `<div style="color: ${sourceFuelColor}; font-size: 12px;">⛽ ${gateway.AvailableFuelUnits?.toLocaleString() || 0} / ${gateway.MaxFuelUnits?.toLocaleString() || 0}</div>`,
              `<div style="color: #22c55e; font-size: 12px;">Fuel for ${sourceJumpsFromFuel} jumps</div>`,
              formatReliability(targetReliability, targetReliabilityColor),
              '</div>',
              // Centered divider
              '<div style="width: 1px; background: #334155;"></div>',
              // Target gateway (right side)
              '<div style="flex: 1; text-align: left;">',
              `<div style="color: #ffffff; font-weight: 600; font-size: 14px; margin-bottom: 8px;">${targetGateway.Name || targetGateway.NaturalId}</div>`,
              `<div style="color: ${targetOperational ? '#4ade80' : '#f87171'}; font-size: 12px; margin-top: 5px;">${targetOperational ? '● Operational' : '○ ' + targetGateway.OperationalState}</div>`,
              `<div style="color: #bfdbfe; font-size: 12px; margin-top: 8px;">Volume: ${targetGateway.MaxShipVolume?.toLocaleString()} m³</div>`,
              `<div style="color: #c4b5fd; font-size: 12px;">Upgrades: ${targetGateway.VolumeUpgrades || 0}</div>`,
              `<div style="color: #fb923c; font-size: 12px; margin-top: 5px;">💰 ${targetGateway.UsageAmount?.toLocaleString() || 0} ${targetGateway.UsageCurrency || 'AIC'}/jump</div>`,
              `<div style="color: ${targetFuelColor}; font-size: 12px;">⛽ ${targetGateway.AvailableFuelUnits?.toLocaleString() || 0} / ${targetGateway.MaxFuelUnits?.toLocaleString() || 0}</div>`,
              `<div style="color: #22c55e; font-size: 12px;">Fuel for ${targetJumpsFromFuel} jumps</div>`,
              formatReliability(sourceReliability, sourceReliabilityColor),
              '</div>',
              '</div>',
              hasVolumeDifference && isBidirectional
                ? `<div style="color: #fbbf24; font-size: 12px; margin-top: 13px; padding-top: 11px; border-top: 1px solid #334155; text-align: center;">⚡ Asymmetric Volume (${targetVolumeUpgrades} ↔ ${sourceVolumeUpgrades} upgrades)</div>`
                : `<div style="color: #64748b; font-size: 12px; margin-top: 13px; padding-top: 11px; border-top: 1px solid #334155; text-align: center;">${isBidirectional ? '↔ Bidirectional' : '→ Unidirectional'}</div>`,
              '</div>'
            ].join('');

            d3.select('body').append('div')
              .attr('class', 'gateway-tooltip')
              .style('position', 'fixed')
              .style('left', (event.clientX + 20) + 'px')
              .style('top', (event.clientY - 15) + 'px')
              .style('pointer-events', 'none')
              .style('z-index', 10000)
              .html(tooltipHtml);
          })
          .on('mouseout', function() {
            // Reset all paths for this link
            gatewayLayer.selectAll(`[data-gateway-link="${linkKey}"]`)
              .filter('path')
              .attr('stroke-opacity', 0.85)
              .attr('stroke-width', strokeWidth);
            d3.selectAll('.gateway-tooltip').remove();
          });
        });
      });

      // Draw gateway markers at system locations
      const gatewaySystemLocations = new Map();
      gatewayData.forEach(gateway => {
        const systemNatId = gateway.LocationNaturalId?.match(/^([A-Z]{2}-\d{3})/i)?.[1]?.toUpperCase();
        if (!systemNatId) return;
        
        const systemId = systemIdByNaturalId[systemNatId] || systemIdByNaturalId[systemNatId.toLowerCase()];
        if (!systemId || !graph.systems[systemId]) return;

        if (!gatewaySystemLocations.has(systemId)) {
          gatewaySystemLocations.set(systemId, []);
        }
        gatewaySystemLocations.get(systemId).push(gateway);
      });

      gatewaySystemLocations.forEach((gateways, systemId) => {
        const system = graph.systems[systemId];
        const markerSize = Math.max(8 / zoomLevel, 4);
        
        // Draw a small gateway indicator
        gatewayLayer.append('circle')
          .attr('cx', system.x)
          .attr('cy', system.y)
          .attr('r', markerSize)
          .attr('fill', 'none')
          .attr('stroke', '#f7a600')
          .attr('stroke-width', Math.max(2 / zoomLevel, 1))
          .attr('stroke-dasharray', `${3 / zoomLevel} ${2 / zoomLevel}`)
          .attr('opacity', 0.8);
      });
    }

    // Trip Route Visualization
    if (tripCalculatorOpen && tripRoute && tripRoute.path && tripRoute.path.length > 0 && graph?.systems) {
      const tripLayer = getLayer('trip-route-layer');
      tripLayer.selectAll('*').remove();
      // Layer order already set above - don't raise here

      const tripStrokeWidth = Math.max(6 / zoomLevel, 3);
      const glowStrokeWidth = Math.max(12 / zoomLevel, 6);

      // Helper to get system center from SVG elements
      const getTripSystemCenter = (systemId) => {
        if (!systemId) return null;
        try {
          const rect = g.select(`rect[id="${systemId}"]`);
          if (!rect.empty()) {
            const x = parseFloat(rect.attr('x'));
            const y = parseFloat(rect.attr('y'));
            const w = parseFloat(rect.attr('width')) || 0;
            const h = parseFloat(rect.attr('height')) || 0;
            return { x: x + w / 2, y: y + h / 2 };
          }
        } catch (err) {
          // Fall back to graph data
        }
        // Fallback to graph coordinates
        const graphNode = graph.systems[systemId];
        if (graphNode) return { x: graphNode.x, y: graphNode.y };
        return null;
      };

      // Draw route segments
      tripRoute.path.forEach((segment, index) => {
        const fromSystem = graph.systems[segment.fromSystem];
        const toSystem = graph.systems[segment.toSystem];
        
        if (!fromSystem || !toSystem) return;

        // Different colors for gateway vs FTL
        const isGateway = segment.type === 'gateway';
        const segmentColor = isGateway ? '#a78bfa' : '#34d399'; // Purple for gateway, green for FTL
        const glowColor = isGateway ? 'rgba(167, 139, 250, 0.3)' : 'rgba(52, 211, 153, 0.3)';

        // For FTL lanes, use system center; for gateways, use graph coordinates
        let fromX, fromY, toX, toY;
        if (!isGateway) {
          // FTL - use center of system circles
          const fromCenter = getTripSystemCenter(segment.fromSystem);
          const toCenter = getTripSystemCenter(segment.toSystem);
          if (!fromCenter || !toCenter) return;
          fromX = fromCenter.x;
          fromY = fromCenter.y;
          toX = toCenter.x;
          toY = toCenter.y;
        } else {
          // Gateway - use graph coordinates
          fromX = fromSystem.x;
          fromY = fromSystem.y;
          toX = toSystem.x;
          toY = toSystem.y;
        }

        // Glow effect
        tripLayer.append('line')
          .attr('x1', fromX)
          .attr('y1', fromY)
          .attr('x2', toX)
          .attr('y2', toY)
          .attr('stroke', glowColor)
          .attr('stroke-width', glowStrokeWidth)
          .attr('stroke-linecap', 'round');

        // Main line - FTL uses dashed line, gateway uses solid
        const mainLine = tripLayer.append('line')
          .attr('x1', fromX)
          .attr('y1', fromY)
          .attr('x2', toX)
          .attr('y2', toY)
          .attr('stroke', segmentColor)
          .attr('stroke-width', tripStrokeWidth)
          .attr('stroke-linecap', 'round');
        
        if (!isGateway) {
          mainLine.attr('stroke-dasharray', `${8 / zoomLevel} ${4 / zoomLevel}`);
        }

        // Arrow at midpoint
        const midX = (fromX + toX) / 2;
        const midY = (fromY + toY) / 2;
        const angle = Math.atan2(toY - fromY, toX - fromX);
        const arrowSize = Math.max(10 / zoomLevel, 5);

        const arrowPoints = [
          { x: midX + arrowSize * Math.cos(angle), y: midY + arrowSize * Math.sin(angle) },
          { x: midX + arrowSize * Math.cos(angle + 2.5), y: midY + arrowSize * Math.sin(angle + 2.5) },
          { x: midX + arrowSize * Math.cos(angle - 2.5), y: midY + arrowSize * Math.sin(angle - 2.5) }
        ];

        tripLayer.append('polygon')
          .attr('points', arrowPoints.map(p => `${p.x},${p.y}`).join(' '))
          .attr('fill', segmentColor);

        // Step number at midpoint
        const labelOffset = Math.max(15 / zoomLevel, 8);
        tripLayer.append('circle')
          .attr('cx', midX - labelOffset * Math.sin(angle))
          .attr('cy', midY + labelOffset * Math.cos(angle))
          .attr('r', Math.max(12 / zoomLevel, 6))
          .attr('fill', '#0f172a')
          .attr('stroke', segmentColor)
          .attr('stroke-width', Math.max(2 / zoomLevel, 1));

        tripLayer.append('text')
          .attr('x', midX - labelOffset * Math.sin(angle))
          .attr('y', midY + labelOffset * Math.cos(angle))
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('fill', segmentColor)
          .attr('font-size', Math.max(10 / zoomLevel, 5))
          .attr('font-weight', 'bold')
          .text(index + 1);
      });

      // Draw start marker (in tripMarkersLayer to be above CX markers)
      if (tripStartSystem && graph.systems[tripStartSystem.id]) {
        const startCenter = getTripSystemCenter(tripStartSystem.id);
        if (startCenter) {
          const markerSize = Math.max(12 / zoomLevel, 6); // Reduced by 25%

          // Outer glow
          tripMarkersLayer.append('circle')
            .attr('cx', startCenter.x)
            .attr('cy', startCenter.y)
            .attr('r', markerSize * 1.5)
            .attr('fill', 'rgba(34, 197, 94, 0.3)')
            .attr('stroke', 'none')
            .style('pointer-events', 'none');

          // Marker circle
          tripMarkersLayer.append('circle')
            .attr('cx', startCenter.x)
            .attr('cy', startCenter.y)
            .attr('r', markerSize)
            .attr('fill', '#22c55e')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(2 / zoomLevel, 1))
            .style('pointer-events', 'none');

          // Start label
          tripMarkersLayer.append('text')
            .attr('x', startCenter.x)
            .attr('y', startCenter.y)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('fill', '#ffffff')
            .attr('font-size', Math.max(10 / zoomLevel, 5))
            .attr('font-weight', 'bold')
            .style('pointer-events', 'none')
            .text('S');
        }
      }

      // Draw end marker (in tripMarkersLayer to be above CX markers)
      if (tripEndSystem && graph.systems[tripEndSystem.id]) {
        const endCenter = getTripSystemCenter(tripEndSystem.id);
        if (endCenter) {
          const markerSize = Math.max(12 / zoomLevel, 6); // Reduced by 25%

          // Outer glow
          tripMarkersLayer.append('circle')
            .attr('cx', endCenter.x)
            .attr('cy', endCenter.y)
            .attr('r', markerSize * 1.5)
            .attr('fill', 'rgba(239, 68, 68, 0.3)')
            .attr('stroke', 'none')
            .style('pointer-events', 'none');

          // Marker circle
          tripMarkersLayer.append('circle')
            .attr('cx', endCenter.x)
            .attr('cy', endCenter.y)
            .attr('r', markerSize)
            .attr('fill', '#ef4444')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(2 / zoomLevel, 1))
            .style('pointer-events', 'none');

          // End label
          tripMarkersLayer.append('text')
            .attr('x', endCenter.x)
            .attr('y', endCenter.y)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('fill', '#ffffff')
            .attr('font-size', Math.max(10 / zoomLevel, 5))
            .attr('font-weight', 'bold')
            .style('pointer-events', 'none')
            .text('E');
        }
      }
    } else {
      // Clear trip route layer if not active
      const tripLayer = getLayer('trip-route-layer');
      tripLayer.selectAll('*').remove();
      const tripMarkersLayer = getLayer('trip-markers-layer');
      tripMarkersLayer.selectAll('*').remove();
    }

    const shipsById = new Map();
    (combinedShips || []).forEach((ship) => {
      const keys = [
        ship?.ShipId,
        ship?.Id,
        ship?.Ship,
        ship?.Registration,
        ship?.Name
      ].filter((candidate) => candidate != null);

      keys.forEach((candidate) => {
        shipsById.set(candidate, ship);
        shipsById.set(String(candidate), ship);
      });
    });

    const flightsByShipId = new Map();
    (combinedFlights || []).forEach((flight) => {
      if (flight.ShipId) {
        flightsByShipId.set(flight.ShipId, flight);
      }
    });
    const flightSegmentsCache = new Map();
    const segmentUsageMap = new Map();
    const lineGenerator = d3.line().x((d) => d.x).y((d) => d.y).curve(d3.curveLinear);
    const markerSlotMap = new Map();
    const activeShipIds = new Set();
    const flightDiagnostics = [];

    const getMarkerSlot = (key) => {
      const count = markerSlotMap.get(key) || 0;
      markerSlotMap.set(key, count + 1);
      return count;
    };

    const extractEpochValues = (record, keys) => {
      if (!record) return [];
      return keys.reduce((acc, key) => {
        const raw = record[key];
        if (raw == null) {
          return acc;
        }
        const num = Number(raw);
        if (Number.isFinite(num) && num > 0) {
          acc.push(num);
        }
        return acc;
      }, []);
    };

    const computeFlightTiming = (flightRecord, segments) => {
      const departures = [];
      const arrivals = [];

      segments.forEach((info) => {
        const bounds = info.timeBounds || getSegmentTimeBounds(info.segment);
        if (bounds.departure != null) {
          departures.push(bounds.departure);
        }
        if (bounds.arrival != null) {
          arrivals.push(bounds.arrival);
        }
      });

      departures.push(
        ...extractEpochValues(flightRecord, [
          'DepartureEpochMs',
          'DepartureTimeEpochMs',
          'ScheduledDepartureEpochMs',
          'StartEpochMs',
          'SegmentDepartureEpochMs'
        ])
      );

      arrivals.push(
        ...extractEpochValues(flightRecord, [
          'ArrivalEpochMs',
          'ArrivalTimeEpochMs',
          'ScheduledArrivalEpochMs',
          'EndEpochMs',
          'SegmentArrivalEpochMs'
        ])
      );

      const earliestDeparture = departures.length > 0 ? Math.min(...departures) : null;
      const finalArrival = arrivals.length > 0 ? Math.max(...arrivals) : null;
      const duration = earliestDeparture != null && finalArrival != null
        ? Math.max(0, finalArrival - earliestDeparture)
        : null;

      return {
        departure: earliestDeparture,
        arrival: finalArrival,
        duration
      };
    };

    const getShipLocationSystemId = (ship) => {
      if (!ship) return null;

      const directCandidates = [
        ship.CurrentSystemId,
        ship.SystemId,
        ship.CurrentLocationSystemId,
        ship.LocationSystemId,
        ship.LocationId,
        ship.LocationSystemNaturalId,
        ship.LocationNaturalId,
        ship.LastKnownSystemId,
        ship.LastSystemId,
        ship.LastLocationSystemId,
        ship.HomeSystemId,
        ship.SystemNaturalId
      ];

      for (const candidate of directCandidates) {
        const resolved = resolveSystemId(candidate);
        if (resolved) {
          return resolved;
        }
      }

      const nestedCandidates = [
        ship.CurrentLocation,
        ship.Location,
        ship.DockedAt,
        ship.LastLocation,
        ship.HomeLocation,
        ship.BasedAt,
        ship.Station
      ];

      for (const nested of nestedCandidates) {
        const resolved = extractSystemId(nested);
        if (resolved) {
          return resolved;
        }
      }

      if (ship.LocationLines) {
        const resolved = extractSystemId(ship.LocationLines);
        if (resolved) {
          return resolved;
        }
      }

      return null;
    };

    const getPrimaryShipId = (ship) => {
      if (!ship) return null;
      const candidates = [ship.ShipId, ship.Id, ship.Ship, ship.Registration, ship.Name];
      const match = candidates.find((candidate) => candidate != null);
      return match != null ? String(match) : null;
    };

    const getShipLoadInfo = (ship, explicitKey) => (
      getShipLoadInfoById(explicitKey ?? getPrimaryShipId(ship))
    );

    const classifyShipType = (ship, explicitKey) => {
      const loadInfo = getShipLoadInfo(ship, explicitKey);
      let volumeCapacity = loadInfo?.volumeCapacity;
      let weightCapacity = loadInfo?.weightCapacity;

      if ((volumeCapacity == null || !Number.isFinite(volumeCapacity)) && ship) {
        volumeCapacity = pickNumericValue(ship, STORAGE_VOLUME_CAPACITY_FIELDS);
      }

      if ((weightCapacity == null || !Number.isFinite(weightCapacity)) && ship) {
        weightCapacity = pickNumericValue(ship, STORAGE_WEIGHT_CAPACITY_FIELDS);
      }

      if (!Number.isFinite(volumeCapacity)) {
        volumeCapacity = null;
      }

      if (!Number.isFinite(weightCapacity)) {
        weightCapacity = null;
      }

      const toComparable = (value) => {
        if (value == null || !Number.isFinite(value)) return null;
        return Math.round(value);
      };

      const approxMatches = (expected, actual) => {
        if (expected == null || actual == null) return false;
        if (expected === 0) return actual === 0;
        const tolerance = Math.max(1, expected * 0.02);
        return Math.abs(actual - expected) <= tolerance;
      };

      const normalizedVolume = toComparable(volumeCapacity);
      const normalizedWeight = toComparable(weightCapacity);

      const matchedProfile = (() => {
        if (normalizedWeight != null && normalizedVolume != null) {
          return SHIP_CAPACITY_PROFILES.find((profile) => (
            approxMatches(profile.weight, normalizedWeight)
            && approxMatches(profile.volume, normalizedVolume)
          )) || null;
        }

        const singleValue = normalizedWeight ?? normalizedVolume;
        if (singleValue == null) {
          return null;
        }

        return SHIP_CAPACITY_PROFILES.find((profile) => (
          profile.weight === profile.volume
          && approxMatches(profile.weight, singleValue)
        )) || null;
      })();

      if (matchedProfile) {
        return {
          key: matchedProfile.key,
          label: matchedProfile.label,
          color: matchedProfile.color,
          volumeCapacity,
          weightCapacity,
          loadInfo
        };
      }

      return {
        key: 'unknown',
        label: 'Unknown Capacity',
        color: '#9ca3af',
        volumeCapacity,
        weightCapacity,
        loadInfo
      };
    };

    const safeString = (value) => (typeof value === 'string' ? value.trim() : value);

    const resolveSystemId = (candidate) => {
      const id = safeString(candidate);
      if (!id) return null;
      if (systemNames?.[id]) return id;
      if (graph?.systems?.[id]) return id;
      if (graph?.nodes?.[id]) return id;
      if (graph?.nodes && typeof graph.nodes.get === 'function' && graph.nodes.get(id)) return id;

      if (typeof id === 'string') {
        const normalizedName = id.trim().toLowerCase();
        if (systemLookups.byName.has(normalizedName)) {
          return systemLookups.byName.get(normalizedName);
        }

        const cleanedName = normalizedName
          .replace(/\bstation\b/g, '')
          .replace(/[^a-z0-9\s-]/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (cleanedName && systemLookups.byName.has(cleanedName)) {
          return systemLookups.byName.get(cleanedName);
        }

        const parenMatch = id.match(/\(([A-Za-z0-9-]+)\)/);
        if (parenMatch) {
          const naturalIdCandidate = parenMatch[1].toUpperCase();
          if (systemLookups.byNaturalId.has(naturalIdCandidate)) {
            return systemLookups.byNaturalId.get(naturalIdCandidate);
          }
        }

        const upperCandidate = id.toUpperCase();
        if (systemLookups.byNaturalId.has(upperCandidate)) {
          return systemLookups.byNaturalId.get(upperCandidate);
        }
      }

      return null;
    };

    const extractSystemId = (lines) => {
      if (!lines) return null;
      const entries = Array.isArray(lines) ? lines : [lines];
      for (const entry of entries) {
        if (typeof entry === 'string') {
          const resolved = resolveSystemId(entry);
          if (resolved) return resolved;
          continue;
        }
        if (entry && typeof entry === 'object') {
          const candidates = [
            entry.SystemId,
            entry.SystemNaturalId,
            entry.OriginSystemId,
            entry.DestinationSystemId,
            entry.NaturalId,
            entry.LineId,
            entry.LineNaturalId,
            entry.Id,
            entry.Line,
            entry.Address,
            entry.From,
            entry.To
          ];
          for (const candidate of candidates) {
            const resolved = resolveSystemId(candidate);
            if (resolved) return resolved;
          }
        }
      }
      return null;
    };

    const extractLocationDetails = (lines) => {
      const entries = Array.isArray(lines) ? lines : (lines ? [lines] : []);
      let systemId = null;
      let systemName = null;
      let systemNaturalId = null;
      let planetId = null;
      let planetNaturalId = null;
      let planetName = null;
      let stationId = null;
      let stationNaturalId = null;
      let stationName = null;

      let displayKind = null;
      let displayPriority = Number.POSITIVE_INFINITY;
      let displayLabel = null;

      const setDisplayCandidate = (kind, label, priority) => {
        if (label == null) {
          return;
        }
        if (priority < displayPriority) {
          displayPriority = priority;
          displayKind = kind;
          displayLabel = label;
        } else if (priority === displayPriority && !displayLabel) {
          displayKind = kind;
          displayLabel = label;
        }
      };

      const applyPlanetEntry = (planetEntry, fallbackId, fallbackNaturalId, fallbackName) => {
        if (!planetEntry && fallbackId == null && fallbackNaturalId == null && fallbackName == null) {
          return false;
        }

        if (!planetId && (planetEntry?.PlanetId != null || fallbackId != null)) {
          planetId = planetEntry?.PlanetId != null
            ? String(planetEntry.PlanetId)
            : String(fallbackId);
        }

        if (!planetNaturalId && (planetEntry?.PlanetNaturalId || fallbackNaturalId)) {
          const rawNaturalId = planetEntry?.PlanetNaturalId || fallbackNaturalId;
          planetNaturalId = rawNaturalId != null ? String(rawNaturalId).toUpperCase() : planetNaturalId;
        }

        if (!planetName && (planetEntry?.PlanetName || fallbackName || planetNaturalId || planetId)) {
          planetName = planetEntry?.PlanetName
            || fallbackName
            || planetNaturalId
            || planetId
            || planetName;
        }

        if (!systemNaturalId && planetEntry?.SystemNaturalId != null) {
          systemNaturalId = String(planetEntry.SystemNaturalId);
        }

        if (!systemId && planetEntry?.SystemId) {
          systemId = planetEntry.SystemId;
        }

        if (!systemName && planetEntry?.SystemId) {
          systemName = systemNames?.[planetEntry.SystemId]
            || systemNaturalId
            || systemName;
        }

        setDisplayCandidate('planet', planetName || fallbackName || planetNaturalId || planetId, 0);
        return Boolean(planetEntry);
      };

      const applyStationEntry = (entryObj, lineId, lineNaturalId, lineName) => {
        const stationIdCandidate = entryObj?.StationId
          || entryObj?.StationIdentifier
          || entryObj?.Identifier
          || lineId;
        const stationNaturalIdCandidate = entryObj?.StationNaturalId
          || entryObj?.NaturalId
          || lineNaturalId;
        const stationNameCandidate = lineName
          || entryObj?.StationName
          || entryObj?.Name
          || entryObj?.DisplayName
          || null;

        if (!stationId && stationIdCandidate) {
          stationId = String(stationIdCandidate);
        }
        if (!stationNaturalId && stationNaturalIdCandidate) {
          stationNaturalId = String(stationNaturalIdCandidate).toUpperCase();
        }
        if (!stationName && (stationNameCandidate || stationNaturalId || stationId)) {
          stationName = stationNameCandidate || stationNaturalId || stationId;
        }

        if (!systemId) {
          const candidateSystemId = entryObj?.SystemId || entryObj?.SystemNaturalId;
          const resolvedSystem = resolveSystemId(candidateSystemId);
          if (resolvedSystem) {
            systemId = resolvedSystem;
          }
        }

        if (!systemNaturalId && entryObj?.SystemNaturalId) {
          systemNaturalId = entryObj.SystemNaturalId;
        }

        if (!systemName && systemId) {
          systemName = systemNames?.[systemId] || systemNaturalId || systemName;
        }

        setDisplayCandidate('station', stationName || stationNaturalId || stationId, 1);
      };

      const resolvePlanetCandidate = (value, fallbackName) => {
        const raw = safeString(value);
        if (!raw) {
          return false;
        }

        const planetEntry = planetsById.get(String(raw).toLowerCase())
          || planetsByNaturalId.get(String(raw).toUpperCase());

        if (planetEntry) {
          applyPlanetEntry(planetEntry, raw, raw, fallbackName || planetEntry.PlanetName || raw);
          return true;
        }

        return false;
      };

      entries.forEach((entry) => {
        if (typeof entry === 'string') {
          const handledAsPlanet = resolvePlanetCandidate(entry);
          if (handledAsPlanet) {
            return;
          }

          const resolved = resolveSystemId(entry);
          if (resolved && !systemId) {
            systemId = resolved;
          }
          if (!systemName) {
            systemName = (resolved && systemNames?.[resolved]) || entry;
            if (systemName) {
              setDisplayCandidate('system', systemName, 2);
            }
          }
          return;
        }

        if (!entry || typeof entry !== 'object') {
          return;
        }

        const typeRaw = entry.Type || entry.type || entry.LineType || entry.lineType || entry.Category || entry.category || null;
        const type = typeof typeRaw === 'string' ? typeRaw.toLowerCase() : '';
        const lineId = entry.LineId || entry.Id || entry.Identifier || entry.Line || entry.Value;
        const lineNaturalId = entry.LineNaturalId || entry.NaturalId || entry.Code;
        const lineName = entry.LineName || entry.Name || entry.DisplayName;
        const baseName = lineName
          || (lineNaturalId ? String(lineNaturalId).toUpperCase() : null)
          || (lineId ? String(lineId) : null);

        if (type === 'station') {
          applyStationEntry(entry, lineId, lineNaturalId, lineName);
        }

        if (type === 'planet') {
          if (!planetId && lineId) {
            planetId = String(lineId);
          }
          if (!planetNaturalId && lineNaturalId) {
            planetNaturalId = String(lineNaturalId).toUpperCase();
          }
          if (!planetName && baseName) {
            planetName = baseName;
          }
        }

        const planetIdentifiers = [
          entry.PlanetId,
          entry.PlanetNaturalId,
          entry.Planet,
          entry.PlanetIdentifier,
          lineId,
          lineNaturalId
        ];

        const planetResolved = planetIdentifiers.some((identifier) => resolvePlanetCandidate(identifier, lineName));
        if (planetResolved) {
          if (!systemName && systemId) {
            systemName = systemNames?.[systemId] || systemNaturalId || systemName;
          }
        } else if (type === 'planet') {
          setDisplayCandidate('planet', planetName || baseName || planetNaturalId || planetId, 0);
        }

        if (!systemNaturalId && entry.SystemNaturalId) {
          systemNaturalId = entry.SystemNaturalId;
        }

        if (!systemId) {
          const candidates = [
            entry.SystemId,
            entry.SystemNaturalId,
            entry.OriginSystemId,
            entry.DestinationSystemId,
            entry.FromSystemId,
            entry.ToSystemId,
            lineId,
            lineNaturalId,
            entry.Id,
            entry.Line,
            entry.Address,
            entry.From,
            entry.To
          ];

          for (const candidate of candidates) {
            const resolved = resolveSystemId(candidate);
            if (resolved) {
              systemId = resolved;
              break;
            }
          }
        }

        if (!systemName) {
          const resolvedName = systemId ? systemNames?.[systemId] : null;
          const fallbackName = baseName || systemNaturalId || systemId;
          systemName = resolvedName || systemNaturalId || fallbackName || systemName;
        }

        if (systemName) {
          setDisplayCandidate('system', systemName, 2);
        }
      });

      if (!systemId) {
        const fallback = extractSystemId(lines);
        if (fallback) {
          systemId = fallback;
        }
      }

      if (!systemName && systemId) {
        systemName = systemNames?.[systemId] || systemNaturalId || systemId;
        if (systemName) {
          setDisplayCandidate('system', systemName, 2);
        }
      }

      if (displayLabel == null) {
        if (planetName || planetNaturalId || planetId) {
          displayKind = 'planet';
          displayPriority = 0;
          displayLabel = planetName || planetNaturalId || planetId;
        } else if (stationName || stationNaturalId || stationId) {
          displayKind = 'station';
          displayPriority = 1;
          displayLabel = stationName || stationNaturalId || stationId;
        } else if (systemName) {
          displayKind = 'system';
          displayPriority = 2;
          displayLabel = systemName;
        }
      }

      return {
        systemId,
        systemName,
        systemNaturalId,
        planetId,
        planetNaturalId,
        planetName,
        stationId,
        stationNaturalId,
        stationName,
        displayName: displayLabel || planetName || stationName || systemName || systemNaturalId || systemId || null,
        displayKind,
        displayPriority: Number.isFinite(displayPriority) ? displayPriority : null
      };
    };

    const getCandidateLabel = (candidate) => {
      if (!candidate) {
        return '';
      }
      const label = candidate.displayName
        || candidate.stationName
        || candidate.planetName
        || candidate.systemName
        || candidate.systemNaturalId
        || candidate.stationNaturalId
        || candidate.planetNaturalId
        || candidate.systemId
        || candidate.stationId
        || candidate.planetId
        || '';
      return typeof label === 'string' ? label.trim() : '';
    };

    const isGenericLabel = (candidate) => {
      const label = getCandidateLabel(candidate).toLowerCase();
      if (!label) {
        return true;
      }
      return label === 'station' || label === 'system' || label === 'planet';
    };

    const hasExplicitIdentifier = (candidate) => Boolean(
      candidate?.stationNaturalId
      || candidate?.stationId
      || candidate?.planetNaturalId
      || candidate?.planetId
    );

    const selectDisplayLocation = (...candidates) => {
      let best = null;
      let bestPriority = Number.POSITIVE_INFINITY;

      candidates.forEach((candidate) => {
        if (!candidate) {
          return;
        }
        const derivedPriority = candidate.displayPriority
          ?? ((candidate.planetId || candidate.planetNaturalId)
            ? 0
            : (candidate.stationId || candidate.stationNaturalId ? 1 : (candidate.systemId ? 2 : 5)));

        if (!best || derivedPriority < bestPriority) {
          best = candidate;
          bestPriority = derivedPriority;
          return;
        }

        if (derivedPriority !== bestPriority) {
          return;
        }

        const bestIsGeneric = isGenericLabel(best);
        const candidateIsGeneric = isGenericLabel(candidate);
        const bestLabel = getCandidateLabel(best);
        const candidateLabel = getCandidateLabel(candidate);
        const candidateHasId = hasExplicitIdentifier(candidate);
        const bestHasId = hasExplicitIdentifier(best);

        if (candidateHasId && !bestHasId) {
          best = candidate;
          return;
        }

        if (!candidateIsGeneric && bestIsGeneric) {
          best = candidate;
          return;
        }

        if (candidateLabel && !bestLabel) {
          best = candidate;
          return;
        }

        if (candidateLabel && bestLabel && candidateLabel.length > bestLabel.length + 2) {
          best = candidate;
        }
      });

      return best;
    };

    const buildSystemOnlyLocation = (systemId) => {
      const resolvedId = resolveSystemId(systemId);
      if (!resolvedId) {
        return null;
      }
      const systemLabel = systemNames?.[resolvedId] || resolvedId;
      return {
        systemId: resolvedId,
        systemName: systemLabel,
        systemNaturalId: resolvedId,
        displayName: systemLabel,
        displayKind: 'system',
        displayPriority: 2
      };
    };

    const deriveLocationFromLabel = (labelValue) => {
      if (typeof labelValue !== 'string') {
        return null;
      }

      const segments = labelValue.split(' - ').map((part) => part.trim()).filter(Boolean);
      if (segments.length === 0) {
        return null;
      }

      const parseSegment = (segment) => {
        if (!segment) return null;
        const match = segment.match(/^(.*)\s+\(([A-Za-z0-9-]+)\)$/);
        if (match) {
          return { name: match[1].trim(), naturalId: match[2].trim() };
        }
        return { name: segment.trim(), naturalId: null };
      };

      const entries = [];
      const systemInfo = parseSegment(segments[0]);
      if (systemInfo?.name) {
        entries.push({
          Type: 'system',
          LineName: systemInfo.name,
          LineNaturalId: systemInfo.naturalId || null,
          Name: systemInfo.name,
          NaturalId: systemInfo.naturalId || null
        });
      }

      for (let i = 1; i < segments.length; i += 1) {
        const info = parseSegment(segments[i]);
        if (!info?.name) {
          continue;
        }
        const lowered = info.name.toLowerCase();

        if (lowered.includes('station')) {
          entries.push({
            Type: 'station',
            LineName: info.name,
            LineNaturalId: info.naturalId || null,
            StationName: info.name,
            StationNaturalId: info.naturalId || null
          });
          continue;
        }

        if (lowered.includes('orbit')) {
          continue;
        }

        entries.push({
          Type: 'planet',
          LineName: info.name,
          LineNaturalId: info.naturalId || null,
          PlanetName: info.name,
          PlanetNaturalId: info.naturalId || null
        });
      }

      if (entries.length === 0) {
        return null;
      }

      const derived = extractLocationDetails(entries);
      if (!derived) {
        return null;
      }

      if (!derived.systemName && systemInfo?.name) {
        derived.systemName = systemInfo.name;
      }
      if (!derived.systemNaturalId && systemInfo?.naturalId) {
        derived.systemNaturalId = systemInfo.naturalId;
      }

      return derived;
    };

    const getShipLocationDetails = (ship, fallbackSystemId = null) => {
      if (!ship) {
        return buildSystemOnlyLocation(fallbackSystemId);
      }

      const candidates = [
        extractLocationDetails(ship.AddressLines),
        extractLocationDetails(ship.LocationLines),
        extractLocationDetails(ship.CurrentLocationLines),
        extractLocationDetails(ship.LastLocationLines),
        extractLocationDetails(ship.HomeLocationLines),
        extractLocationDetails(ship.DockingLines),
        extractLocationDetails(ship.DockedAt),
        extractLocationDetails(ship.LastLocation),
        extractLocationDetails(ship.HomeLocation),
        deriveLocationFromLabel(ship.Location),
        deriveLocationFromLabel(ship.LastLocation),
        deriveLocationFromLabel(ship.HomeLocation),
        buildSystemOnlyLocation(fallbackSystemId)
      ].filter(Boolean);

      if (candidates.length === 0) {
        return buildSystemOnlyLocation(fallbackSystemId);
      }

      return selectDisplayLocation(...candidates);
    };

    const formatLocationDisplay = (location) => {
      if (!location) return 'Unknown';

      const label = location.displayName
        || location.planetName
        || location.stationName
        || location.systemName
        || null;

      if (location.displayKind === 'station' || location.stationName || location.stationNaturalId) {
        const identifier = location.stationNaturalId || location.stationId;
        if (label && identifier && label !== identifier) {
          return `${label} (${identifier})`;
        }
        return label || identifier || 'Unknown';
      }

      const planetIdentifier = location.planetNaturalId || location.planetId;
      if (planetIdentifier) {
        const name = label || location.planetName;
        if (name && name !== planetIdentifier) {
          return `${name} (${planetIdentifier})`;
        }
        return planetIdentifier;
      }

      if (label) {
        return label;
      }

      return location.systemName
        || location.systemNaturalId
        || location.systemId
        || 'Unknown';
    };

    const getSystemCenter = (systemId) => {
      const id = safeString(systemId);
      if (!id) return null;
      try {
        const rect = g.select(`rect[id="${id}"]`);
        if (!rect.empty()) {
          const x = parseFloat(rect.attr('x'));
          const y = parseFloat(rect.attr('y'));
          const w = parseFloat(rect.attr('width')) || 0;
          const h = parseFloat(rect.attr('height')) || 0;
          return { x: x + w / 2, y: y + h / 2 };
        }

        const circle = g.select(`circle[id="${id}"]`);
        if (!circle.empty()) {
          const cx = parseFloat(circle.attr('cx'));
          const cy = parseFloat(circle.attr('cy'));
          if (!Number.isNaN(cx) && !Number.isNaN(cy)) {
            return { x: cx, y: cy };
          }
        }

        const node = g.select(`[id="${id}"]`);
        if (!node.empty() && typeof node.node === 'function') {
          const element = node.node();
          if (element?.getBBox) {
            const bbox = element.getBBox();
            if (bbox) {
              return {
                x: bbox.x + bbox.width / 2,
                y: bbox.y + bbox.height / 2
              };
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to compute system center', systemId, err);
      }

      const graphNode = graph?.systems?.[id];
      if (graphNode && Number.isFinite(graphNode.x) && Number.isFinite(graphNode.y)) {
        return { x: graphNode.x, y: graphNode.y };
      }

      return null;
    };

    const clamp01 = (val) => Math.max(0, Math.min(1, val));

    const formatEpoch = (epoch) => {
      if (epoch == null) return 'Unknown';
      const value = Number(epoch);
      if (Number.isNaN(value) || value <= 0) return 'Unknown';
      return new Date(value).toLocaleString();
    };

    const formatDuration = (ms) => {
      if (typeof ms !== 'number' || Number.isNaN(ms) || !Number.isFinite(ms)) return '—';
      if (ms <= 0) return '0m';
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
      if (hours === 0 && minutes < 5 && seconds > 0) parts.push(`${seconds}s`);
      if (parts.length === 0) return `${seconds}s`;
      return parts.join(' ');
    };

    const getOffsetSlotForSegment = (key) => {
      const usage = segmentUsageMap.get(key) || { count: 0 };
      const slot = usage.count;
      usage.count += 1;
      segmentUsageMap.set(key, usage);
      return slot;
    };

    const computeOffsetIndex = (slot) => {
      if (slot === 0) return 0;
      const magnitude = Math.floor((slot + 1) / 2);
      return slot % 2 === 1 ? magnitude : -magnitude;
    };

    const getSegmentTimeBounds = (segment) => {
      if (!segment) {
        return { departure: null, arrival: null, duration: null };
      }
      const departure = segment.SegmentDepartureEpochMs ?? segment.DepartureEpochMs ?? segment.SegmentDepartureTimeEpochMs ?? segment.DepartureTimeEpochMs ?? null;
      const arrival = segment.SegmentArrivalEpochMs ?? segment.ArrivalEpochMs ?? segment.SegmentArrivalTimeEpochMs ?? segment.ArrivalTimeEpochMs ?? null;
      const duration = segment.DurationMs ?? segment.SegmentDurationMs ?? (arrival && departure ? arrival - departure : null);
      return {
        departure: typeof departure === 'number' ? departure : Number(departure) || null,
        arrival: typeof arrival === 'number' ? arrival : Number(arrival) || null,
        duration: typeof duration === 'number' ? duration : Number(duration) || null
      };
    };

    const interpolateShipPosition = (ship, segments, flightContext) => {
      if (!ship || !segments?.length) return null;

      const shipKey = ship.ShipId || ship.Id || ship.Ship;
      const activeFlight = flightContext || (shipKey ? flightsByShipId.get(shipKey) : null);

      const segmentIndexCandidates = [
        activeFlight?.CurrentSegmentIndex,
        ship?.CurrentSegmentIndex,
        ship?.SegmentIndex,
        ship?.CurrentSegment
      ].filter((value) => typeof value === 'number' && !Number.isNaN(value));

      const selectedIndex = segmentIndexCandidates.length > 0
        ? Math.max(0, Math.min(segments.length - 1, segmentIndexCandidates[0]))
        : segments.length - 1;

      const segmentInfo = segments[selectedIndex];
      if (!segmentInfo) return segments[segments.length - 1]?.toCenter || null;

      const { fromCenter, toCenter, segment, timeBounds } = segmentInfo;
      if (!fromCenter || !toCenter) {
        return toCenter || fromCenter || null;
      }

      const bounds = timeBounds || getSegmentTimeBounds(segment);
      const { departure, arrival, duration } = bounds;
      let progress = null;

      if (departure && arrival && arrival > departure) {
        progress = clamp01((Date.now() - departure) / (arrival - departure));
      } else if (typeof duration === 'number' && duration > 0 && departure) {
        progress = clamp01((Date.now() - departure) / duration);
      }

      if (progress == null) {
        const progressCandidates = [activeFlight?.Progress, ship?.Progress, ship?.SegmentProgress, ship?.Completion];
        const validProgress = progressCandidates.find((value) => value != null && !Number.isNaN(Number(value)));
        if (validProgress != null) {
          progress = clamp01(Number(validProgress));
        }
      }

      if (progress == null) {
        progress = selectedIndex >= segments.length - 1 ? 1 : 0;
      }

      const dx = toCenter.x - fromCenter.x;
      const dy = toCenter.y - fromCenter.y;
      const length = Math.hypot(dx, dy) || 1;
      const direction = { x: dx / length, y: dy / length };

      return {
        x: fromCenter.x + dx * progress,
        y: fromCenter.y + dy * progress,
        direction,
        fromId: segmentInfo.fromId,
        toId: segmentInfo.toId,
        fromLocation: segmentInfo.fromLocation,
        toLocation: segmentInfo.toLocation,
        progress,
        segmentInfo,
        segmentIndex: selectedIndex,
        totalSegments: segments.length,
        timeBounds: { departure, arrival, duration }
      };
    };

    // Collect destination information for markers
    const destinations = new Map(); // Map<systemId, {arrivals: Array<{shipName, eta, owner}>, isStation: boolean}>

    if (graph && (combinedShips?.length > 0 || combinedFlights?.length > 0)) {
      (combinedFlights || []).forEach((flight) => {
        const flightShipId = flight?.ShipId ?? flight?.shipId ?? flight?.Ship ?? null;
        const flightShipIdStr = flightShipId != null ? String(flightShipId) : null;

        if (selectedShipId !== '__all__' && flightShipIdStr !== selectedShipId) {
          return;
        }
        const ship = shipsById.get(flight.ShipId) || null;
        const shipStyle = classifyShipType(ship, flightShipIdStr);
        const pathColor = shipStyle.color;
        const shipTypeLabel = shipStyle.label;
        const loadInfo = shipStyle.loadInfo;
        const shipIdentifierForFilter = getPrimaryShipId(ship) ?? flightShipIdStr;
        const normalizedShipKey = normalizeLookupKey(shipIdentifierForFilter);
        const filteredShipments = partnerFilterActive
          ? ((normalizedShipKey && partnerFilteredShipments?.get(normalizedShipKey))
            || filterShipmentsByPartner(loadInfo?.shipments))
          : (Array.isArray(loadInfo?.shipments) ? loadInfo.shipments : []);

        if (partnerFilterActive && filteredShipments.length === 0) {
          return;
        }

        const loadInfoForTiles = loadInfo
          ? { ...loadInfo, shipments: filteredShipments }
          : { shipments: filteredShipments };
        const originLabelLocation = deriveLocationFromLabel(flight.Origin);
        const destinationLabelLocation = deriveLocationFromLabel(flight.Destination);
        const segmentsRaw = Array.isArray(flight.Segments) ? [...flight.Segments] : [];
        segmentsRaw.sort((a, b) => {
          const aStart = Number.isFinite(a?.DepartureTimeEpochMs)
            ? Number(a.DepartureTimeEpochMs)
            : (Number.isFinite(a?.ArrivalTimeEpochMs) ? Number(a.ArrivalTimeEpochMs) : 0);
          const bStart = Number.isFinite(b?.DepartureTimeEpochMs)
            ? Number(b.DepartureTimeEpochMs)
            : (Number.isFinite(b?.ArrivalTimeEpochMs) ? Number(b.ArrivalTimeEpochMs) : 0);
          return aStart - bStart;
        });
        const segmentPairs = [];
        let previousLocation = null;
        let firstLocation = null;
        let finalLocation = null;
        let isSTLOnly = true; // Track if this is an STL-only flight

        segmentsRaw.forEach((segment) => {
          const originLocation = extractLocationDetails(segment.OriginLines);
          const destinationLocation = extractLocationDetails(segment.DestinationLines);

          if (destinationLocation && (destinationLocation.systemId || destinationLocation.planetName || destinationLocation.planetNaturalId || destinationLocation.stationName || destinationLocation.stationNaturalId)) {
            finalLocation = destinationLocation;
          }

          const effectiveFrom = originLocation?.systemId
            ? originLocation
            : (previousLocation?.systemId ? previousLocation : originLocation);

          const effectiveTo = [destinationLocation, originLocation, previousLocation]
            .find((loc) => loc && loc.systemId) || null;

          if (!firstLocation && effectiveFrom?.systemId) {
            firstLocation = effectiveFrom;
          }

          if (!effectiveFrom?.systemId || !effectiveTo?.systemId || effectiveFrom.systemId === effectiveTo.systemId) {
            previousLocation = destinationLocation?.systemId ? destinationLocation : effectiveTo || previousLocation;
            return;
          }

          // If we reach here, there's at least one inter-system segment
          isSTLOnly = false;

          const fromCenter = getSystemCenter(effectiveFrom.systemId);
          const toCenter = getSystemCenter(effectiveTo.systemId);
          if (!fromCenter || !toCenter) {
            previousLocation = destinationLocation?.systemId ? destinationLocation : effectiveTo || previousLocation;
            return;
          }

          const timeBounds = getSegmentTimeBounds(segment);

          segmentPairs.push({
            fromId: effectiveFrom.systemId,
            toId: effectiveTo.systemId,
            fromCenter,
            toCenter,
            segment,
            index: segmentPairs.length,
            timeBounds,
            fromLocation: effectiveFrom,
            toLocation: effectiveTo
          });

          previousLocation = destinationLocation?.systemId ? destinationLocation : effectiveTo;
        });

        if (!firstLocation && segmentPairs.length > 0) {
          firstLocation = segmentPairs[0].fromLocation;
        }
        firstLocation = selectDisplayLocation(firstLocation, originLabelLocation);
        if (!finalLocation && segmentPairs.length > 0) {
          finalLocation = segmentPairs[segmentPairs.length - 1].toLocation;
        }
        if (!finalLocation && previousLocation?.systemId) {
          finalLocation = previousLocation;
        }
        finalLocation = selectDisplayLocation(finalLocation, destinationLabelLocation);

        segmentPairs.meta = {
          firstLocation,
          finalLocation
        };

        if (segmentPairs.length === 0) {
          // Check if this is an STL-only flight - don't skip it, let it show as idle with STL status
          if (isSTLOnly && segmentsRaw.length > 0 && (firstLocation?.systemId || finalLocation?.systemId)) {
            flightDiagnostics.push({
              shipId: flightShipIdStr,
              shipName: ship?.Name || ship?.ShipName || flightShipIdStr || 'Unknown',
              flightId: flight?.FlightId ?? null,
              state: 'stl-only',
              systemId: firstLocation?.systemId || finalLocation?.systemId,
              segments: segmentsRaw.length
            });
            // Don't add to activeShipIds - let it render as "idle" with STL info
          } else {
            flightDiagnostics.push({
              shipId: flightShipIdStr,
              shipName: ship?.Name || ship?.ShipName || flightShipIdStr || 'Unknown',
              flightId: flight?.FlightId ?? null,
              state: 'skipped-no-segments'
            });
          }
          return;
        }

        const flightTiming = computeFlightTiming(flight, segmentPairs);

        // Collect destination information for markers
        if (finalLocation?.systemId && (finalLocation?.planetNaturalId || finalLocation?.stationNaturalId || finalLocation?.AddressableId || finalLocation?.addressableId)) {
          // Use planet or station identifier
          const locationId = finalLocation.planetNaturalId || finalLocation.stationNaturalId || finalLocation.AddressableId || finalLocation.addressableId || 'unknown';
          const planetKey = `${finalLocation.systemId}-${locationId}`;
          if (!destinations.has(planetKey)) {
            // Check if this is a station based on the location properties
            const isStation = !!(finalLocation.stationId || finalLocation.stationNaturalId || finalLocation.stationName ||
              finalLocation.AddressableId || finalLocation.addressableId);

            destinations.set(planetKey, {
              systemId: finalLocation.systemId,
              planetNaturalId: finalLocation.planetNaturalId || locationId,
              planetName: finalLocation.planetName,
              stationNaturalId: finalLocation.stationNaturalId,
              stationName: finalLocation.stationName,
              arrivals: [],
              isStation: isStation
            });
          }

          const shipName = ship?.Name || ship?.ShipName || flightShipIdStr || 'Unknown Ship';
          const eta = flightTiming.arrival ? new Date(flightTiming.arrival) : null;
          const owner = ship?._user || 'Unknown';

          destinations.get(planetKey).arrivals.push({
            shipName,
            eta,
            owner,
            flightId: flight?.FlightId || flightShipIdStr
          });
        }

        const segmentIndexCandidatesRaw = [
          flight?.CurrentSegmentIndex,
          flight?.SegmentIndex,
          ship?.CurrentSegmentIndex,
          ship?.SegmentIndex,
          ship?.CurrentSegment
        ];
        const hasSegmentIndexData = segmentIndexCandidatesRaw.some((value) => (
          typeof value === 'number' && !Number.isNaN(value)
        ));

        const progressCandidates = [
          flight?.Progress,
          flight?.Completion,
          flight?.SegmentProgress,
          ship?.Progress,
          ship?.SegmentProgress,
          ship?.Completion
        ];
        const rawProgress = progressCandidates.find((value) => value != null && !Number.isNaN(Number(value)));
        const numericProgress = rawProgress != null ? Number(rawProgress) : null;
        const hasProgressData = rawProgress != null;
        const hasTraversalData = hasSegmentIndexData || hasProgressData;

        // Flight data only includes active flights, so we treat all flights with segments as active
        flightDiagnostics.push({
          shipId: flightShipIdStr,
          shipName: ship?.Name || ship?.ShipName || flightShipIdStr || 'Unknown',
          flightId: flight?.FlightId ?? null,
          state: 'active',
          progress: numericProgress,
          segments: segmentPairs.length
        });

        if (flightShipIdStr) {
          activeShipIds.add(flightShipIdStr);
        }

        const firstSegmentInfo = segmentPairs[0];
        const finalSegmentInfo = segmentPairs[segmentPairs.length - 1];
        const firstLocationMeta = selectDisplayLocation(
          segmentPairs.meta?.firstLocation,
          firstSegmentInfo?.fromLocation,
          originLabelLocation
        );
        const finalLocationMeta = selectDisplayLocation(
          segmentPairs.meta?.finalLocation,
          finalSegmentInfo?.toLocation,
          destinationLabelLocation
        );

        const interpolatedPosition = ship ? interpolateShipPosition(ship, segmentPairs, flight) : null;
        const activeSegmentIndex = (hasTraversalData && Number.isInteger(interpolatedPosition?.segmentIndex))
          ? interpolatedPosition.segmentIndex
          : null;
        const activeSegmentProgress = (hasTraversalData && typeof interpolatedPosition?.progress === 'number')
          ? clamp01(interpolatedPosition.progress)
          : null;

        if (flight.FlightId) {
          flightSegmentsCache.set(flight.FlightId, segmentPairs);
        }
        if (flight.ShipId) {
          flightSegmentsCache.set(flight.ShipId, segmentPairs);
        }
        if (flightShipIdStr) {
          flightSegmentsCache.set(flightShipIdStr, segmentPairs);
        }

        segmentPairs.forEach((segmentInfo, segmentIndex) => {
          if (hasTraversalData && activeSegmentIndex != null && segmentIndex < activeSegmentIndex) {
            return;
          }

          const segmentKey = [segmentInfo.fromId, segmentInfo.toId].sort().join('__');
          const slot = getOffsetSlotForSegment(segmentKey);
          const offsetIndex = computeOffsetIndex(slot);
          const baseOffset = Math.max(3 / Math.max(1, zoomLevel), 1.5);

          const dx = segmentInfo.toCenter.x - segmentInfo.fromCenter.x;
          const dy = segmentInfo.toCenter.y - segmentInfo.fromCenter.y;
          const length = Math.hypot(dx, dy) || 1;
          const perpX = -dy / length;
          const perpY = dx / length;
          const offsetAmount = offsetIndex * baseOffset;

          const applyOffset = (point) => ({
            x: point.x + perpX * offsetAmount,
            y: point.y + perpY * offsetAmount
          });

          const offsetFrom = offsetAmount === 0 ? segmentInfo.fromCenter : applyOffset(segmentInfo.fromCenter);
          const offsetTo = offsetAmount === 0 ? segmentInfo.toCenter : applyOffset(segmentInfo.toCenter);

          let pathStart = offsetFrom;

          if (hasTraversalData && activeSegmentIndex != null && segmentIndex === activeSegmentIndex) {
            const progress = activeSegmentProgress != null ? activeSegmentProgress : 0;
            if (progress >= 0.999) {
              return;
            }

            const baseStart = {
              x: segmentInfo.fromCenter.x + (segmentInfo.toCenter.x - segmentInfo.fromCenter.x) * progress,
              y: segmentInfo.fromCenter.y + (segmentInfo.toCenter.y - segmentInfo.fromCenter.y) * progress
            };

            pathStart = offsetAmount === 0 ? baseStart : applyOffset(baseStart);
          }

          const startPoint = pathStart;
          const endPoint = offsetTo;
          const deltaX = endPoint.x - startPoint.x;
          const deltaY = endPoint.y - startPoint.y;
          if (Math.hypot(deltaX, deltaY) < 0.01) {
            return;
          }

          flightLayer.append('path')
            .attr('class', 'flight-path')
            .attr('d', lineGenerator([startPoint, endPoint]))
            .attr('fill', 'none')
            .attr('stroke', pathColor)
            .attr('stroke-width', Math.max(1.5 / zoomLevel, 1))
            .attr('stroke-opacity', 0.7)
            .attr('data-flight-id', flight.FlightId || flightShipIdStr || 'unknown')
            .attr('data-ship-id', flightShipIdStr || 'unknown')
            .style('cursor', 'pointer')
            .on('mouseover', function () {
              // Highlight entire route
              d3.selectAll(`[data-flight-id="${flight.FlightId || flightShipIdStr || 'unknown'}"]`)
                .attr('stroke-opacity', 1.0)
                .attr('stroke-width', Math.max(2.5 / zoomLevel, 1.5));
              // Highlight ship marker with black outline and 1.3x size
              d3.selectAll(`[data-ship-id="${flightShipIdStr || 'unknown'}"] .ship-group path`)
                .attr('stroke', '#000000')
                .attr('stroke-width', function () {
                  const currentWidth = d3.select(this).attr('stroke-width');
                  return parseFloat(currentWidth) * 1.3;
                });
              // Show ship info tooltip
              d3.selectAll(`[data-ship-id="${flightShipIdStr || 'unknown'}"]`)
                .dispatch('mouseover.shipinfo');
            })
            .on('mouseout', function () {
              // Reset highlighting
              d3.selectAll(`[data-flight-id="${flight.FlightId || flightShipIdStr || 'unknown'}"]`)
                .attr('stroke-opacity', 0.7)
                .attr('stroke-width', Math.max(1.5 / zoomLevel, 1));
              // Reset ship marker highlighting
              d3.selectAll(`[data-ship-id="${flightShipIdStr || 'unknown'}"] .ship-group path`)
                .attr('stroke', function () {
                  // Reset to original stroke color - white outline
                  return '#ffffff';
                })
                .attr('stroke-width', function () {
                  const currentWidth = d3.select(this).attr('stroke-width');
                  return parseFloat(currentWidth) / 1.3;
                });
              // Hide ship info tooltip
              d3.selectAll(`[data-ship-id="${flightShipIdStr || 'unknown'}"]`)
                .dispatch('mouseout.shipinfo');
            })
            .on('click', function (event) {
              event.stopPropagation();

              // Show detailed route info
              const flightId = d3.select(this).attr('data-flight-id');
              const shipId = d3.select(this).attr('data-ship-id');

              // Find the flight data
              const flightData = combinedFlights.find(f =>
                (f.FlightId || f.shipId || f.ShipId) === flightId ||
                (f.FlightId || f.shipId || f.ShipId) === shipId
              );

              if (flightData) {
                console.log('Route details:', {
                  flight: flightData,
                  segments: flightData.Segments || [],
                  ship: shipsById.get(flightData.ShipId)
                });

                // TODO: Show detailed route modal/tooltip with full path information
                // For now, just log the route details
              }
            });

          // Add animated flow particles
          const pathLength = Math.hypot(deltaX, deltaY);
          if (pathLength > 50) { // Only add particles to longer paths
            const particleCount = Math.min(Math.max(Math.floor(pathLength / 100), 1), 3);
            for (let i = 0; i < particleCount; i++) {
              const delay = (i / particleCount) * 4000; // Stagger particles
              const particle = flightLayer.append('circle')
                .attr('class', 'flight-particle')
                .attr('r', Math.max(1.5 / zoomLevel, 0.8))
                .attr('fill', pathColor)
                .attr('opacity', 0.8)
                .attr('data-flight-id', flight.FlightId || flightShipIdStr || 'unknown');

              // Animate particle along path continuously
              const restartAnimation = function () {
                d3.select(this)
                  .attr('cx', startPoint.x)
                  .attr('cy', startPoint.y)
                  .transition()
                  .duration(4000)
                  .ease(d3.easeLinear)
                  .attr('cx', endPoint.x)
                  .attr('cy', endPoint.y)
                  .on('end', restartAnimation); // Loop continuously
              };

              particle
                .attr('cx', startPoint.x)
                .attr('cy', startPoint.y)
                .transition()
                .duration(4000)
                .delay(delay)
                .ease(d3.easeLinear)
                .attr('cx', endPoint.x)
                .attr('cy', endPoint.y)
                .on('end', restartAnimation);
            }
          }
        });

        // Render ship marker at interpolated position if this flight is current ship
        if (ship) {
          const segmentsForShip = flightSegmentsCache.get(flight.FlightId)
            || flightSegmentsCache.get(flight.ShipId)
            || flightSegmentsCache.get(flightShipIdStr);
          const interpolated = interpolatedPosition
            || interpolateShipPosition(ship, segmentsForShip || segmentPairs, flight);
          if (interpolated) {
            const effectiveZoom = Math.max(1, zoomLevel);
            const radius = Math.max(6 / effectiveZoom, 3);
            const positionKey = `${Math.round(interpolated.x / 5)}:${Math.round(interpolated.y / 5)}`;
            const markerSlot = getMarkerSlot(positionKey);
            const baseSpacing = Math.max(radius * 2.1, 6 / effectiveZoom);

            const identifierForLoad = getPrimaryShipId(ship) ?? flightShipIdStr;
            const shipIdStr = identifierForLoad != null ? String(identifierForLoad) : (flightShipIdStr || 'unknown');

            let offsetX = 0;
            let offsetY = 0;
            if (markerSlot > 0) {
              if (interpolated.direction && Number.isFinite(interpolated.direction.x) && Number.isFinite(interpolated.direction.y)) {
                const perpX = -interpolated.direction.y;
                const perpY = interpolated.direction.x;
                const magnitude = Math.floor((markerSlot + 1) / 2);
                const sign = markerSlot % 2 === 1 ? 1 : -1;
                offsetX = perpX * baseSpacing * magnitude * sign;
                offsetY = perpY * baseSpacing * magnitude * sign;
              } else {
                const angle = (markerSlot - 1) * (Math.PI / 3);
                offsetX = Math.cos(angle) * baseSpacing;
                offsetY = Math.sin(angle) * baseSpacing;
              }
            }

            const markerX = interpolated.x + offsetX;
            const markerY = interpolated.y + offsetY;

            const markerGroup = shipLayer.append('g')
              .attr('class', 'ship-group')
              .attr('data-ship-id', shipIdStr);

            markerGroup.raise();

            // Create thick chevron arrow shape
            const chevronSize = Math.max(9 / effectiveZoom, 4.5);
            const chevronWidth = chevronSize * 0.8;

            // Default direction is right if no direction provided
            const dirX = interpolated.direction?.x || 1;
            const dirY = interpolated.direction?.y || 0;

            // Calculate angle of direction
            const angle = Math.atan2(dirY, dirX) * (180 / Math.PI);

            // Create chevron shape pointing right (will be rotated)
            // Chevron consists of two lines forming a ">" shape
            const halfHeight = chevronWidth / 2;
            const chevronPath = `
              M ${-chevronSize * 0.3} ${-halfHeight}
              L ${chevronSize * 0.5} 0
              L ${-chevronSize * 0.3} ${halfHeight}
            `;

            // Add white outline for visibility
            markerGroup.append('path')
              .attr('d', chevronPath)
              .attr('transform', `translate(${markerX}, ${markerY}) rotate(${angle})`)
              .attr('fill', 'none')
              .attr('stroke', '#ffffff')
              .attr('stroke-width', Math.max(5 / effectiveZoom, 3.5))
              .attr('stroke-linecap', 'round')
              .attr('stroke-linejoin', 'round')
              .attr('opacity', 0.8);

            // Main thick chevron
            markerGroup.append('path')
              .attr('d', chevronPath)
              .attr('transform', `translate(${markerX}, ${markerY}) rotate(${angle})`)
              .attr('fill', 'none')
              .attr('stroke', pathColor)
              .attr('stroke-width', Math.max(3.5 / effectiveZoom, 2))
              .attr('stroke-linecap', 'round')
              .attr('stroke-linejoin', 'round')
              .attr('opacity', 0.95);

            // Add shipment count badge if there are shipments
            const shipmentCount = filteredShipments.length;
            if (shipmentCount > 0) {
              const badgeRadius = Math.max(7 / effectiveZoom, 4.2);
              const badgeOffsetX = chevronSize * 1.2;
              const badgeOffsetY = -chevronSize * 1.2;
              const badgeFontSize = Math.max(8.4 / effectiveZoom, 5.6);

              // Badge background circle
              markerGroup.append('circle')
                .attr('cx', markerX + badgeOffsetX)
                .attr('cy', markerY + badgeOffsetY)
                .attr('r', badgeRadius)
                .attr('fill', '#ef4444')
                .attr('stroke', '#ffffff')
                .attr('stroke-width', Math.max(1.12 / effectiveZoom, 0.7))
                .attr('opacity', 0.95);

              // Badge count text
              markerGroup.append('text')
                .attr('x', markerX + badgeOffsetX)
                .attr('y', markerY + badgeOffsetY)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'central')
                .attr('fill', '#ffffff')
                .attr('font-size', `${badgeFontSize}px`)
                .attr('font-weight', 700)
                .attr('opacity', 1)
                .text(shipmentCount > 99 ? '99+' : shipmentCount);
            }

            const labelX = markerX + radius + 6;
            const labelY = markerY - (radius * 0.8);
            const baseFontSize = 8;
            const lineHeight = baseFontSize * 1.2;

            const shipDisplayName = ship.Name || ship.ShipName || ship.ShipId || 'Unknown';
            const preferredDestinationLocation = selectDisplayLocation(
              destinationLabelLocation,
              segmentPairs.meta?.finalLocation,
              finalLocationMeta
            );
            const destinationDisplay = formatLocationDisplay(preferredDestinationLocation);

            const statusLabel = (() => {
              if (interpolated.totalSegments > 0) {
                const onFirstSegment = interpolated.segmentIndex === 0;
                const onFinalSegment = interpolated.segmentIndex === interpolated.totalSegments - 1;
                const progress = clamp01(interpolated.progress ?? 0);
                const currentDestinationLocation = selectDisplayLocation(interpolated.toLocation, preferredDestinationLocation);
                const currentDestinationDisplay = formatLocationDisplay(currentDestinationLocation);
                const currentOriginLocation = selectDisplayLocation(interpolated.fromLocation, firstLocationMeta, originLabelLocation);
                const currentOriginDisplay = formatLocationDisplay(currentOriginLocation);

                if (onFinalSegment && progress >= 0.95) {
                  return `Arriving ${currentDestinationDisplay}`;
                }

                if (onFirstSegment && progress <= 0.05) {
                  return `Departing ${currentOriginDisplay}`;
                }

                return `In transit to ${destinationDisplay}`;
              }

              return `In transit to ${destinationDisplay}`;
            })();

            const finalArrivalEpoch = flightTiming.arrival ?? finalSegmentInfo?.timeBounds?.arrival ?? null;
            const nowTs = Date.now();
            const remainingMs = finalArrivalEpoch != null ? Math.max(0, finalArrivalEpoch - nowTs) : null;
            const timeRemainingText = remainingMs != null ? formatDuration(remainingMs) : 'Unknown';
            const etaText = finalArrivalEpoch != null ? formatEpoch(finalArrivalEpoch) : 'Unknown';

            const loadSummary = buildLoadSummary(loadInfo);
            const shipmentTiles = buildShipmentTiles(loadInfoForTiles);
            const loadBarDescriptors = buildLoadBarDescriptors(loadInfo);

            const label = markerGroup.append('text')
              .attr('x', labelX)
              .attr('y', labelY)
              .attr('fill', '#ffffff')
              .attr('font-size', `${baseFontSize}px`)
              .attr('font-weight', 600)
              .attr('stroke', '#000000')
              .attr('stroke-width', 1 / Math.max(1, effectiveZoom * 2))
              .attr('paint-order', 'stroke')
              .attr('text-anchor', 'start')
              .attr('dominant-baseline', 'hanging')
              .attr('opacity', 0.95);

            if (!labelsEnabled) {
              label.style('display', 'none');
            }

            const labelPaddingX = 6;
            const labelPaddingY = 3;
            const labelCornerRadius = 4;
            const labelBackground = markerGroup.insert('rect', 'text')
              .attr('class', 'ship-label-background')
              .attr('fill', 'rgba(15, 23, 42, 0.85)')
              .attr('stroke', '#000000')
              .attr('stroke-width', 0.5)
              .attr('rx', labelCornerRadius)
              .attr('ry', labelCornerRadius)
              .attr('opacity', 0.95)
              .style('pointer-events', 'none')
              .style('display', 'none');

            let loadBarsContainer = null;
            let loadBarEntries = [];

            if (loadBarDescriptors.length > 0) {
              loadBarsContainer = markerGroup.append('g')
                .attr('class', 'ship-load-bars')
                .style('pointer-events', 'none')
                .style('display', 'none');

              loadBarEntries = loadBarDescriptors.map((descriptor) => {
                const barGroup = loadBarsContainer.append('g')
                  .attr('class', `ship-load-bar ship-load-bar-${descriptor.key}`);
                const background = barGroup.append('rect')
                  .attr('class', 'ship-load-bar-bg');
                const fill = barGroup.append('rect')
                  .attr('class', 'ship-load-bar-fill');
                const labelNode = barGroup.append('text')
                  .attr('class', 'ship-load-bar-label')
                  .attr('text-anchor', 'middle')
                  .attr('dominant-baseline', 'hanging')
                  .attr('font-weight', 500)
                  .attr('fill', '#bfdbfe');

                return {
                  descriptor,
                  group: barGroup,
                  background,
                  fill,
                  label: labelNode
                };
              });
            }

            let shipmentTilesContainer = null;
            let shipmentTileEntries = [];

            if (shipmentTiles.length > 0) {
              shipmentTilesContainer = markerGroup.append('g')
                .attr('class', 'ship-shipment-tiles')
                .style('pointer-events', 'none')
                .style('display', 'none');

              shipmentTileEntries = shipmentTiles.map((tile) => {
                const tileGroup = shipmentTilesContainer.append('g')
                  .attr('class', 'ship-shipment-tile');

                const background = tileGroup.append('rect')
                  .attr('class', 'ship-shipment-tile-bg');

                const textNode = tileGroup.append('text')
                  .attr('class', 'ship-shipment-tile-text')
                  .attr('text-anchor', 'start')
                  .attr('dominant-baseline', 'hanging')
                  .attr('font-weight', 500);

                const lineTspans = tile.lines.map((line) => textNode.append('tspan').text(line));

                return {
                  tile,
                  group: tileGroup,
                  background,
                  text: textNode,
                  lineTspans
                };
              });
            }

            const updateInfoLayout = () => {
              const labelNode = label.node();
              if (!labelNode) return;
              const bbox = labelNode.getBBox();
              if (!bbox || (bbox.width === 0 && bbox.height === 0)) return;

              let minX = bbox.x;
              const minY = bbox.y;
              let maxX = bbox.x + bbox.width;
              let maxY = bbox.y + bbox.height;

              if (loadBarsContainer && loadBarEntries.length > 0) {
                const baseBarWidth = Math.max(bbox.width, 70);
                const barHeight = 6;
                const barRadius = barHeight / 2;
                const gap = 4;
                const barX = bbox.x;
                let currentY = bbox.y + bbox.height + gap;

                loadBarEntries.forEach((entry, index) => {
                  const ratioValueRaw = entry.descriptor.ratio != null ? entry.descriptor.ratio : 0;
                  const ratioValue = Math.max(0, Math.min(ratioValueRaw, 1.1));
                  const fillRatio = Math.min(ratioValue, 1);
                  const barWidth = baseBarWidth;

                  entry.group.attr('transform', `translate(${barX}, ${currentY})`);

                  entry.background
                    .attr('width', barWidth)
                    .attr('height', barHeight)
                    .attr('rx', barRadius)
                    .attr('ry', barRadius)
                    .attr('fill', 'rgba(15, 23, 42, 0.85)')
                    .attr('stroke', '#0f172a')
                    .attr('stroke-width', 0.5)
                    .attr('opacity', 0.95);

                  entry.fill
                    .attr('width', Math.max(barWidth * fillRatio, 2))
                    .attr('height', barHeight)
                    .attr('rx', barRadius)
                    .attr('ry', barRadius)
                    .attr('fill', getLoadColorForRatio(ratioValue) || '#38bdf8')
                    .attr('opacity', 0.95);

                  const labelFontSize = 9;
                  const labelGap = 4;
                  const labelText = entry.descriptor.summary
                    || `${entry.descriptor.kindLabel} ${(Math.min(ratioValue, 1) * 100).toFixed(1)}%`;

                  entry.label
                    .attr('x', barWidth / 2)
                    .attr('y', barHeight + labelGap)
                    .attr('font-size', `${labelFontSize}px`)
                    .text(labelText);

                  const barBottom = currentY + barHeight + labelGap + labelFontSize;

                  if (index === loadBarEntries.length - 1) {
                    maxY = Math.max(maxY, barBottom);
                  } else {
                    maxY = Math.max(maxY, barBottom + 2);
                  }

                  minX = Math.min(minX, barX);
                  maxX = Math.max(maxX, barX + barWidth);

                  currentY = barBottom + 2;
                });
              }

              if (shipmentTilesContainer && shipmentTileEntries.length > 0) {
                const tilePaddingX = 6;
                const tilePaddingY = 4;
                const tileGap = 4;
                const tileRadius = 4;
                const textFontSize = 9;
                const tileLineHeight = baseFontSize * 1.05;
                const tileX = bbox.x;
                let currentY = Math.max(maxY + tileGap, bbox.y + bbox.height + tileGap);

                shipmentTileEntries.forEach((entry) => {
                  entry.group.attr('transform', `translate(${tileX}, ${currentY})`);

                  entry.text
                    .attr('x', tilePaddingX)
                    .attr('y', tilePaddingY)
                    .attr('fill', '#e0f2fe')
                    .attr('font-size', `${textFontSize}px`);

                  entry.lineTspans.forEach((tspanNode, idx) => {
                    tspanNode
                      .attr('x', tilePaddingX)
                      .attr('dy', idx === 0 ? 0 : tileLineHeight)
                      .attr('fill', idx === 0 ? '#facc15' : (idx === 1 ? '#bae6fd' : '#cbd5f5'))
                      .attr('font-weight', idx === 0 ? 700 : (idx === 1 ? 600 : 500))
                      .text(entry.tile.lines[idx]);
                  });

                  const textNode = entry.text.node();
                  const textBBox = textNode && typeof textNode.getBBox === 'function'
                    ? textNode.getBBox()
                    : null;

                  const tileWidth = textBBox
                    ? Math.max(bbox.width, textBBox.width + tilePaddingX * 2)
                    : Math.max(bbox.width, 90);
                  const tileHeight = textBBox
                    ? textBBox.height + tilePaddingY * 2
                    : tilePaddingY * 2 + tileLineHeight * entry.tile.lines.length;

                  entry.background
                    .attr('x', 0)
                    .attr('y', 0)
                    .attr('width', tileWidth)
                    .attr('height', tileHeight)
                    .attr('rx', tileRadius)
                    .attr('ry', tileRadius)
                    .attr('fill', 'rgba(12, 25, 46, 0.92)')
                    .attr('stroke', '#3b82f6')
                    .attr('stroke-width', 0.75)
                    .attr('opacity', 0.95);

                  minX = Math.min(minX, tileX);
                  maxX = Math.max(maxX, tileX + tileWidth);
                  const tileBottom = currentY + tileHeight;
                  maxY = Math.max(maxY, tileBottom);

                  currentY = tileBottom + tileGap;
                });
              }

              labelBackground
                .attr('x', minX - labelPaddingX)
                .attr('y', minY - labelPaddingY)
                .attr('width', (maxX - minX) + (labelPaddingX * 2))
                .attr('height', (maxY - minY) + (labelPaddingY * 2));
            };

            label.append('tspan')
              .attr('x', labelX)
              .attr('dy', 0)
              .style('font-size', `${baseFontSize * 1.25}px`)
              .attr('font-weight', 700)
              .text(shipDisplayName);

            // Add owner label for group ships
            if (ship._user && groupShips && groupShips.length > 0) {
              label.append('tspan')
                .attr('x', labelX)
                .attr('dy', `${lineHeight * 0.9}px`)
                .attr('fill', '#60a5fa')
                .attr('font-weight', 600)
                .style('font-size', `${baseFontSize * 0.85}px`)
                .text(`Owner: ${ship._user}`);
            }

            const typeDescription = shipTypeLabel;

            const typeTspan = label.append('tspan')
              .attr('x', labelX)
              .attr('dy', `${lineHeight}px`)
              .attr('fill', '#c7d2fe')
              .attr('font-weight', 500)
              .style('font-size', `${baseFontSize * 1.05}px`)
              .style('display', 'none')
              .text(typeDescription);

            const statusTspan = label.append('tspan')
              .attr('x', labelX)
              .attr('dy', `${lineHeight}px`)
              .attr('fill', '#facc15')
              .attr('font-weight', 600)
              .style('font-size', `${baseFontSize * 1.05}px`)
              .style('display', 'none')
              .text(statusLabel);

            const loadSummaryTspan = loadSummary
              ? label.append('tspan')
                .attr('x', labelX)
                .attr('dy', `${lineHeight}px`)
                .attr('fill', '#bfdbfe')
                .attr('font-weight', 500)
                .style('font-size', `${baseFontSize}px`)
                .style('display', 'none')
                .text(loadSummary)
              : null;

            const remainingTspan = label.append('tspan')
              .attr('x', labelX)
              .attr('dy', `${lineHeight}px`)
              .attr('fill', '#d1d5db')
              .attr('font-weight', 400)
              .style('display', 'none')
              .text(`Time Remaining: ${timeRemainingText}`);

            const etaTspan = label.append('tspan')
              .attr('x', labelX)
              .attr('dy', `${lineHeight}px`)
              .attr('fill', '#d1d5db')
              .attr('font-weight', 400)
              .style('display', 'none')
              .text(`ETA: ${etaText}`);
            const infoSpans = [typeTspan, statusTspan, loadSummaryTspan, remainingTspan, etaTspan].filter(Boolean);

            const showInfo = () => {
              markerGroup.raise();
              label.style('display', null);
              infoSpans.forEach((span) => span.style('display', null));
              labelBackground.style('display', null);
              if (loadBarsContainer) {
                loadBarsContainer.style('display', null);
              }
              if (shipmentTilesContainer) {
                shipmentTilesContainer.style('display', null);
              }
              updateInfoLayout();
            };

            const hideInfo = () => {
              infoSpans.forEach((span) => span.style('display', 'none'));
              labelBackground.style('display', 'none');
              if (loadBarsContainer) {
                loadBarsContainer.style('display', 'none');
              }
              if (shipmentTilesContainer) {
                shipmentTilesContainer.style('display', 'none');
              }
              if (!labelsEnabled) {
                label.style('display', 'none');
              }
            };

            // Enable pointer events on tooltip elements to keep them visible when hovering
            if (labelBackground) {
              labelBackground.style('pointer-events', 'all');
            }
            if (loadBarsContainer) {
              loadBarsContainer.style('pointer-events', 'all');
            }
            if (shipmentTilesContainer) {
              shipmentTilesContainer.style('pointer-events', 'all');
            }

            const tooltipElements = [labelBackground, loadBarsContainer, shipmentTilesContainer].filter(Boolean);

            markerGroup
              .on('mouseover.shipinfo', () => {
                showInfo();
                // Highlight the entire flight path
                d3.selectAll(`[data-flight-id="${flight.FlightId || flightShipIdStr || 'unknown'}"]`)
                  .attr('stroke-opacity', 1.0)
                  .attr('stroke-width', Math.max(2.5 / zoomLevel, 1.5));
              })
              .on('mouseout.shipinfo', (event) => {
                const related = event.relatedTarget;
                const groupNode = markerGroup.node();
                if (related && groupNode && groupNode.contains(related)) {
                  return;
                }
                hideInfo();
                // Reset flight path highlighting
                d3.selectAll(`[data-flight-id="${flight.FlightId || flightShipIdStr || 'unknown'}"]`)
                  .attr('stroke-opacity', 0.7)
                  .attr('stroke-width', Math.max(1.5 / zoomLevel, 1));
              });

            // Keep tooltip visible when hovering over tooltip elements
            tooltipElements.forEach(element => {
              if (element) {
                element
                  .on('mouseover.tooltip', () => {
                    showInfo();
                  })
                  .on('mouseout.tooltip', (event) => {
                    const related = event.relatedTarget;
                    const groupNode = markerGroup.node();
                    if (related && groupNode && groupNode.contains(related)) {
                      return;
                    }
                    hideInfo();
                    // Reset flight path highlighting
                    d3.selectAll(`[data-flight-id="${flight.FlightId || flightShipIdStr || 'unknown'}"]`)
                      .attr('stroke-opacity', 0.7)
                      .attr('stroke-width', Math.max(1.5 / zoomLevel, 1));
                  });
              }
            });
          }
        }
      });
    }

    if (graph && (combinedShips?.length > 0 || combinedFlights?.length > 0)) {
      (combinedShips || []).forEach((ship) => {
        const shipKey = ship?.ShipId || ship?.Id || ship?.Ship || ship?.Registration || ship?.Name;
        if (!shipKey) {
          return;
        }

        const shipIdStr = String(shipKey);

        if (selectedShipId !== '__all__' && shipIdStr !== selectedShipId) {
          return;
        }

        if (activeShipIds.has(shipIdStr)) {
          return;
        }

        let locationSystemId = getShipLocationSystemId(ship);
        let idleLocationDetails = getShipLocationDetails(ship, locationSystemId);
        let effectiveSystemId = idleLocationDetails?.systemId || locationSystemId;

        // If no location found but ship has an STL flight, get system from flight
        if (!effectiveSystemId) {
          const shipFlight = flightsByShipId.get(ship.ShipId) || flightsByShipId.get(ship.Id);
          if (shipFlight && Array.isArray(shipFlight.Segments) && shipFlight.Segments.length > 0) {
            // Extract system from flight segments
            const firstSegment = shipFlight.Segments[0];
            const originLocation = extractLocationDetails(firstSegment.OriginLines);
            const destinationLocation = extractLocationDetails(firstSegment.DestinationLines);
            const flightSystemId = originLocation?.systemId || destinationLocation?.systemId;
            if (flightSystemId) {
              effectiveSystemId = flightSystemId;
              idleLocationDetails = originLocation || destinationLocation;
            }
          }
        }

        if (!effectiveSystemId) {
          return;
        }

        const systemCenter = getSystemCenter(effectiveSystemId);

        if (!systemCenter) {
          return;
        }

        // Count STL transit ships as arrivals at their destination
        const stlShipFlight = flightsByShipId.get(ship.ShipId) || flightsByShipId.get(ship.Id);
        if (stlShipFlight && Array.isArray(stlShipFlight.Segments) && stlShipFlight.Segments.length > 0) {
          // Check if this is an STL-only flight (no inter-system segments)
          const hasInterSystemSegment = stlShipFlight.Segments.some(segment => {
            const originLocation = extractLocationDetails(segment.OriginLines);
            const destinationLocation = extractLocationDetails(segment.DestinationLines);
            return originLocation?.systemId && destinationLocation?.systemId &&
              originLocation.systemId !== destinationLocation.systemId;
          });

          if (!hasInterSystemSegment) {
            // This is an STL transit within the system
            const currentSegmentIndex = stlShipFlight.CurrentSegmentIndex ?? 0;
            const currentSegment = stlShipFlight.Segments[currentSegmentIndex];

            if (currentSegment) {
              const destinationLocation = extractLocationDetails(currentSegment.DestinationLines);
              if (destinationLocation && (destinationLocation.planetNaturalId || destinationLocation.stationNaturalId)) {
                const locationId = destinationLocation.planetNaturalId || destinationLocation.stationNaturalId;
                const planetKey = `${effectiveSystemId}-${locationId}`;

                // Ensure destination exists
                if (!destinations.has(planetKey)) {
                  const isStation = !!(destinationLocation.stationId || destinationLocation.stationNaturalId || destinationLocation.stationName);
                  destinations.set(planetKey, {
                    systemId: effectiveSystemId,
                    planetNaturalId: destinationLocation.planetNaturalId || locationId,
                    planetName: destinationLocation.planetName,
                    stationNaturalId: destinationLocation.stationNaturalId,
                    stationName: destinationLocation.stationName,
                    arrivals: [],
                    isStation: isStation
                  });
                }

                // Add STL ship to arrivals
                const shipName = ship?.Name || ship?.ShipName || shipIdStr || 'Unknown Ship';
                const eta = currentSegment.ArrivalTimeEpochMs ? new Date(currentSegment.ArrivalTimeEpochMs) : null;
                destinations.get(planetKey).arrivals.push({
                  shipName,
                  eta,
                  owner: ship?._user || 'Unknown',
                  flightId: stlShipFlight?.FlightId || shipIdStr,
                  isSTL: true
                });
              }
            }
          }
        }

        const effectiveZoom = Math.max(1, zoomLevel);
        // Make idle ship markers 33% smaller
        const radius = Math.max(4 / effectiveZoom, 2);
        const positionKey = `${Math.round(systemCenter.x / 5)}:${Math.round(systemCenter.y / 5)}`;
        const markerSlot = getMarkerSlot(positionKey);

        // Calculate ship position based on flight direction
        let shipAngle = 0;
        let ringRadius = 0;

        // Check if ship has flight data to determine direction
        const directionShipFlight = flightsByShipId.get(ship.ShipId) || flightsByShipId.get(ship.Id);
        let isSTLOnly = false;
        if (directionShipFlight && Array.isArray(directionShipFlight.Segments) && directionShipFlight.Segments.length > 0) {
          // Check if this is an STL-only flight
          isSTLOnly = !directionShipFlight.Segments.some(segment => {
            const originLocation = extractLocationDetails(segment.OriginLines);
            const destinationLocation = extractLocationDetails(segment.DestinationLines);
            return originLocation?.systemId && destinationLocation?.systemId &&
              originLocation.systemId !== destinationLocation.systemId;
          });

          if (!isSTLOnly) {
            // Ship has inter-system flight data - position based on direction
            const segments = directionShipFlight.Segments;
            const currentSegmentIndex = directionShipFlight.CurrentSegmentIndex ?? 0;
            const currentSegment = segments[currentSegmentIndex];

            if (currentSegment) {
              const fromSystem = currentSegment.OriginLines ? extractLocationDetails(currentSegment.OriginLines)?.systemId : null;
              const toSystem = currentSegment.DestinationLines ? extractLocationDetails(currentSegment.DestinationLines)?.systemId : null;

              let directionSystem = null;
              if (fromSystem === effectiveSystemId && toSystem) {
                // Ship is departing from current system to another
                directionSystem = toSystem;
              } else if (toSystem === effectiveSystemId && fromSystem) {
                // Ship is arriving at current system from another
                directionSystem = fromSystem;
              }

              if (directionSystem) {
                const directionCenter = getSystemCenter(directionSystem);
                if (directionCenter) {
                  // Calculate angle from system center to direction system
                  const dx = directionCenter.x - systemCenter.x;
                  const dy = directionCenter.y - systemCenter.y;
                  shipAngle = Math.atan2(dy, dx);

                  // Position in outer ring for ships with directions
                  const destinationMarkerRadius = Math.max(8 / effectiveZoom, 4) * 0.75 * 5; // Planet ring max radius
                  ringRadius = (destinationMarkerRadius + Math.max(8 / effectiveZoom, 6) * 3) * 0.5;
                }
              }
            }
          }
        }

        // If no direction determined, use ring-based positioning
        if (ringRadius === 0) {
          // Arrange idle ships in rings around the system marker (no center position)
          // First ring: 8 ships, Second ring: 16 ships, etc.
          const shipsPerRing = [8, 12, 16, 20, 24, 28]; // Ring sizes
          let ringIndex = 0;
          let positionInRing = markerSlot;
          let cumulativeShips = 0;

          // Determine which ring and position within that ring
          for (let i = 0; i < shipsPerRing.length; i++) {
            if (positionInRing < shipsPerRing[i]) {
              ringIndex = i;
              break;
            }
            positionInRing -= shipsPerRing[i];
            cumulativeShips += shipsPerRing[i];
            ringIndex = i + 1;
          }

          // If we have more ships than planned rings, keep adding rings
          if (ringIndex >= shipsPerRing.length) {
            const extraShips = markerSlot - cumulativeShips;
            const shipsInExtraRing = 28 + (ringIndex - shipsPerRing.length + 1) * 4;
            positionInRing = extraShips % shipsInExtraRing;
          }

          // Arrange in semi-circular rings (bottom half to avoid planet markers)
          const destinationMarkerRadius = Math.max(8 / effectiveZoom, 4) * 0.75 * 5; // Planet ring max radius
          ringRadius = (destinationMarkerRadius + Math.max(8 / effectiveZoom, 6) * ((ringIndex) * 1.5 + 1.5)) * 0.5; // Decreased by 50%
          const shipsInThisRing = ringIndex < shipsPerRing.length
            ? shipsPerRing[ringIndex]
            : 28 + (ringIndex - shipsPerRing.length + 1) * 4;
          shipAngle = (positionInRing / Math.max(shipsInThisRing - 1, 1)) * Math.PI + Math.PI / 2;
        }

        const offsetX = Math.cos(shipAngle) * ringRadius;
        const offsetY = Math.sin(shipAngle) * ringRadius;

        const markerX = systemCenter.x + offsetX;
        const markerY = systemCenter.y + offsetY;
        const shipStyle = classifyShipType(ship, shipIdStr);
        const pathColor = shipStyle.color;
        const loadInfo = shipStyle.loadInfo;
        const normalizedShipKey = normalizeLookupKey(getPrimaryShipId(ship) ?? shipIdStr);
        const filteredShipments = partnerFilterActive
          ? ((normalizedShipKey && partnerFilteredShipments?.get(normalizedShipKey))
            || filterShipmentsByPartner(loadInfo?.shipments))
          : (Array.isArray(loadInfo?.shipments) ? loadInfo.shipments : []);

        if (partnerFilterActive && filteredShipments.length === 0) {
          return;
        }

        const loadInfoForTiles = loadInfo
          ? { ...loadInfo, shipments: filteredShipments }
          : { shipments: filteredShipments };

        const locationName = formatLocationDisplay(idleLocationDetails)
          || systemNames[effectiveSystemId]
          || effectiveSystemId
          || 'Unknown';
        const shipDisplayName = ship.Name || ship.ShipName || ship.ShipId || 'Unknown';

        // Check if this ship has an STL flight and get destination
        const shipFlight = flightsByShipId.get(ship.ShipId) || flightsByShipId.get(ship.Id);
        const hasSTLFlight = shipFlight && Array.isArray(shipFlight.Segments) && shipFlight.Segments.length > 0;

        let statusLabel = `Idle at ${locationName}`;
        if (hasSTLFlight && shipFlight.Segments.length > 0) {
          // Get the destination from the current or next segment
          const currentSegmentIndex = shipFlight.CurrentSegmentIndex ?? 0;
          const currentSegment = shipFlight.Segments[currentSegmentIndex];
          if (currentSegment) {
            const destinationLocation = extractLocationDetails(currentSegment.DestinationLines);
            const destinationDisplay = formatLocationDisplay(destinationLocation);
            if (destinationDisplay) {
              statusLabel = `STL Transit to ${destinationDisplay}`;
            } else {
              statusLabel = `STL Transit in ${locationName}`;
            }
          } else {
            statusLabel = `STL Transit in ${locationName}`;
          }
        }

        const shipTypeLabel = shipStyle.label;
        const loadSummary = buildLoadSummary(loadInfo);
        const shipmentTiles = buildShipmentTiles(loadInfoForTiles);
        const loadBarDescriptors = buildLoadBarDescriptors(loadInfo);

        // Calculate time information for STL transit ships
        let timeRemainingText = '—';
        let etaText = '—';

        const stlFlight = flightsByShipId.get(ship.ShipId) || flightsByShipId.get(ship.Id);
        if (stlFlight && Array.isArray(stlFlight.Segments) && stlFlight.Segments.length > 0) {
          // Check if this is an STL-only flight
          const hasInterSystemSegment = stlFlight.Segments.some(segment => {
            const originLocation = extractLocationDetails(segment.OriginLines);
            const destinationLocation = extractLocationDetails(segment.DestinationLines);
            return originLocation?.systemId && destinationLocation?.systemId &&
              originLocation.systemId !== destinationLocation.systemId;
          });

          if (!hasInterSystemSegment) {
            // This is an STL transit - calculate time remaining
            const currentSegmentIndex = stlFlight.CurrentSegmentIndex ?? 0;
            const currentSegment = stlFlight.Segments[currentSegmentIndex];

            if (currentSegment && currentSegment.ArrivalTimeEpochMs) {
              const arrivalTime = new Date(currentSegment.ArrivalTimeEpochMs);
              const now = new Date();
              const remainingMs = arrivalTime - now;

              if (remainingMs > 0) {
                timeRemainingText = formatDuration(remainingMs);
                etaText = arrivalTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              } else {
                timeRemainingText = 'Arrived';
                etaText = '—';
              }
            }
          }
        }

        const markerGroup = shipLayer.append('g')
          .attr('class', 'ship-group idle-ship-group')
          .attr('data-ship-id', shipIdStr);

        markerGroup.raise();

        markerGroup.append('circle')
          .attr('cx', markerX)
          .attr('cy', markerY)
          .attr('r', radius)
          .attr('fill', pathColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', Math.max(1 / effectiveZoom, 0.6))
          .attr('opacity', 0.95);

        // Add shipment count badge if there are shipments
        const shipmentCount = filteredShipments.length;
        if (shipmentCount > 0) {
          const badgeRadius = Math.max(7 / effectiveZoom, 4.2);
          const badgeOffsetX = radius * 2.0;
          const badgeOffsetY = -radius * 2.0;
          const badgeFontSize = Math.max(8.4 / effectiveZoom, 5.6);

          // Badge background circle
          markerGroup.append('circle')
            .attr('cx', markerX + badgeOffsetX)
            .attr('cy', markerY + badgeOffsetY)
            .attr('r', badgeRadius)
            .attr('fill', '#ef4444')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1.12 / effectiveZoom, 0.7))
            .attr('opacity', 0.95);

          // Badge count text
          markerGroup.append('text')
            .attr('x', markerX + badgeOffsetX)
            .attr('y', markerY + badgeOffsetY)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .attr('fill', '#ffffff')
            .attr('font-size', `${badgeFontSize}px`)
            .attr('font-weight', 700)
            .attr('opacity', 1)
            .text(shipmentCount > 99 ? '99+' : shipmentCount);
        }

        const labelX = markerX + radius + 6;
        const labelY = markerY - (radius * 0.8);
        const baseFontSize = 8;
        const lineHeight = baseFontSize * 1.2;

        const label = markerGroup.append('text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('fill', '#ffffff')
          .attr('font-size', `${baseFontSize}px`)
          .attr('font-weight', 600)
          .attr('stroke', '#000000')
          .attr('stroke-width', 1 / Math.max(1, effectiveZoom * 2))
          .attr('paint-order', 'stroke')
          .attr('text-anchor', 'start')
          .attr('dominant-baseline', 'hanging')
          .attr('opacity', 0.95);

        if (!labelsEnabled) {
          label.style('display', 'none');
        }

        const labelPaddingX = 6;
        const labelPaddingY = 3;
        const labelCornerRadius = 4;
        const labelBackground = markerGroup.insert('rect', 'text')
          .attr('class', 'ship-label-background')
          .attr('fill', 'rgba(15, 23, 42, 0.85)')
          .attr('stroke', '#000000')
          .attr('stroke-width', 0.5)
          .attr('rx', labelCornerRadius)
          .attr('ry', labelCornerRadius)
          .attr('opacity', 0.95)
          .style('pointer-events', 'none')
          .style('display', 'none');

        let loadBarsContainer = null;
        let loadBarEntries = [];

        if (loadBarDescriptors.length > 0) {
          loadBarsContainer = markerGroup.append('g')
            .attr('class', 'ship-load-bars')
            .style('pointer-events', 'none')
            .style('display', 'none');

          loadBarEntries = loadBarDescriptors.map((descriptor) => {
            const barGroup = loadBarsContainer.append('g')
              .attr('class', `ship-load-bar ship-load-bar-${descriptor.key}`);
            const background = barGroup.append('rect')
              .attr('class', 'ship-load-bar-bg');
            const fill = barGroup.append('rect')
              .attr('class', 'ship-load-bar-fill');
            const labelNode = barGroup.append('text')
              .attr('class', 'ship-load-bar-label')
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'hanging')
              .attr('font-weight', 500)
              .attr('fill', '#bfdbfe');

            return {
              descriptor,
              group: barGroup,
              background,
              fill,
              label: labelNode
            };
          });
        }

        let shipmentTilesContainer = null;
        let shipmentTileEntries = [];

        if (shipmentTiles.length > 0) {
          shipmentTilesContainer = markerGroup.append('g')
            .attr('class', 'ship-shipment-tiles')
            .style('pointer-events', 'none')
            .style('display', 'none');

          shipmentTileEntries = shipmentTiles.map((tile) => {
            const tileGroup = shipmentTilesContainer.append('g')
              .attr('class', 'ship-shipment-tile');

            const background = tileGroup.append('rect')
              .attr('class', 'ship-shipment-tile-bg');

            const textNode = tileGroup.append('text')
              .attr('class', 'ship-shipment-tile-text')
              .attr('text-anchor', 'start')
              .attr('dominant-baseline', 'hanging')
              .attr('font-weight', 500);

            const lineTspans = tile.lines.map((line) => textNode.append('tspan').text(line));

            return {
              tile,
              group: tileGroup,
              background,
              text: textNode,
              lineTspans
            };
          });
        }

        const updateInfoLayout = () => {
          const labelNode = label.node();
          if (!labelNode) return;
          const bbox = labelNode.getBBox();
          if (!bbox || (bbox.width === 0 && bbox.height === 0)) return;

          let minX = bbox.x;
          const minY = bbox.y;
          let maxX = bbox.x + bbox.width;
          let maxY = bbox.y + bbox.height;

          if (loadBarsContainer && loadBarEntries.length > 0) {
            const baseBarWidth = Math.max(bbox.width, 70);
            const barHeight = 6;
            const barRadius = barHeight / 2;
            const gap = 4;
            const barX = bbox.x;
            let currentY = bbox.y + bbox.height + gap;

            loadBarEntries.forEach((entry, index) => {
              const ratioValueRaw = entry.descriptor.ratio != null ? entry.descriptor.ratio : 0;
              const ratioValue = Math.max(0, Math.min(ratioValueRaw, 1.1));
              const fillRatio = Math.min(ratioValue, 1);
              const barWidth = baseBarWidth;

              entry.group.attr('transform', `translate(${barX}, ${currentY})`);

              entry.background
                .attr('width', barWidth)
                .attr('height', barHeight)
                .attr('rx', barRadius)
                .attr('ry', barRadius)
                .attr('fill', 'rgba(15, 23, 42, 0.85)')
                .attr('stroke', '#0f172a')
                .attr('stroke-width', 0.5)
                .attr('opacity', 0.95);

              entry.fill
                .attr('width', Math.max(barWidth * fillRatio, 2))
                .attr('height', barHeight)
                .attr('rx', barRadius)
                .attr('ry', barRadius)
                .attr('fill', getLoadColorForRatio(ratioValue) || '#38bdf8')
                .attr('opacity', 0.95);

              const labelFontSize = 9;
              const labelGap = 4;
              const labelText = entry.descriptor.summary
                || `${entry.descriptor.kindLabel} ${(Math.min(ratioValue, 1) * 100).toFixed(1)}%`;

              entry.label
                .attr('x', barWidth / 2)
                .attr('y', barHeight + labelGap)
                .attr('font-size', `${labelFontSize}px`)
                .text(labelText);

              const barBottom = currentY + barHeight + labelGap + labelFontSize;

              if (index === loadBarEntries.length - 1) {
                maxY = Math.max(maxY, barBottom);
              } else {
                maxY = Math.max(maxY, barBottom + 2);
              }

              minX = Math.min(minX, barX);
              maxX = Math.max(maxX, barX + barWidth);

              currentY = barBottom + 2;
            });
          }

          if (shipmentTilesContainer && shipmentTileEntries.length > 0) {
            const tilePaddingX = 6;
            const tilePaddingY = 4;
            const tileGap = 4;
            const tileRadius = 4;
            const textFontSize = 9;
            const tileLineHeight = baseFontSize * 1.05;
            const tileX = bbox.x;
            let currentY = Math.max(maxY + tileGap, bbox.y + bbox.height + tileGap);

            shipmentTileEntries.forEach((entry) => {
              entry.group.attr('transform', `translate(${tileX}, ${currentY})`);

              entry.text
                .attr('x', tilePaddingX)
                .attr('y', tilePaddingY)
                .attr('fill', '#e0f2fe')
                .attr('font-size', `${textFontSize}px`);

              entry.lineTspans.forEach((tspanNode, idx) => {
                tspanNode
                  .attr('x', tilePaddingX)
                  .attr('dy', idx === 0 ? 0 : tileLineHeight)
                  .attr('fill', idx === 0 ? '#facc15' : (idx === 1 ? '#bae6fd' : '#cbd5f5'))
                  .attr('font-weight', idx === 0 ? 700 : (idx === 1 ? 600 : 500))
                  .text(entry.tile.lines[idx]);
              });

              const textNode = entry.text.node();
              const textBBox = textNode && typeof textNode.getBBox === 'function'
                ? textNode.getBBox()
                : null;

              const tileWidth = textBBox
                ? Math.max(bbox.width, textBBox.width + tilePaddingX * 2)
                : Math.max(bbox.width, 90);
              const tileHeight = textBBox
                ? textBBox.height + tilePaddingY * 2
                : tilePaddingY * 2 + tileLineHeight * entry.tile.lines.length;

              entry.background
                .attr('x', 0)
                .attr('y', 0)
                .attr('width', tileWidth)
                .attr('height', tileHeight)
                .attr('rx', tileRadius)
                .attr('ry', tileRadius)
                .attr('fill', 'rgba(12, 25, 46, 0.92)')
                .attr('stroke', '#3b82f6')
                .attr('stroke-width', 0.75)
                .attr('opacity', 0.95);

              minX = Math.min(minX, tileX);
              maxX = Math.max(maxX, tileX + tileWidth);
              const tileBottom = currentY + tileHeight;
              maxY = Math.max(maxY, tileBottom);

              currentY = tileBottom + tileGap;
            });
          }

          labelBackground
            .attr('x', minX - labelPaddingX)
            .attr('y', minY - labelPaddingY)
            .attr('width', (maxX - minX) + (labelPaddingX * 2))
            .attr('height', (maxY - minY) + (labelPaddingY * 2));
        };

        label.append('tspan')
          .attr('x', labelX)
          .attr('dy', 0)
          .style('font-size', `${baseFontSize * 1.25}px`)
          .attr('font-weight', 700)
          .text(shipDisplayName);

        // Add owner label for group ships
        if (ship._user && groupShips && groupShips.length > 0) {
          label.append('tspan')
            .attr('x', labelX)
            .attr('dy', `${lineHeight * 0.9}px`)
            .attr('fill', '#60a5fa')
            .attr('font-weight', 600)
            .style('font-size', `${baseFontSize * 0.85}px`)
            .text(`Owner: ${ship._user}`);
        }

        const typeDescription = shipTypeLabel;

        const typeTspan = label.append('tspan')
          .attr('x', labelX)
          .attr('dy', `${lineHeight}px`)
          .attr('fill', '#c7d2fe')
          .attr('font-weight', 500)
          .style('font-size', `${baseFontSize * 1.05}px`)
          .style('display', 'none')
          .text(typeDescription);

        const statusTspan = label.append('tspan')
          .attr('x', labelX)
          .attr('dy', `${lineHeight}px`)
          .attr('fill', '#facc15')
          .attr('font-weight', 600)
          .style('font-size', `${baseFontSize * 1.05}px`)
          .style('display', 'none')
          .text(statusLabel);

        const loadSummaryTspan = loadSummary
          ? label.append('tspan')
            .attr('x', labelX)
            .attr('dy', `${lineHeight}px`)
            .attr('fill', '#bfdbfe')
            .attr('font-weight', 500)
            .style('font-size', `${baseFontSize}px`)
            .style('display', 'none')
            .text(loadSummary)
          : null;

        const remainingTspan = label.append('tspan')
          .attr('x', labelX)
          .attr('dy', `${lineHeight}px`)
          .attr('fill', '#d1d5db')
          .attr('font-weight', 400)
          .style('display', 'none')
          .text(`Time Remaining: ${timeRemainingText}`);

        const etaTspan = label.append('tspan')
          .attr('x', labelX)
          .attr('dy', `${lineHeight}px`)
          .attr('fill', '#d1d5db')
          .attr('font-weight', 400)
          .style('display', 'none')
          .text(`ETA: ${etaText}`);

        const infoSpans = [typeTspan, statusTspan, loadSummaryTspan, remainingTspan, etaTspan].filter(Boolean);

        const showInfo = () => {
          markerGroup.raise();
          label.style('display', null);
          infoSpans.forEach((span) => span.style('display', null));
          labelBackground.style('display', null);
          if (loadBarsContainer) {
            loadBarsContainer.style('display', null);
          }
          if (shipmentTilesContainer) {
            shipmentTilesContainer.style('display', null);
          }
          updateInfoLayout();
        };

        const hideInfo = () => {
          infoSpans.forEach((span) => span.style('display', 'none'));
          labelBackground.style('display', 'none');
          if (loadBarsContainer) {
            loadBarsContainer.style('display', 'none');
          }
          if (shipmentTilesContainer) {
            shipmentTilesContainer.style('display', 'none');
          }
          if (!labelsEnabled) {
            label.style('display', 'none');
          }
        };

        // Enable pointer events on tooltip elements to keep them visible when hovering
        if (labelBackground) {
          labelBackground.style('pointer-events', 'all');
        }
        if (loadBarsContainer) {
          loadBarsContainer.style('pointer-events', 'all');
        }
        if (shipmentTilesContainer) {
          shipmentTilesContainer.style('pointer-events', 'all');
        }

        const tooltipElements = [labelBackground, loadBarsContainer, shipmentTilesContainer].filter(Boolean);

        markerGroup
          .on('mouseover.shipinfo', () => {
            showInfo();
          })
          .on('mouseout.shipinfo', (event) => {
            const related = event.relatedTarget;
            const groupNode = markerGroup.node();
            if (related && groupNode && groupNode.contains(related)) {
              return;
            }
            hideInfo();
          });

        // Keep tooltip visible when hovering over tooltip elements
        tooltipElements.forEach(element => {
          if (element) {
            element
              .on('mouseover.tooltip', () => {
                showInfo();
              })
              .on('mouseout.tooltip', (event) => {
                const related = event.relatedTarget;
                const groupNode = markerGroup.node();
                if (related && groupNode && groupNode.contains(related)) {
                  return;
                }
                hideInfo();
              });
          }
        });
      });
    }

    // Create permanent station markers for all stations (static, affects nothing)
    if (stationData && Array.isArray(stationData)) {
      const stationLayer = overlayLayer.append('g').attr('class', 'permanent-station-markers');

      // Group stations by system
      const stationsBySystem = new Map();
      stationData.forEach((station) => {
        if (station?.SystemId) {
          if (!stationsBySystem.has(station.SystemId)) {
            stationsBySystem.set(station.SystemId, []);
          }
          stationsBySystem.get(station.SystemId).push(station);
        }
      });

      stationsBySystem.forEach((systemStations, systemId) => {
        const systemCenter = getSystemCenter(systemId);
        if (!systemCenter) return;

        const effectiveZoom = Math.max(1, zoomLevel);
        const baseMarkerSize = Math.max(8 / effectiveZoom, 4) * 0.75;

        systemStations.forEach((station, index) => {
          let markerX = systemCenter.x;
          let markerY = systemCenter.y;

          // If multiple stations, arrange them in a small circle around center
          if (systemStations.length > 1) {
            const angle = (index / systemStations.length) * 2 * Math.PI;
            const radius = baseMarkerSize * 1.5;
            markerX += Math.cos(angle) * radius;
            markerY += Math.sin(angle) * radius;
          }

          const markerSize = baseMarkerSize * 1.69; // Same size as destination markers

          // Create permanent station marker (static, no interactions)
          const markerGroup = stationLayer.append('g')
            .attr('class', 'permanent-station-marker')
            .style('pointer-events', 'none'); // No interactions

          // Background circle
          markerGroup.append('circle')
            .attr('cx', markerX)
            .attr('cy', markerY)
            .attr('r', markerSize + 2)
            .attr('fill', 'rgba(0, 0, 0, 0.7)')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1 / effectiveZoom, 0.5));

          // Main marker (station = square)
          markerGroup.append('rect')
            .attr('x', markerX - markerSize / 2)
            .attr('y', markerY - markerSize / 2)
            .attr('width', markerSize)
            .attr('height', markerSize)
            .attr('fill', '#3b82f6')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1 / effectiveZoom, 0.5))
            .attr('rx', 1);
        });
      });
    }

    // Render destination markers
    if (destinations.size > 0) {
      const destinationLayer = overlayLayer.append('g').attr('class', 'destination-markers');

      // Group destinations by system for positioning
      const destinationsBySystem = new Map();
      destinations.forEach((destInfo, planetKey) => {
        const systemId = destInfo.systemId;
        if (!destinationsBySystem.has(systemId)) {
          destinationsBySystem.set(systemId, []);
        }
        destinationsBySystem.get(systemId).push(destInfo);
      });

      destinationsBySystem.forEach((systemDestinations, systemId) => {
        const systemCenter = getSystemCenter(systemId);
        if (!systemCenter) return;

        const effectiveZoom = Math.max(1, zoomLevel);
        const baseMarkerSize = Math.max(8 / effectiveZoom, 4) * 0.75; // Reduced by 25%

        // Separate stations and planets
        const stations = systemDestinations.filter(dest => dest.isStation);
        const planets = systemDestinations.filter(dest => !dest.isStation);

        // Position station markers in the center (permanent)
        stations.forEach((destInfo, index) => {
          let markerX = systemCenter.x;
          let markerY = systemCenter.y;

          // If multiple stations, arrange them in a small circle around center
          if (stations.length > 1) {
            const angle = (index / stations.length) * 2 * Math.PI;
            const radius = baseMarkerSize * 1.5;
            markerX += Math.cos(angle) * radius;
            markerY += Math.sin(angle) * radius;
          }

          const markerSize = baseMarkerSize * 1.69; // Increased by 69% total for stations (30% + 30% more)
          const arrivals = destInfo.arrivals.sort((a, b) => {
            if (!a.eta && !b.eta) return 0;
            if (!a.eta) return 1;
            if (!b.eta) return -1;
            return a.eta - b.eta;
          });

          // Create destination marker
          const markerGroup = destinationLayer.append('g')
            .attr('class', 'destination-marker')
            .attr('data-system-id', systemId)
            .style('cursor', 'pointer');

          // Background circle
          markerGroup.append('circle')
            .attr('cx', markerX)
            .attr('cy', markerY)
            .attr('r', markerSize + 2)
            .attr('fill', 'rgba(0, 0, 0, 0.7)')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1 / effectiveZoom, 0.5));

          // Main marker (station = square)
          markerGroup.append('rect')
            .attr('x', markerX - markerSize / 2)
            .attr('y', markerY - markerSize / 2)
            .attr('width', markerSize)
            .attr('height', markerSize)
            .attr('fill', '#3b82f6')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1 / effectiveZoom, 0.5))
            .attr('rx', 1);

          // Badge variables (shared between arrival and shipment badges)
          const badgeRadius = Math.max(5 / effectiveZoom, 3);
          const badgeOffsetX = -markerSize * 0.6;
          const badgeOffsetY = -markerSize * 1;

          // Arrival count badge (render over marker)
          if (arrivals.length > 0) {
            markerGroup.append('circle')
              .attr('cx', markerX + badgeOffsetX)
              .attr('cy', markerY + badgeOffsetY)
              .attr('r', badgeRadius)
              .attr('fill', '#3b82f6')
              .attr('stroke', '#ffffff')
              .attr('stroke-width', Math.max(0.5 / effectiveZoom, 0.3));

            markerGroup.append('text')
              .attr('x', markerX + badgeOffsetX)
              .attr('y', markerY + badgeOffsetY)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'central')
              .attr('fill', '#ffffff')
              .attr('font-size', `${Math.max(6 / effectiveZoom, 4)}px`)
              .attr('font-weight', 700)
              .text(arrivals.length > 9 ? '9+' : arrivals.length);
          }

          // Station shipments badge (under arriving ships badge)
          const systemShipments = systemShipmentCounts?.shipmentDetails?.get(systemId) || [];
          if (systemShipments.length > 0) {
            const shipmentBadgeOffsetY = markerSize; // Position below arriving badge
            markerGroup.append('circle')
              .attr('cx', markerX + badgeOffsetX)
              .attr('cy', markerY + shipmentBadgeOffsetY)
              .attr('r', badgeRadius)
              .attr('fill', '#ef4444')
              .attr('stroke', '#ffffff')
              .attr('stroke-width', Math.max(0.5 / effectiveZoom, 0.3));

            markerGroup.append('text')
              .attr('x', markerX + badgeOffsetX)
              .attr('y', markerY + shipmentBadgeOffsetY)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'central')
              .attr('fill', '#ffffff')
              .attr('font-size', `${Math.max(6 / effectiveZoom, 4)}px`)
              .attr('font-weight', 700)
              .text(systemShipments.length > 99 ? '99+' : systemShipments.length);
          }

          // Hover tooltip
          markerGroup
            .on('mouseover', function (event) {
              event.stopPropagation();

              // Highlight all flight paths for ships arriving at this destination
              arrivals.forEach(arrival => {
                if (arrival.flightId) {
                  d3.selectAll(`[data-flight-id="${arrival.flightId}"]`)
                    .attr('stroke-opacity', 1.0)
                    .attr('stroke-width', Math.max(2.5 / zoomLevel, 1.5));
                }
              });

              // Create tooltip
              const tooltipGroup = tooltipLayer.append('g')
                .attr('class', 'destination-tooltip')
                .style('pointer-events', 'none'); const tooltipX = markerX + 15;
              let tooltipY = markerY - 10;

              // Header
              const headerText = tooltipGroup.append('text')
                .attr('x', tooltipX)
                .attr('y', tooltipY)
                .attr('fill', '#f7a600')
                .attr('font-size', '11px')
                .attr('font-weight', 700)
                .attr('text-anchor', 'start')
                .attr('dominant-baseline', 'hanging')
                .text(`${destInfo.isStation ? 'Station' : 'Planet'}: ${destInfo.stationName || destInfo.stationNaturalId || destInfo.planetName || destInfo.planetNaturalId || systemNames[systemId] || systemId}`);

              tooltipY += 16;

              // Arrival list
              arrivals.slice(0, 8).forEach((arrival, index) => {
                const etaText = arrival.eta ? (() => {
                  const ms = arrival.eta - new Date();
                  if (ms <= 0) return '0m';
                  const totalMinutes = Math.floor(ms / (1000 * 60));
                  const days = Math.floor(totalMinutes / (24 * 60));
                  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
                  const minutes = totalMinutes % 60;

                  const parts = [];
                  if (days > 0) parts.push(`${days}d`);
                  if (hours > 0) parts.push(`${hours}h`);
                  if (minutes > 0 || (days === 0 && hours === 0)) parts.push(`${minutes}m`);

                  return parts.join(' ');
                })() : 'Unknown';

                const arrivalText = tooltipGroup.append('text')
                  .attr('x', tooltipX)
                  .attr('y', tooltipY)
                  .attr('fill', index === 0 ? '#60a5fa' : '#cbd5f5')
                  .attr('font-size', '10px')
                  .attr('font-weight', index === 0 ? 600 : 400)
                  .attr('text-anchor', 'start')
                  .attr('dominant-baseline', 'hanging')
                  .text(`${arrival.shipName} (${etaText})`);

                tooltipY += 12;
              });

              if (arrivals.length > 8) {
                tooltipGroup.append('text')
                  .attr('x', tooltipX)
                  .attr('y', tooltipY)
                  .attr('fill', '#888')
                  .attr('font-size', '9px')
                  .attr('text-anchor', 'start')
                  .attr('dominant-baseline', 'hanging')
                  .text(`...and ${arrivals.length - 8} more`);
                tooltipY += 12;
              }

              // Add system shipment tiles below arrivals
              const systemShipments = systemShipmentCounts?.shipmentDetails?.get(systemId) || [];
              if (systemShipments.length > 0) {
                tooltipY += 8; // Add some spacing

                // Build shipment tiles similar to system tooltip
                const baseFontSize = 9;
                const lineHeight = baseFontSize * 1.2;
                const tilePaddingX = 6;
                const tilePaddingY = 4;
                const tileGap = 4;
                const tileRadius = 3;

                systemShipments.slice(0, 5).forEach(shipment => { // Limit to 5 tiles
                  if (shipment.contract?.localId) {
                    const contract = shipment.contract;
                    const contractLocalId = contract.localId;

                    const weightText = shipment.weight > 0 ? formatCapacityValue(shipment.weight) : '—';
                    const volumeText = shipment.volume > 0 ? formatCapacityValue(shipment.volume) : '—';
                    const destination = contract.destination || '—';

                    let deadlineText = '';
                    if (contract.deadlineEpochMs) {
                      const deadlineDate = new Date(contract.deadlineEpochMs);
                      const now = new Date();
                      const totalHours = Math.round((deadlineDate - now) / (1000 * 60 * 60));
                      const days = Math.floor(totalHours / 24);
                      const hours = totalHours % 24;

                      if (days > 0) {
                        deadlineText = ` (${days}d ${hours}h)`;
                      } else {
                        deadlineText = ` (${hours}h)`;
                      }
                    }

                    const lines = [
                      `${contractLocalId}${deadlineText}`,
                      `Wt ${weightText} | Vol ${volumeText}`,
                      `→${destination}`
                    ];

                    // Create tile background
                    const tileGroup = tooltipGroup.append('g');

                    // Calculate tile dimensions first
                    const tempText = tileGroup.append('text')
                      .attr('font-size', `${baseFontSize}px`)
                      .attr('text-anchor', 'start')
                      .attr('dominant-baseline', 'hanging')
                      .style('visibility', 'hidden'); // Hidden for measurement

                    lines.forEach((line, idx) => {
                      tempText.append('tspan')
                        .attr('x', 0)
                        .attr('dy', idx === 0 ? 0 : lineHeight)
                        .text(line);
                    });

                    const textBBox = tempText.node().getBBox();
                    const tileWidth = textBBox.width + tilePaddingX * 2;
                    const tileHeight = textBBox.height + tilePaddingY * 2;

                    tempText.remove(); // Remove temp text

                    // Draw tile background
                    tileGroup.append('rect')
                      .attr('x', tooltipX)
                      .attr('y', tooltipY)
                      .attr('width', tileWidth)
                      .attr('height', tileHeight)
                      .attr('fill', 'rgba(30, 41, 59, 0.8)')
                      .attr('stroke', '#475569')
                      .attr('stroke-width', 1)
                      .attr('rx', tileRadius);

                    // Draw tile text
                    const tileText = tileGroup.append('text')
                      .attr('x', tooltipX + tilePaddingX)
                      .attr('y', tooltipY + tilePaddingY)
                      .attr('font-size', `${baseFontSize}px`)
                      .attr('text-anchor', 'start')
                      .attr('dominant-baseline', 'hanging');

                    lines.forEach((line, idx) => {
                      tileText.append('tspan')
                        .attr('x', tooltipX + tilePaddingX)
                        .attr('dy', idx === 0 ? 0 : lineHeight)
                        .attr('fill', idx === 0 ? '#facc15' : (idx === 1 ? '#bae6fd' : '#cbd5f5'))
                        .attr('font-weight', idx === 0 ? 700 : (idx === 1 ? 600 : 500))
                        .text(line);
                    });

                    tooltipY += tileHeight + tileGap;
                  }
                });

                if (systemShipments.length > 5) {
                  tooltipGroup.append('text')
                    .attr('x', tooltipX)
                    .attr('y', tooltipY)
                    .attr('fill', '#888')
                    .attr('font-size', '8px')
                    .attr('text-anchor', 'start')
                    .attr('dominant-baseline', 'hanging')
                    .text(`...and ${systemShipments.length - 5} more shipments`);
                  tooltipY += 10;
                }
              }

              // Tooltip background
              const bbox = tooltipGroup.node().getBBox();
              tooltipGroup.insert('rect', ':first-child')
                .attr('x', bbox.x - 6)
                .attr('y', bbox.y - 4)
                .attr('width', bbox.width + 12)
                .attr('height', bbox.height + 8)
                .attr('fill', 'rgba(0, 0, 0, 0.9)')
                .attr('stroke', '#333')
                .attr('stroke-width', 1)
                .attr('rx', 4);

              // Store tooltip reference for cleanup
              markerGroup._tooltip = tooltipGroup;
            })
            .on('mouseout', function (event) {
              event.stopPropagation();

              // Reset flight path highlighting
              arrivals.forEach(arrival => {
                if (arrival.flightId) {
                  d3.selectAll(`[data-flight-id="${arrival.flightId}"]`)
                    .attr('stroke-opacity', 0.7)
                    .attr('stroke-width', Math.max(1.5 / zoomLevel, 1));
                }
              });

              // Remove tooltip
              if (markerGroup._tooltip) {
                markerGroup._tooltip.remove();
                markerGroup._tooltip = null;
              }
            });
        });

        // Position planet markers in a semi-circle on the top outer ring
        planets.forEach((destInfo, index) => {
          let markerX = systemCenter.x;
          let markerY = systemCenter.y;

          // Arrange planet markers in a semi-circle (top half) with auto-scaling
          // Calculate radius to fit all planets without overlap
          const minRadius = baseMarkerSize * 2;
          const maxRadius = baseMarkerSize * 5;
          const planetSpacing = baseMarkerSize * 1.5; // Minimum spacing between planets
          const requiredArcLength = planets.length * planetSpacing;
          const requiredRadius = requiredArcLength / Math.PI; // Semi-circle arc length
          const radius = Math.max(minRadius, Math.min(maxRadius, requiredRadius)) * 1.3; // Increased by 30%

          const angle = ((index - Math.floor(planets.length / 2)) / planets.length) * Math.PI * 0.75; // 0 to π (right to left on top)
          markerX += Math.cos(angle) * radius;
          markerY += Math.sin(angle) * radius;

          const markerSize = baseMarkerSize * 0.6; // 40% smaller
          const arrivals = destInfo.arrivals.sort((a, b) => {
            if (!a.eta && !b.eta) return 0;
            if (!a.eta) return 1;
            if (!b.eta) return -1;
            return a.eta - b.eta;
          });

          // Create destination marker
          const markerGroup = destinationLayer.append('g')
            .attr('class', 'destination-marker')
            .attr('data-system-id', systemId)
            .style('cursor', 'pointer');

          // Background circle
          markerGroup.append('circle')
            .attr('cx', markerX)
            .attr('cy', markerY)
            .attr('r', markerSize + 2)
            .attr('fill', 'rgba(0, 0, 0, 0.7)')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1 / effectiveZoom, 0.5));

          // Main marker (planet = circle)
          markerGroup.append('circle')
            .attr('cx', markerX)
            .attr('cy', markerY)
            .attr('r', markerSize)
            .attr('fill', '#10b981')
            .attr('stroke', '#ffffff')
            .attr('stroke-width', Math.max(1 / effectiveZoom, 0.5));

          // Arrival count badge (render over marker)
          if (arrivals.length > 0) {
            const badgeRadius = Math.max(5 / effectiveZoom, 3);
            markerGroup.append('circle')
              .attr('cx', markerX + markerSize)
              .attr('cy', markerY - markerSize)
              .attr('r', badgeRadius)
              .attr('fill', '#3b82f6')
              .attr('stroke', '#ffffff')
              .attr('stroke-width', Math.max(0.5 / effectiveZoom, 0.3));

            markerGroup.append('text')
              .attr('x', markerX + markerSize)
              .attr('y', markerY - markerSize)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'central')
              .attr('fill', '#ffffff')
              .attr('font-size', `${Math.max(6 / effectiveZoom, 4)}px`)
              .attr('font-weight', 700)
              .text(arrivals.length > 9 ? '9+' : arrivals.length);
          }

          // Hover tooltip
          markerGroup
            .on('mouseover', function (event) {
              event.stopPropagation();

              // Highlight all flight paths for ships arriving at this destination
              arrivals.forEach(arrival => {
                if (arrival.flightId) {
                  d3.selectAll(`[data-flight-id="${arrival.flightId}"]`)
                    .attr('stroke-opacity', 1.0)
                    .attr('stroke-width', Math.max(2.5 / zoomLevel, 1.5));
                }
              });

              // Create tooltip
              const tooltipGroup = tooltipLayer.append('g')
                .attr('class', 'destination-tooltip')
                .style('pointer-events', 'none'); const tooltipX = markerX + 15;
              let tooltipY = markerY - 10;

              // Header
              const headerText = tooltipGroup.append('text')
                .attr('x', tooltipX)
                .attr('y', tooltipY)
                .attr('fill', '#f7a600')
                .attr('font-size', '11px')
                .attr('font-weight', 700)
                .attr('text-anchor', 'start')
                .attr('dominant-baseline', 'hanging')
                .text(`${destInfo.isStation ? 'Station' : 'Planet'}: ${destInfo.stationName || destInfo.stationNaturalId || destInfo.planetName || destInfo.planetNaturalId || systemNames[systemId] || systemId}`);

              tooltipY += 16;

              // Arrival list
              arrivals.slice(0, 8).forEach((arrival, index) => {
                const etaText = arrival.eta ? (() => {
                  const ms = arrival.eta - new Date();
                  if (ms <= 0) return '0m';
                  const totalMinutes = Math.floor(ms / (1000 * 60));
                  const days = Math.floor(totalMinutes / (24 * 60));
                  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
                  const minutes = totalMinutes % 60;

                  const parts = [];
                  if (days > 0) parts.push(`${days}d`);
                  if (hours > 0) parts.push(`${hours}h`);
                  if (minutes > 0 || (days === 0 && hours === 0)) parts.push(`${minutes}m`);

                  return parts.join(' ');
                })() : 'Unknown';

                const arrivalText = tooltipGroup.append('text')
                  .attr('x', tooltipX)
                  .attr('y', tooltipY)
                  .attr('fill', index === 0 ? '#60a5fa' : '#cbd5f5')
                  .attr('font-size', '10px')
                  .attr('font-weight', index === 0 ? 600 : 400)
                  .attr('text-anchor', 'start')
                  .attr('dominant-baseline', 'hanging')
                  .text(`${arrival.shipName} (${etaText})`);

                tooltipY += 12;
              });

              if (arrivals.length > 8) {
                tooltipGroup.append('text')
                  .attr('x', tooltipX)
                  .attr('y', tooltipY)
                  .attr('fill', '#888')
                  .attr('font-size', '9px')
                  .attr('text-anchor', 'start')
                  .attr('dominant-baseline', 'hanging')
                  .text(`...and ${arrivals.length - 8} more`);
              }

              // Tooltip background
              const bbox = tooltipGroup.node().getBBox();
              tooltipGroup.insert('rect', ':first-child')
                .attr('x', bbox.x - 6)
                .attr('y', bbox.y - 4)
                .attr('width', bbox.width + 12)
                .attr('height', bbox.height + 8)
                .attr('fill', 'rgba(0, 0, 0, 0.9)')
                .attr('stroke', '#333')
                .attr('stroke-width', 1)
                .attr('rx', 4);

              // Store tooltip reference for cleanup
              markerGroup._tooltip = tooltipGroup;
            })
            .on('mouseout', function (event) {
              event.stopPropagation();

              // Reset flight path highlighting
              arrivals.forEach(arrival => {
                if (arrival.flightId) {
                  d3.selectAll(`[data-flight-id="${arrival.flightId}"]`)
                    .attr('stroke-opacity', 0.7)
                    .attr('stroke-width', Math.max(1.5 / zoomLevel, 1));
                }
              });

              // Remove tooltip
              if (markerGroup._tooltip) {
                markerGroup._tooltip.remove();
                markerGroup._tooltip = null;
              }
            });
        });
      });
    }

    if (!isOverlayVisible || isLoading || error) {
      return;
    }

    // Color scales for both metrics
    const densityColorScale = d3.scaleSequential()
      .domain([0, maxValues.density])
      .interpolator(d3.interpolatePuBu);

    const luminosityColorScale = d3.scaleSequential()
      .domain([0, maxValues.luminosity])
      .interpolator(d3.interpolateWarm);

    g.selectAll('rect:not(.meteor-density-bar)').each(function () {
      const node = d3.select(this);
      const systemId = node.attr('id');

      if (systemId === 'rect1') return;

      const density = meteorDensityData[systemId] || 0;
      const luminosity = luminosityData[systemId] || 0;

      const nodeWidth = parseFloat(node.attr('width'));
      const nodeHeight = parseFloat(node.attr('height'));
      const nodeX = parseFloat(node.attr('x'));
      const nodeY = parseFloat(node.attr('y'));

      const systemGroup = overlayLayer.append('g')
        .attr('class', 'meteor-density-group');

      // Calculate bar dimensions
      const barWidth = Math.max(3, nodeWidth * 0.2 / zoomLevel);
      const maxBarHeight = nodeHeight;
      const barSpacing = barWidth * 0.5;

      // Create log scale for luminosity bar height
      const luminosityLogScale = d3.scaleLog()
        .domain([0.01, maxValues.luminosity])
        .range([0, maxBarHeight]);

      // Density bar
      const densityHeight = maxBarHeight * (density / maxValues.density);
      const densityX = nodeX + nodeWidth * 1.2;

      // Background for density bar
      systemGroup.append('rect')
        .attr('class', 'meteor-density-bar-background data-overlay')
        .attr('x', densityX)
        .attr('y', nodeY)
        .attr('width', barWidth)
        .attr('height', maxBarHeight)
        .attr('fill', '#2a2a2a')
        .attr('opacity', 0.5);

      // Density bar
      const densityBar = systemGroup.append('rect')
        .attr('class', 'meteor-density-bar data-overlay')
        .attr('x', densityX)
        .attr('y', nodeY + maxBarHeight - densityHeight)
        .attr('width', barWidth)
        .attr('height', densityHeight)
        .attr('fill', densityColorScale(density))
        .attr('opacity', 0.8);

      // Luminosity bar
      const luminosityHeight = luminosityLogScale(Math.max(0.1, luminosity));
      const luminosityX = densityX + barWidth + barSpacing;

      // Background for luminosity bar
      systemGroup.append('rect')
        .attr('class', 'luminosity-bar-background data-overlay')
        .attr('x', luminosityX)
        .attr('y', nodeY)
        .attr('width', barWidth)
        .attr('height', maxBarHeight)
        .attr('fill', '#2a2a2a')
        .attr('opacity', 0.5);

      // Luminosity bar
      const luminosityBar = systemGroup.append('rect')
        .attr('class', 'luminosity-bar data-overlay')
        .attr('x', luminosityX)
        .attr('y', nodeY + maxBarHeight - luminosityHeight)
        .attr('width', barWidth)
        .attr('height', luminosityHeight)
        .attr('fill', luminosityColorScale(luminosity))
        .attr('opacity', 0.8);

      // System name label
      systemGroup.append('text')
        .attr('class', 'system-name-label data-overlay')
        .attr('x', nodeX + (nodeWidth / 2))
        .attr('y', nodeY + nodeHeight + 2)
        .attr('fill', '#CCCCCC')
        .attr('stroke', '#000000')
        .attr('stroke-width', 1 / zoomLevel)
        .attr('paint-order', 'stroke')
        .attr('font-size', '6px')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'hanging')
        .style('pointer-events', 'none')
        .text(systemNames[systemId] || systemId);

      const addBarHoverEffects = (bar, dataType, value, colorScale) => {
        bar.on('mouseover.data', function (event) {
          event.stopPropagation();
          d3.select(this)
            .attr('opacity', 1)
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 1 / zoomLevel);

          d3.select('body')
            .append('div')
            .attr('class', 'data-overlay-tooltip')
            .style('position', 'absolute')
            .style('left', `${event.pageX + 10}px`)
            .style('top', `${event.pageY - 10}px`)
            .style('background-color', 'rgba(0, 0, 0, 0.8)')
            .style('color', 'white')
            .style('padding', '5px')
            .style('border-radius', '4px')
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .html([
              '<div style="background: rgba(0,0,0,0.9); padding: 8px; border-radius: 4px; border: 1px solid #444">',
              `  <div style="font-weight: bold; color: #f7a600; margin-bottom: 4px">${systemNames[systemId] || systemId}</div>`,
              `  <div>${dataType}: ${value.toFixed(3)}</div>`,
              '  <div style="color: #aaa; font-size: 11px; margin-top: 2px">',
              `    Relative to Max (${maxValues[dataType.toLowerCase()].toFixed(2)}):`,
              `    ${((value / maxValues[dataType.toLowerCase()]) * 100).toFixed(1)}%`,
              '  </div>',
              '</div>'
            ].join(''));
        })
          .on('mouseout.data', function (event) {
            event.stopPropagation();
            d3.select(this)
              .attr('opacity', 0.8)
              .attr('stroke', 'none');
            d3.selectAll('.data-overlay-tooltip').remove();
          });
      };

      addBarHoverEffects(densityBar, 'Density', density, densityColorScale);
      addBarHoverEffects(luminosityBar, 'Luminosity', luminosity, luminosityColorScale);
    });
  }, [mapRef, isOverlayVisible, isLoading, error, meteorDensityData, luminosityData, systemNames, maxValues, combinedShips, combinedFlights, graph, selectedShipId, planetLookups, systemLookups, showShipLabels, getShipLoadInfoById, getLoadColorForRatio, buildLoadSummary, buildShipmentTiles, buildLoadBarDescriptors, formatCapacityValue, partnerFilterActive, partnerFilteredShipments, filterShipmentsByPartner, systemShipmentCounts, isGatewayLayerVisible, gatewayData, systemIdByNaturalId, tripCalculatorOpen, tripRoute, tripStartSystem, tripEndSystem, showGatewayBubbles]);

  useEffect(() => {
    renderOverlay();
  }, [renderOverlay]);

  useEffect(() => {
    if (!mapRef?.current?.svg) return;

    // Capture the current svg reference
    const svg = mapRef.current.svg;

    const handleZoom = () => {
      renderOverlay();
    };

    svg.on('zoom.overlay', handleZoom);

    return () => {
      // Use the captured reference in cleanup
      svg.on('zoom.overlay', null);
    };
  }, [mapRef, renderOverlay]);

  const partnerFilterDisplay = partnerFilter.trim().toUpperCase();

  return (
    <div
      className="ship-filter-control"
      style={{
        position: 'absolute',
        top: '12px',
        right: '16px',
        zIndex: 10,
        background: 'rgba(0, 0, 0, 0.65)',
        color: '#f5f5f5',
        padding: '10px 14px',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        fontSize: '12px',
        lineHeight: 1.4,
        minWidth: '220px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => {
            const newState = !isShipFilterCollapsed;
            setIsShipFilterCollapsed(newState);
            // Close trip calculator when opening ship tracking
            if (!newState) setIsTripCalculatorCollapsed(true);
          }}
        >
          <span style={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '11px' }}>Ship Tracking</span>
          <span style={{ fontSize: '14px', opacity: 0.7 }}>{isShipFilterCollapsed ? '▶' : '▼'}</span>
        </div>

        {!isShipFilterCollapsed && (
          <>
            {!isSilTracking && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 500, opacity: 0.85 }}>
                  Username for Flight tracking {!isAuthenticated && '(requires login)'}
                </span>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={pendingApiUsername}
                    onChange={handleApiUsernameChange}
                    placeholder={isAuthenticated ? "Leave empty to use logged-in user" : "Login required"}
                    disabled={!isAuthenticated}
                    style={{
                      background: isAuthenticated ? '#1f2933' : '#2a2a2a',
                      color: isAuthenticated ? '#f5f5f5' : '#888',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '4px',
                      padding: '5px 6px',
                      fontSize: '12px',
                      outline: 'none',
                      flex: 1,
                      cursor: isAuthenticated ? 'text' : 'not-allowed'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleApplyApiUsername}
                    disabled={!isAuthenticated || !pendingApiUsername.trim() || pendingApiUsername.trim() === apiUsername}
                    style={{
                      background: (!isAuthenticated || !pendingApiUsername.trim() || pendingApiUsername.trim() === apiUsername) ? '#374151' : '#3b82f6',
                      color: '#f5f5f5',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '5px 10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: (!isAuthenticated || !pendingApiUsername.trim() || pendingApiUsername.trim() === apiUsername) ? 'not-allowed' : 'pointer',
                      opacity: (!isAuthenticated || !pendingApiUsername.trim() || pendingApiUsername.trim() === apiUsername) ? 0.5 : 1
                    }}
                  >
                    Apply
                  </button>
                  {(apiUsername || pendingApiUsername) && isAuthenticated && (
                    <button
                      type="button"
                      onClick={() => {
                        setApiUsername('');
                        refetchUserData();
                      }}
                      style={{
                        background: '#dc2626',
                        color: '#f5f5f5',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        opacity: 1,
                        transition: 'background 0.2s ease'
                      }}
                      title="Reset to logged-in username"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </label>
            )}

            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 500, opacity: 0.85 }}>Filter shipments by company code</span>
              <input
                type="text"
                value={partnerFilter}
                onChange={handlePartnerFilterChange}
                placeholder="Company code"
                style={{
                  background: '#1f2933',
                  color: '#f5f5f5',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  padding: '5px 6px',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
              <button
                type="button"
                onClick={handleSilTrackingToggle}
                disabled={silToggleLoading}
                style={{
                  background: isSilTracking ? '#ef4444' : '#3b82f6',
                  color: '#f5f5f5',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 8px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: silToggleLoading ? 'not-allowed' : 'pointer',
                  opacity: silToggleLoading ? 0.65 : 1,
                  transition: 'background 0.2s ease',
                  flex: 1
                }}
              >
                {silToggleLoading
                  ? 'Updating…'
                  : (isSilTracking ? 'Disable SIL Shipment Tracking' : 'Track SIL Shipments')}
              </button>
              <div
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={() => setShowSilTooltip(true)}
                onMouseLeave={() => setShowSilTooltip(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ cursor: 'help', opacity: 0.7 }}
                >
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <text x="8" y="11.5" fontSize="11" fontWeight="bold" textAnchor="middle" fill="currentColor">i</text>
                </svg>
                {showSilTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: '8px',
                    padding: '8px 10px',
                    background: 'rgba(15, 23, 42, 0.95)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    lineHeight: '1.5',
                    color: '#f5f5f5',
                    whiteSpace: 'normal',
                    width: '280px',
                    textAlign: 'left',
                    zIndex: 10000,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                    pointerEvents: 'none'
                  }}>
                    SIL Shipment Tracking mode allows you to track shipments being shipped by OptimizedFunction | SL. You can further filter to look at your shipment by entering your company code above!
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: '8px',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '6px solid rgba(15, 23, 42, 0.95)'
                    }} />
                  </div>
                )}
              </div>
            </div>

            {silToggleError ? (
              <span style={{ fontSize: '10px', color: '#fca5a5' }}>
                {silToggleError}
              </span>
            ) : null}

            {showNoPartnerMatches ? (
              <span style={{ fontSize: '11px', color: '#fca5a5' }}>
                {`No shipments found for company code "${partnerFilterDisplay}"`}
              </span>
            ) : null}

            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '11px', fontWeight: 500, opacity: 0.85 }}>Ship Filter</span>
              <select
                value={selectedShipId}
                onChange={handleShipChange}
                style={{
                  background: '#1f2933',
                  color: '#f5f5f5',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  padding: '4px 6px',
                  fontSize: '12px',
                  outline: 'none'
                }}
              >
                {shipOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={toggleShipLabels}
              style={{
                marginTop: '4px',
                background: labelsEnabled ? '#f7a600' : '#3b82f6',
                color: '#0b0d10',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 8px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
            >
              {labelsEnabled ? 'Hide Ship Labels' : 'Show Ship Labels'}
            </button>

            {!isSilTracking && (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 500, opacity: 0.85 }}>Show Group Ships & Flights</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder={isAuthenticated ? "Enter Group ID" : "Log in to use group functionality"}
                      value={groupId}
                      onChange={e => setGroupId(e.target.value)}
                      disabled={!isAuthenticated}
                      style={{
                        background: isAuthenticated ? '#1f2933' : '#2a2a2a',
                        color: isAuthenticated ? '#f5f5f5' : '#888',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        padding: '5px 6px',
                        fontSize: '12px',
                        outline: 'none',
                        flex: 1,
                        cursor: isAuthenticated ? 'text' : 'not-allowed'
                      }}
                    />
                    <button
                      onClick={handleGroupFetch}
                      disabled={!isAuthenticated || groupLoading || !groupId}
                      style={{
                        background: (!isAuthenticated || groupLoading || !groupId) ? '#6b7280' : '#3b82f6',
                        color: '#f5f5f5',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: (!isAuthenticated || groupLoading || !groupId) ? 'not-allowed' : 'pointer',
                        opacity: (!isAuthenticated || groupLoading || !groupId) ? 0.65 : 1,
                        transition: 'background 0.2s ease'
                      }}
                    >
                      {!isAuthenticated ? 'Login Required' : groupLoading ? 'Loading...' : 'Show Group'}
                    </button>
                    <button
                      onClick={handleGroupClear}
                      disabled={!isAuthenticated}
                      style={{
                        background: !isAuthenticated ? '#6b7280' : '#dc2626',
                        color: '#f5f5f5',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: !isAuthenticated ? 'not-allowed' : 'pointer',
                        opacity: !isAuthenticated ? 0.65 : 1,
                        transition: 'background 0.2s ease'
                      }}
                    >
                      Clear Group
                    </button>
                  </div>
                  {groupError && (
                    <span style={{ fontSize: '10px', color: '#fca5a5', marginTop: '4px' }}>
                      {groupError}
                    </span>
                  )}
                </label>

                {groupUsernames.length > 0 && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        userSelect: 'none',
                        marginBottom: '4px'
                      }}
                      onClick={() => setIsGroupUserFilterCollapsed(!isGroupUserFilterCollapsed)}
                    >
                      <span style={{ fontSize: '11px', fontWeight: 500, opacity: 0.85 }}>Filter Group Users</span>
                      <span style={{ fontSize: '14px', opacity: 0.7 }}>{isGroupUserFilterCollapsed ? '▶' : '▼'}</span>
                    </div>

                    {!isGroupUserFilterCollapsed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                        <span style={{ fontSize: '11px', fontWeight: 500, opacity: 0.85 }}>Select users to display</span>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                          <input
                            type="checkbox"
                            checked={selectedGroupUsers.includes('__all__')}
                            onChange={(e) => handleGroupUserSelectionChange('__all__', e.target.checked)}
                            disabled={!isAuthenticated}
                            style={{ cursor: isAuthenticated ? 'pointer' : 'not-allowed' }}
                          />
                          <span>All Users</span>
                        </label>

                        {groupUsernames.map((username) => (
                          <label key={username} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            <input
                              type="checkbox"
                              checked={selectedGroupUsers.includes(username)}
                              onChange={(e) => handleGroupUserSelectionChange(username, e.target.checked)}
                              disabled={!isAuthenticated || selectedGroupUsers.includes('__all__')}
                              style={{ cursor: (isAuthenticated && !selectedGroupUsers.includes('__all__')) ? 'pointer' : 'not-allowed' }}
                            />
                            <span>{username}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Trip Calculator Section */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px', marginTop: '4px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
            onClick={() => {
              const newState = !isTripCalculatorCollapsed;
              setIsTripCalculatorCollapsed(newState);
              // Sync with context for route visualization
              setTripCalculatorOpen(!newState);
              // Close ship tracking when opening trip calculator
              if (!newState) setIsShipFilterCollapsed(true);
            }}
          >
            <span style={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '11px' }}>
              🚀 Gateway Trip Calculator
            </span>
            <span style={{ fontSize: '14px', opacity: 0.7 }}>{isTripCalculatorCollapsed ? '▶' : '▼'}</span>
          </div>

          {!isTripCalculatorCollapsed && (
            <div style={{ marginTop: '8px' }}>
              <GatewayTripCalculator embedded={true} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(DataPointOverlay);